// Shared answer-matching utilities used by StudentPaperTake (practice-mode verify)
// and StudentPaperReview (post-paper review correctness display).

// A literal &nbsp; entity (from stored HTML answers/questions) is a space, not text — decode it (plus
// the numeric forms) so tokens like "&nbsp;6.531" compare as "6.531".
export const decodeHtmlSpaces = (v) => String(v ?? "").replace(/&nbsp;|&#160;|&#xa0;/gi, " ");

// Some authored answers (e.g. inequality signs "<"/">") were stored as literal HTML entities
// ("&lt;"/"&gt;") rather than the raw character. Decode any remaining entities after tag-stripping
// so they don't show up as literal "&lt;" text in plain-text answer boxes (which, unlike
// HtmlWithLatex, don't run content through the browser's HTML parser).
export const decodeHtmlEntities = (v) => {
  const s = String(v ?? "");
  if (!/&(?:#\d+|#x[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]+);/.test(s)) return s;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = s;
  return textarea.value;
};

// Extracted mark schemes carry a stray space between a minus sign and its digits ("-&nbsp;7°C"), so
// the value reads as +7 and a correct "-7" is scored wrong. Re-glue the sign, but only where a value
// STARTS (string start or after a list separator) — never after a digit, or the expression "5 - 3"
// would turn into "5 -3".
export const glueLeadingSign = (v) =>
  String(v ?? "")
    .replace(/(^|[,;|(]\s*)[-−–—]\s+(?=[.\d])/g, "$1-")
    // ...and the same sign written as a unicode dash glued to its digits ("–8°C"): a minus, not a
    // range separator, so it must compare as one — otherwise "-8°C" fails its own mark scheme.
    // Mirrors FuzzyAnswerMatching::normalizeFuzzyAnswer.
    .replace(/(^|[\s,;|(])[−–—\u2010\u2011\u2012](?=[.\d])/g, "$1-");

// Mark schemes come out of the rich-text editor wrapped in markup ("<p>33%</p>"). Every client tier
// compares the normalised strings, and none of them stripped the tags — so a wrapped scheme matched
// NOTHING, including its own answer typed back verbatim. Only touched when a real tag is present, so
// a bare inequality answer ("<", "a < b") is left alone. Block tags become a space (two paragraphs are
// two values), inline ones vanish (3<sup>2</sup> must not split into "3 2").
const HTML_TAG_RE = /<\/?[a-z][a-z0-9]*(?:\s[^>]*)?>/gi;
const HTML_BLOCK_TAG_RE = /<\/?(?:p|div|br|li|ul|ol|tr|td|th|table|tbody|thead|h[1-6])(?:\s[^>]*)?>/gi;
export const stripAnswerHtmlTags = (v) => {
  const s = String(v ?? "");
  if (!/<\/?[a-z][a-z0-9]*(?:\s[^>]*)?>/i.test(s)) return s;
  return s.replace(HTML_BLOCK_TAG_RE, " ").replace(HTML_TAG_RE, "");
};

// A degree sign the PDF pipeline lifted as a superscript character: "72⁰" (U+2070), "72º" (masculine
// ordinal) and "72ᵒ" are all the printed "72°". Left alone, U+2070 is folded into a zero EXPONENT by
// normalizeNumericPowerExpr, so isNumericPowerExpression claimed the mark scheme was a power expression
// and the student's correct "72°" failed it. A zero exponent is never an 11+ answer; a degree is.
// Mirrors FuzzyAnswerMatching::canonicalizeDegreeGlyphs.
export const canonicalizeDegreeGlyphs = (v) => String(v ?? "").replace(/[⁰ºᵒ]/g, "°");

// Mirrors FuzzyAnswerMatching::canonicalizePiToken.
export const canonicalizePiToken = (v) => String(v ?? "").replace(/\\pi\b/gi, "π").replace(/\bpi\b/gi, "π");

export const normalizeVal = (v) => glueLeadingSign(decodeHtmlSpaces(stripAnswerHtmlTags(canonicalizePiToken(canonicalizeDegreeGlyphs(v)))).replace(/\s+/g, " ").trim()).toLowerCase().replace(/÷/g, '/');

export const normalizeHyphensAndBrackets = (v) =>
  String(v ?? "").replace(/(\w)-(\w)/g, '$1 $2').replace(/[()[\]{}]/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

export const stripAnswerBrackets = (val) => {
  if (!val) return val;
  try {
    const p = JSON.parse(val);
    if (Array.isArray(p)) return p.flat(Infinity).map((v) => String(v ?? '').trim()).filter(Boolean).join(' / ');
  } catch { /* not JSON */ }
  return val;
};

export const stripMarkSchemeOperatorPrefix = (v) =>
  String(v ?? "")
    .trim()
    .replace(/^\(\s*(?:\\[a-z]+|[-+x×*÷/])\s*\)\s*/iu, "")
    .trim();

// Strip trailing prime/apostrophe variants so A′ matches A (coordinate geometry notation).
export const stripPrime = (v) => String(v ?? "").replace(/[′'ʹ']+/g, "").trim();

// am/pm may be written with dots ("a.m", "p.m") — e.g. an authored answer-unit suffix of "a.m" gets
// appended to the typed value as "11.20 a.m". Match both the plain and dotted forms throughout.
const AMPM_RE = "a\\.?\\s*m\\.?|p\\.?\\s*m\\.?";

// "23.50 a.m" names no instant: past noon the hour already carries the half of the day, and the student
// wrote the other half beside it. sameClockInstant reduced the hour mod 12 and read it as 11:50 am.
// A meridiem that AGREES with the 24-hour hour ("19.50 p.m") is merely redundant, not wrong.
// Mirrors ClockTimeAnswerMatcher::periodContradictsHour.
const periodContradictsHour = (hour, period) =>
  period !== null && ((hour >= 13 && period === "am") || (hour === 0 && period === "pm"));

const prepareClockString = (value) => {
  let v = decodeHtmlSpaces(String(value ?? "").replace(/<[^>]*>/g, " "))
    .toLowerCase()
    .trim()
    .replace(/[\u2212]/g, "-")
    .replace(/[\u00a0\u202f\u2009]/g, " ")
    .replace(/[\u200b\ufeff]/g, "")
    .replace(/[\u2024\uff0e\u00b7\u30fb]/g, ".")
    .replace(/\uFF1A/g, ":");
  v = v.replace(
    /\s*((?:a\.?\s*m\.?|p\.?\s*m\.?))(?:\s*(?:a\.?\s*m\.?|p\.?\s*m\.?))+$/i,
    " $1"
  );
  return v.trim();
};

export const parseClockTime = (value, allowBareDigits = false) => {
  let v = prepareClockString(value);
  if (!v) return null;

  // Students space out the separator ("4 :08", "12. 55 pm") — allow a single space either side of
  // it, but only one: a wider gap ("12.  55") is not a time a student meant to write. Mirrors
  // ClockTimeAnswerMatcher, so the submission is never collapsed before this test.
  const colon = v.match(/^(\d{1,2}) ?[:.] ?(\d{2})\s*(a\.?\s*m\.?|p\.?\s*m\.?)?$/i);
  if (colon) {
    const hour = parseInt(colon[1], 10);
    const minute = parseInt(colon[2], 10);
    if (minute >= 60 || hour > 23) return null;
    const periodRaw = colon[3];
    let period = null;
    if (periodRaw) {
      period = periodRaw.replace(/\s/g, "").startsWith("a") ? "am" : "pm";
    }
    if (periodContradictsHour(hour, period)) return null;
    return { hour, minute, period };
  }

  // "3.0 p.m" with a meridiem is o'clock, not a one-digit minute. Bare "3.0" stays a decimal.
  const oClockDotZero = v.match(/^(\d{1,2})\.0\s*(a\.?\s*m\.?|p\.?\s*m\.?)$/i);
  if (oClockDotZero) {
    const hour = parseInt(oClockDotZero[1], 10);
    const period = oClockDotZero[2].replace(/\s/g, "").startsWith("a") ? "am" : "pm";
    if (hour <= 23 && !periodContradictsHour(hour, period)) {
      return { hour, minute: 0, period };
    }
  }

  // An o'clock time written without the minutes ("7 pm"). The meridiem is what makes it a time —
  // a bare "7" stays a number, so it never answers a "7:00 pm" mark scheme.
  const oClock = v.match(/^(\d{1,2})\s*(a\.?\s*m\.?|p\.?\s*m\.?)$/i);
  if (oClock) {
    const hour = parseInt(oClock[1], 10);
    const period = oClock[2].replace(/\s/g, "").startsWith("a") ? "am" : "pm";
    if (hour <= 23 && !periodContradictsHour(hour, period)) {
      return { hour, minute: 0, period };
    }
  }

  // A bare space is NOT a separator: "3 24" is neither 3:24 nor 3.24. Mirrors ClockTimeAnswerMatcher.

  const dotted = v.match(/^(\d{1,2})\.(\d{2})$/);
  if (dotted) {
    const hour = parseInt(dotted[1], 10);
    const minute = parseInt(dotted[2], 10);
    if (minute < 60 && hour <= 23) {
      return { hour, minute, period: null };
    }
  }

  // "0802" — the separator-less four-digit form the UK 24-hour clock is normally written in. Only read
  // when the stem has already asked for a time; four bare digits are otherwise just a number.
  const bare = allowBareDigits ? v.match(/^([01]\d|2[0-3])([0-5]\d)$/) : null;
  if (bare) return { hour: parseInt(bare[1], 10), minute: parseInt(bare[2], 10), period: null };

  return null;
};

// A railway-timetable mark scheme is four digits with no separator — "0800", "0728". The timetable the
// question is read from prints every time that way, so the leading zero is part of how the answer is
// written: "800" is not an answer off that timetable. A submission carrying the same four digits in
// order ("08:00", "0800 hours") is left to the tiers that decide separator style.
// Mirrors ClockTimeAnswerMatcher::timetableDigitsMissing.
export const timetableDigitsMissing = (submitted, correct, stemAsksForTime = false) => {
  const plain = (v) => decodeHtmlSpaces(String(v ?? "").replace(/<[^>]*>/g, " ")).trim();
  const c = plain(correct).replace(/\s+/g, "");
  // The leading zero is what identifies the scheme as a time on sight. Without it "1200" is just as
  // likely a count, so an afternoon time only qualifies when the stem asked for a time.
  const isTimetable = /^0\d[0-5]\d$/.test(c) || (stemAsksForTime && /^(?:1\d|2[0-3])[0-5]\d$/.test(c));
  if (!isTimetable) return false;
  const s = plain(submitted);
  if (!s) return false;
  return !s.replace(/\D+/g, "").includes(c);
};

const extractWrittenAmPm = (value) => {
  const v = String(value ?? "").toLowerCase();
  if (/(?:^|(?<=\d))\s*a\.?\s*m\.?(?:\b|$)/i.test(v)) return "am";
  if (/(?:^|(?<=\d))\s*p\.?\s*m\.?(?:\b|$)/i.test(v)) return "pm";
  return null;
};

// The half-day a 24-hour mark scheme names on its own, or null when it genuinely does not say.
// Only unambiguously 24-hour forms qualify: an afternoon hour (13-23), midnight hour 00, and a
// zero-padded morning hour ("02:00", "09.15") — the leading zero is what marks the value as 24-hour
// notation rather than a 12-hour reading. A bare "3.00" stays ambiguous, so "3 pm" goes on answering
// it; 12 stays ambiguous too (noon and midnight are both written that way).
// Mirrors ClockTimeAnswerMatcher::implicitPeriodOfScheme.
const implicitPeriodOfScheme = (correctRaw, correctPeriod) => {
  if (correctPeriod != null) return correctPeriod;
  const c = parseClockTime(correctRaw, true);
  if (!c || c.period != null) return c ? c.period : null;
  if (c.hour >= 13) return "pm";
  if (c.hour === 0) return "am";
  const plain = decodeHtmlSpaces(String(correctRaw ?? "").replace(/<[^>]*>/g, " ")).toLowerCase().trim();
  if (c.hour >= 1 && c.hour <= 9 && /^0\d/.test(plain)) return "am";
  return null;
};

const clockTimePeriodsCompatible = (studentRaw, correctRaw, studentPeriod, correctPeriod) => {
  const studentWritten = extractWrittenAmPm(studentRaw);
  const correctWritten = extractWrittenAmPm(correctRaw);
  // A 24-hour mark scheme states the half of the day in the hour itself, so it constrains a written
  // meridiem just as firmly as an "am"/"pm" on the scheme would: "02:00" is two in the MORNING, and
  // "2 pm" is a different instant. Without this the hour comparison saw 2 === 2, found no meridiem to
  // disagree with, and marked the afternoon correct.
  const expected = correctWritten ?? implicitPeriodOfScheme(correctRaw, correctPeriod);
  if (studentWritten === null) return true;
  if (expected === null) return true;
  return studentWritten === expected;
};

// A 12-hour mark scheme writes am/pm because the hour alone does not name the time — "5:28" is as much
// morning as evening, so it has not answered "5:28 pm". A 24-hour submission carries the half of the
// day in the hour itself ("17:28") and still stands without a meridiem.
// Mirrors ClockTimeAnswerMatcher::submissionOmitsRequiredPeriod.
const clockSubmissionOmitsRequiredPeriod = (student, correct, s) => {
  if (extractWrittenAmPm(correct) === null) return false;
  if (extractWrittenAmPm(student) !== null) return false;
  return s.hour >= 1 && s.hour <= 12;
};

// Settings → Student → time answers: "both" accepts 5:52 or 5.52 for a "5:52" scheme, "colon" marks
// the dot wrong. Set once per page from student/parent settings (see hooks/usePracticeModeVisible);
// mirrors NotificationSetting::clockTimeSeparator in ClockTimeAnswerMatcher::equivalent.
let clockSeparatorMode = "both";
export const setClockTimeSeparatorMode = (mode) => {
  clockSeparatorMode = mode === "colon" ? "colon" : "both";
};
const clockSeparatorUsed = (value) => {
  const v = String(value ?? "").replace(/<[^>]*>/g, "");
  if (/\d\s*:\s*\d/.test(v)) return ":";
  if (/\d\s*\.\s*\d/.test(v)) return ".";
  return null;
};

export const clockTimeAnswersEquivalent = (student, correct, stemAsksForTime = false) => {
  // Only where the scheme itself writes ":" — a dotted scheme ("12.15") may be a decimal.
  if (clockSeparatorMode === "colon" && clockSeparatorUsed(correct) === ":" && clockSeparatorUsed(student) === ".") return false;
  const v = prepareClockString(student).replace(/\s+/g, " ");
  // Two-digit-minute scheme vs a single digit after the separator ("1.0 am" for "1.00 am").
  if (/^\d{1,2}\s*[.:]\s*\d(?!\d)\s*(a\.?\s*m\.?|p\.?\s*m\.?)?$/i.test(v)) return false;
  const s = parseClockTime(student, stemAsksForTime);
  const c = parseClockTime(correct, stemAsksForTime);
  if (!s || !c) return false;
  if (s.minute !== c.minute) return false;
  const sPeriod = extractWrittenAmPm(student);
  const cPeriod = extractWrittenAmPm(correct);
  if (sPeriod && cPeriod && s.hour >= 13 && c.hour >= 1 && c.hour <= 12) return false;
  if (!sameClockInstant(s, c) && s.hour !== c.hour) return false;
  if (clockSubmissionOmitsRequiredPeriod(student, correct, s)) return false;
  return clockTimePeriodsCompatible(student, correct, s.period, c.period);
};

export const clockTimeInDisplayedRange = (submitted, correctDisplay, stemAsksForTime = false) => {
  const m = String(correctDisplay || "").match(/from\s+(\d{1,2}[:.]\d{2})\s+to\s+(\d{1,2}[:.]\d{2})/i);
  if (!m) return false;
  const s = parseClockTime(submitted, stemAsksForTime);
  const lo = parseClockTime(m[1], stemAsksForTime);
  const hi = parseClockTime(m[2], stemAsksForTime);
  if (!s || !lo || !hi) return false;
  const toMin = (t) => t.hour * 60 + t.minute;
  const n = toMin(s);
  const a = toMin(lo);
  const b = toMin(hi);
  return n >= Math.min(a, b) && n <= Math.max(a, b);
};

const sameClockInstant = (a, b) => {
  const toMinutes = (t) => {
    const h = t.hour;
    const period = t.period;
    if (h > 23 || t.minute >= 60) return null;
    if (period) {
      let h24;
      if (h === 12) h24 = period === "am" ? 0 : 12;
      else h24 = period === "pm" ? (h % 12) + 12 : (h % 12);
      return h24 * 60 + t.minute;
    }
    return h * 60 + t.minute;
  };
  const ma = toMinutes(a);
  const mb = toMinutes(b);
  return ma !== null && mb !== null && ma === mb;
};

export const normalizeTimeForMatch = (v) => {
  const parsed = parseClockTime(v);
  if (parsed) {
    return `${parsed.hour}:${String(parsed.minute).padStart(2, "0")}`;
  }
  let s = String(v ?? "").replace(/^(\d{1,2})\.(\d{2})\s*(am|pm)?$/i, (_, h, m) => `${h}:${m}`);
  s = s.replace(/\s*(am|pm)$/i, "").trim();
  return s;
};

// A clock-time mark scheme is decided solely by timesMatch (mirrors the backend's
// correctAnswerIsClockTime guard): the fuzzy tiers below collapse whitespace and split "12:55" into a
// {12,55} token set, which would re-accept the wider gaps the one-space limit above rejects. The mark
// scheme is authored HTML so its own whitespace is noise — only the submission is held to one space.
// Require a successful parse: "0.75" / "1.50" match d.dd shape but are decimals / invalid minutes —
// without this, fraction↔decimal (3/4 for 0.75) never runs.
export const correctIsClockTime = (v) => {
  const plain = decodeHtmlSpaces(v).replace(/\s+/g, " ").trim();
  // The minutes are optional when a meridiem is present ("3 pm") — the meridiem alone makes the field a
  // clock time. Without this the tiers below read "3.00" as the number 3 and matched it.
  if (!new RegExp(`^\\d{1,2}\\s*(?:[.:]\\s*\\d{2}\\s*(?:${AMPM_RE})?|\\s*(?:${AMPM_RE}))$`, "i").test(plain)) return false;
  return parseClockTime(plain) !== null;
};

// UK time notation uses a dot ("17.50") as well as a colon ("17:50"), so a dot-separated submission
// with a two-digit minute part is the same time as the colon mark scheme — accept it. A decimal like
// "17.5" has only one fractional digit, so parseClockTime (which requires \d{2} minutes) never
// converts it to a time and it stays incorrect.
// Asymmetric: a bare dotted mark scheme ("12.15" scale reading) must NOT accept a colon ("12:15").
// With am/pm — or a stem that has already asked for a clock time ("At what time does it end?") —
// the field is unambiguously a time, so either separator is fine.
export const timesMatch = (submitted, correct, stemAsksForTime = false) => {
  const sRaw = String(submitted);
  const cRaw = String(correct).replace(/\s+/g, " ");
  // Mirrors ClockTimeAnswerMatcher::equivalent: ".0 am" parses as o'clock, so incomplete minutes
  // must be refused before normalizeTimeForMatch collapses "1.0 a.m." and "1.00 am" to "1:00".
  const preparedStudent = prepareClockString(sRaw).replace(/\s+/g, " ");
  if (/^\d{1,2}\s*[.:]\s*\d(?!\d)\s*(a\.?\s*m\.?|p\.?\s*m\.?)?$/i.test(preparedStudent)) return false;
  const hasAmPm = (v) => new RegExp(`(?:${AMPM_RE})`, "i").test(v);
  // Both sides name a half of the day and they disagree — 7 a.m is not 7:00 pm. Checked before
  // normalizeTimeForMatch, which drops the meridiem and would call them the same time.
  const sPeriod = extractWrittenAmPm(sRaw);
  const cPeriod = extractWrittenAmPm(cRaw);
  if (sPeriod && cPeriod && sPeriod !== cPeriod) return false;
  // A 12-hour mark scheme states am/pm because the hour alone does not name the time. Checked here as
  // well as inside clockTimeAnswersEquivalent because the normalizeTimeForMatch fallback below drops
  // the meridiem from both sides and would call "5.28" the same time as "5:28 pm".
  if (cPeriod && !sPeriod) {
    const s = parseClockTime(sRaw, stemAsksForTime);
    if (s && s.hour >= 1 && s.hour <= 12) return false;
  }
  // A 24-hour scheme names its half of the day in the hour itself, so a contradicting written
  // meridiem is wrong even though the hour digits agree: "2 pm" does not answer "02:00". Checked here
  // too because the normalizeTimeForMatch fallback at the bottom strips the meridiem off both sides
  // and would call them the same time.
  if (sPeriod && !cPeriod && implicitPeriodOfScheme(cRaw, null) === (sPeriod === "am" ? "pm" : "am")) {
    return false;
  }
  if (!stemAsksForTime && !hasAmPm(sRaw) && !hasAmPm(cRaw)) {
    const sep = (v) => (/\d\s*:\s*\d/.test(v) ? ":" : /\d\s*\.\s*\d/.test(v) ? "." : null);
    if (sep(cRaw) === "." && sep(sRaw) === ":") return false;
  }
  if (clockTimeAnswersEquivalent(submitted, correct, stemAsksForTime)) return true;
  const sClock = parseClockTime(sRaw, stemAsksForTime);
  const cClock = parseClockTime(cRaw, stemAsksForTime);
  if (sClock && cClock && sPeriod && cPeriod
      && sClock.hour >= 13 && cClock.hour >= 1 && cClock.hour <= 12) {
    return false;
  }
  return normalizeTimeForMatch(sRaw) === normalizeTimeForMatch(cRaw);
};

// A stem that asks the student to read or state a CLOCK TIME — frees "11:05" to match a dotted
// "11.05" mark scheme. "How long / how much time" asks for a duration and is excluded.
// Mirrors FuzzyAnswerMatching::questionAsksForClockTime.
export const questionAsksForClockTime = (questionText) => {
  const text = plainAnswerText(questionText).toLowerCase();
  if (!text) return false;
  if (/\bhow\s+(?:long|much\s+time)\b/.test(text)) return false;
  return /(?:at|by)?\s*what\s+time\b/.test(text)
    // "What is the LATEST time she needs to leave?" — the qualifier splits "what" from "time".
    || /\b(?:latest|earliest|last|first|next|correct|actual)\s+time\b/.test(text)
    || /\btime\s+(?:does|do|did|will|would|is|are|was|were|should)\b/.test(text)
    // "What IS THE time and date in New York?" — a qualifier or a linking verb between "what" and
    // "time" is still a request for a clock time, and without this the 24-hour form the question
    // itself used ("2200 hours") was refused against a "17:00" mark scheme.
    || /\bwhat\s+(?:is|was|are|were)\s+the\s+(?:\w+\s+){0,2}time\b/.test(text)
    || /\b(?:12|24)[\s-]*hour\s+clock\b/.test(text)
    || /\bwrite\s+(?:down\s+)?the\s+time\b/.test(text)
    || /\b(?:start|starting|finish|finishing|end|ending|arrival|departure)\s+time\b/.test(text)
    || /\bo'?\s?clock\b/.test(text);
};

// "Write your answer using the 24-hour clock" — the notation IS half the task, so the 12-hour form is
// the working, not the answer. Mirrors FuzzyAnswerMatching::questionRequires24HourAnswer.
export const questionRequires24HourAnswer = (questionText) => {
  const text = plainAnswerText(questionText).toLowerCase();
  if (!text) return false;
  return /\b24[\s-]*hour\s+(?:clock|time|notation|format)\b/.test(text);
};

// "08:02" / "08.02" / "0802" state the hour in two digits; a bare "8.02" does not, whatever the
// same value's parsed hour says. Mirrors ClockTimeAnswerMatcher::hasZeroPaddedHour.
const hasZeroPaddedHour = (value) => /(?<!\d)0\d\s*[:. ]?\s*\d{2}(?!\d)/.test(plainAnswerText(value));

// "5.13 p.m" against a "17:13" scheme: the same instant, written in the form the stem ruled out.
// Mirrors ClockTimeAnswerMatcher::submissionBreaks24HourForm.
export const submissionBreaks24HourForm = (submitted, correct) => {
  const cRaw = plainAnswerText(correct);
  const sRaw = plainAnswerText(submitted);
  if (extractWrittenAmPm(cRaw)) return false;
  const c = parseClockTime(cRaw, true);
  const s = parseClockTime(sRaw, true);
  if (!c || !s) return false;
  if (extractWrittenAmPm(sRaw)) return true;
  // The 24-hour clock is always four digits: "08:02", never "8:02". A morning scheme that carries
  // the leading zero is stating exactly that, so dropping it answers in the form the stem ruled out.
  if (c.hour < 10 && hasZeroPaddedHour(cRaw) && !hasZeroPaddedHour(sRaw)) return true;
  return c.hour >= 13 && s.hour >= 1 && s.hour <= 12;
};

export const normalizeVulgarFractions = (v) => {
  const map = {
    '½': ' 1/2', '⅓': ' 1/3', '⅔': ' 2/3',
    '¼': ' 1/4', '¾': ' 3/4',
    '⅕': ' 1/5', '⅖': ' 2/5', '⅗': ' 3/5', '⅘': ' 4/5',
    '⅙': ' 1/6', '⅚': ' 5/6',
    '⅐': ' 1/7', '⅛': ' 1/8', '⅜': ' 3/8', '⅝': ' 5/8', '⅞': ' 7/8',
    '⅑': ' 1/9', '⅒': ' 1/10',
  };
  return String(v ?? '').replace(/\u2044/g, '/').replace(/[½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅐⅛⅜⅝⅞⅑⅒]/g, (ch) => map[ch] ?? ch).trim();
};

export const normalizeLatexFraction = (v) => {
  let s = normalizeVulgarFractions(v);
  s = s.replace(/^\$+|\$+$/g, "").trim();
  // Admins author maths as \(\frac{21}{4}\); drop the delimiters so a LaTeX-formatted mark
  // scheme still compares against a student's plain "21/4".
  s = s.replace(/^\\[([]\s*|\s*\\[)\]]$/g, "").trim();
  s = s.replace(/(\d)\\frac\{([^}]+)\}\{([^}]+)\}/g, "$1 $2/$3");
  s = s.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, "$1/$2");
  return s;
};

// The degree branch has no \b (° is not a word character), so "-7°C" / "-7 °c" / "45°" strip to the
// bare number — the backend's normalizeFuzzyAnswer already did this, and the drift made the client
// override a correct server verdict on temperature questions.
// Admin-added units (AnswerUnitVocabulary) arrive at runtime from the settings fetch. A unit that
// earns a chip but is unknown here makes the client override disagree with the server verdict.
let customAnswerUnits = [];
const escapeUnit = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const setCustomAnswerUnits = (units) => {
  customAnswerUnits = Array.isArray(units)
    ? [...units].filter((u) => u && String(u).trim()).sort((a, b) => String(b).length - String(a).length)
    : [];
};

export const stripCommonUnit = (v) => {
  const base = "kg|g|mg|cm|mm|km\\/h|m\\/s|kmph|kph|mph|mps|km|m|ml|l|litres?|liters?|metres?|meters?"
    + "|feet|foot|ft|inch(?:es)?|yards?|yds?|miles?|pints?|gallons?|stones?|tonnes?|ounces?|oz|pounds?|lbs?"
    + "|hours?|hrs?|minutes?|mins?|seconds?|secs?"
    + "|degrees?\\s*(?:celsius|fahrenheit)?|celsius|fahrenheit|kilowatts?|watts?|kW|W|RF|calories?|cals?|cal";
  const extra = customAnswerUnits.length ? "|" + customAnswerUnits.map(escapeUnit).join("|") : "";
  const re = new RegExp(`\\s*(?:°\\s*[cf]?|\\b(?:${base}${extra})\\b)\\s*(?:\\^?\\s*[²³23])?\\s*$`, "i");
  return v.replace(re, "").trim();
};

// Mirrors FuzzyAnswerMatching::valueHasMeasurementUnit — glued "5mm" has no \\b before the unit.
const valueHasMeasurementUnit = (value) => {
  const v = plainAnswerText(value).toLowerCase().trim();
  if (!v) return false;
  if (/[£$€¥%°]/.test(v)) return true;
  return /\d\s*(?:mm|cm|km|kg|mg|ml|cl|m|g|l|p|met(?:re|er)s?|kilomet(?:re|er)s?|millimet(?:re|er)s?|centimet(?:re|er)s?|gram(?:me)?s?|kilogram(?:me)?s?|milligram(?:me)?s?|lit(?:re|er)s?|millilit(?:re|er)s?|miles?|minutes?|mins?|seconds?|secs?|hours?|hrs?|hr|pence|percent|degrees?|°c|°f|mph|km\/h|m\/s)\s*(?:\^?\s*[²³23])?\s*$/i.test(v);
};

// A fill-blank whose whole answer IS a unit noun ("Andy is 183 ___ tall" -> "centimetres"). The
// unit-stripping tiers only fire when a NUMBER is attached, so a bare "cm" against a mark scheme
// of "centimetres" reduced both sides to "" and never matched. Mirrors
// backend App\Support\AnswerUnitVocabulary::UNIT_NOUN_SYNONYMS — keep both in sync.
const UNIT_NOUN_SYNONYMS = [
  ["cm", "centimetre", "centimetres", "centimeter", "centimeters"],
  ["mm", "millimetre", "millimetres", "millimeter", "millimeters"],
  ["km", "kilometre", "kilometres", "kilometer", "kilometers"],
  ["m", "metre", "metres", "meter", "meters"],
  ["kg", "kilogram", "kilograms"],
  ["g", "gram", "grams", "gramme", "grammes"],
  ["mg", "milligram", "milligrams"],
  ["l", "litre", "litres", "liter", "liters"],
  ["ml", "millilitre", "millilitres", "milliliter", "milliliters"],
  ["cl", "centilitre", "centilitres"],
  ["ft", "foot", "feet"],
  ["in", "inch", "inches"],
  ["yd", "yard", "yards"],
  ["mi", "mile", "miles"],
  ["oz", "ounce", "ounces"],
  ["lb", "pound", "pounds"],
  ["t", "tonne", "tonnes", "ton", "tons"],
  ["hr", "hrs", "hour", "hours"],
  ["min", "mins", "minute", "minutes"],
  ["sec", "secs", "second", "seconds"],
  ["°c", "degree celsius", "degrees celsius", "celsius"],
  ["°f", "degree fahrenheit", "degrees fahrenheit", "fahrenheit"],
];

const unitNounEquivalent = (a, b) => {
  const norm = (v) => String(v ?? "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim().replace(/\.+$/, "").toLowerCase();
  const na = norm(a);
  const nb = norm(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  return UNIT_NOUN_SYNONYMS.some((group) => group.includes(na) && group.includes(nb));
};

// When the UI already shows an answer_unit chip (RF, W, cals, £…), the student must type only the
// numeric/maths value. These aliases catch "70RF", "70 RF", "900 watts" against a "W" chip, etc.
const UNIT_CHIP_ALIASES = {
  rf: ["rf"],
  w: ["w", "watt", "watts"],
  watt: ["w", "watt", "watts"],
  watts: ["w", "watt", "watts"],
  kw: ["kw", "kilowatt", "kilowatts"],
  kilowatt: ["kw", "kilowatt", "kilowatts"],
  kilowatts: ["kw", "kilowatt", "kilowatts"],
  cal: ["cal", "cals", "calorie", "calories"],
  cals: ["cal", "cals", "calorie", "calories"],
  calorie: ["cal", "cals", "calorie", "calories"],
  calories: ["cal", "cals", "calorie", "calories"],
  "£": ["£"],
  $: ["$"],
  "€": ["€"],
  "¥": ["¥"],
  "%": ["%", "percent", "percentage"],
  p: ["p", "pence"],
  "a.m": ["a.m", "a.m.", "am"],
  "p.m": ["p.m", "p.m.", "pm"],
};

const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const unitAliasesForChip = (unit) => {
  const raw = String(unit ?? "").trim();
  if (!raw) return [];
  const key = raw.toLowerCase().replace(/\s+/g, "");
  const listed = UNIT_CHIP_ALIASES[key];
  if (listed) return listed;
  return [raw];
};

/**
 * Strip a given answer-unit chip from student typing ("70RF" / "70 RF" → "70" when unit is RF).
 * When stripLetters is true (numeric-only mark schemes), also drops leftover letters so the unit
 * cannot be re-typed beside the chip. Algebraic answers ("3x/5 days") pass stripLetters:false so
 * variables survive next to a unit chip.
 */
export const sanitizeTypedAnswerWithGivenUnit = (raw, unit, { isPrefix = false, stripLetters = true } = {}) => {
  if (!unit) return String(raw ?? "");
  let v = String(raw ?? "");
  const aliases = [...unitAliasesForChip(unit)].sort((a, b) => b.length - a.length);
  for (let pass = 0; pass < 4; pass += 1) {
    let changed = false;
    for (const alias of aliases) {
      const esc = escapeRegExp(alias);
      const re = isPrefix
        ? new RegExp(`^\\s*${esc}\\s*`, "i")
        : new RegExp(`\\s*${esc}\\s*$`, "i");
      const next = v.replace(re, "");
      if (next !== v) {
        v = next;
        changed = true;
        break;
      }
    }
    if (!changed) break;
  }
  // Unit chip already supplies the unit — keep numeric / maths tokens only (fractions, decimals, °)
  // when the mark scheme itself is numeric. The Math Tools minus button inserts U+2212, so it must
  // survive here — dropping it silently deleted the sign from a negative answer ("−8" in a °C field).
  if (stripLetters) {
    v = v.replace(/[^0-9\s.,/:\-−–—+()×÷°²³⁴½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞]/g, "");
  }
  return v;
};

// When answer_data.answer_input_mode is "numeric" the mark scheme cannot be answered with words
// (QuestionBankAnswer::markSchemeIsNumericOnly), so the typed box drops letters as they are typed —
// "25/12a" can never reach grading. Only Latin letters go: π, √, °, ², ½ and the rest of the Math
// Tools palette are maths notation, not words.
export const isNumericOnlyAnswerMode = (answerData) =>
  String(answerData?.answer_input_mode ?? "") === "numeric";

export const stripLettersFromNumericAnswer = (raw) =>
  String(raw ?? "").replace(/[A-Za-z]/g, "");

// A single number written with a space where its decimal point belongs — "5 04" for "£5.04", "3 23"
// for "3.24". Invalid notation, not a typo to repair: reading the space as the point turned a wrong
// value into a right one. Only 1–2 digits after the space (three is a thousands group, "9 625" =
// 9625) and only against a single-value numeric mark scheme — against a comma-joined list "12 8"
// legitimately answers "12, 8". Mirrors FuzzyAnswerMatching::submittedSpacesDecimalPoint.
export const submittedSpacesDecimalPoint = (submitted, correct) => {
  const plain = (v) =>
    stripCommonUnit(
      decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim().replace(/^[£$€¥]\s*/, "")
    )
      .replace(/\s*%$/, "")
      .trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(plain(correct))) return false;
  return /^-?\d+ \d{1,2}$/.test(plain(submitted));
};

// A plain number with no unit — "18", "18.5", "-3", "3/4", "1 1/2", "1,200", "1 000". Used to enforce:
// when the mark scheme is JUST a number, the student answer must be too (no appended unit/word). The
// value carries no "cm"/"m"/"kg" etc. — anything with a trailing unit is NOT a plain number.
export const isPlainNumberVal = (v) => {
  // A LaTeX-authored mark scheme ("5 \frac{23}{24}") must be recognised as a plain mixed number
  // too, or this check silently no-ops for it and the "submission must also be a plain number"
  // guard elsewhere never fires.
  let s = normalizeLatexFraction(String(v ?? "")).trim().toLowerCase();
  if (s === "") return false;
  return /^[-+]?\d{1,3}(?:[,\s]\d{3})+(?:\.\d+)?$/.test(s)   // grouped thousands "1,200" / "1 000"
    || /^[-+]?\d+(?:\.\d+)?$/.test(s)                         // integer / decimal
    || /^[-+]?\d+\s*\/\s*\d+$/.test(s)                        // fraction "3/4"
    || /^[-+]?\d+\s+\d+\s*\/\s*\d+$/.test(s);                 // mixed number "1 1/2"
};

const answerTokensForMatch = (v) =>
  normalizeVal(stripMarkSchemeOperatorPrefix(v)).split(" ").filter(Boolean);

export const questionAsksForCount = (questionText) => {
  const q = decodeHtmlSpaces(String(questionText ?? ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return /\b(?:how many|number of)\b/.test(q);
};

const countSuffixAppearsInQuestion = (suffix, questionText) => {
  const stem = String(suffix ?? "").toLowerCase().replace(/[^a-z]/g, "");
  if (stem.length < 2) return false;
  const q = decodeHtmlSpaces(String(questionText ?? ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  const base = stem.replace(/s$/, "");
  // Mark-scheme abbreviations ("cals", "W") must still match the stem wording ("calories", "watts").
  const aliases = {
    cal: ["cal", "cals", "calorie", "calories"],
    watt: ["watt", "watts", "w"],
    w: ["watt", "watts", "w"],
    kw: ["kw", "kilowatt", "kilowatts"],
    rf: ["rf"],
  };
  const forms = aliases[base] || [base, `${base}s`];
  return forms.some((form) => new RegExp(`\\b${form.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(q));
};

export const isOrderedTokenPrefix = (submitted, correct) => {
  const short = answerTokensForMatch(submitted);
  const long = answerTokensForMatch(correct);
  if (short.length === 0 || long.length === 0 || short.length >= long.length) return false;
  return short.every((token, index) => token === long[index]);
};

/** "How many edges?" — bare "12" is fully correct vs mark scheme "12 edges". */
export const countQuestionNumericAnswerEquivalent = (submitted, correct, questionText) => {
  if (!questionAsksForCount(questionText) || !isOrderedTokenPrefix(submitted, correct)) return false;
  const short = answerTokensForMatch(submitted);
  const long = answerTokensForMatch(correct);
  if (long.length !== short.length + 1) return false;
  const suffix = long[long.length - 1];
  if (!countSuffixAppearsInQuestion(suffix, questionText)) return false;
  if (!short.every((token) => isPlainNumberVal(token))) return false;
  return short.every((token, index) => token === long[index]);
};

export const parseValueWithUnit = (v) => {
  let s = String(v ?? "").replace(/\u00a0/g, " ").trim().toLowerCase();
  s = s.replace(/\s*\^\s*2\b/g, "²").replace(/\s*\^\s*3\b/g, "³");
  s = s.replace(/\bcm2\b/g, "cm²").replace(/\bm2\b/g, "m²").replace(/\bkm2\b/g, "km²").replace(/\bmm2\b/g, "mm²");
  const poundMatch = s.match(/^£\s*(\d+(?:\.\d+)?)\s*$/);
  if (poundMatch) return [parseFloat(poundMatch[1]) * 100, "p", true];
  // "124 m2" / "124 square metres" are the spellings a student without a superscript key can type —
  // they name the same unit as the mark scheme's "124 m²" and must parse to it.
  const squared = s.match(/^(-?\d+(?:\.\d+)?)\s*(?:(mm|cm|m|km)\s*[²³23]|square\s+(millimetres?|millimeters?|centimetres?|centimeters?|metres?|meters?|kilometres?|kilometers?))\s*$/i);
  if (squared) {
    const word = { millimetre: "mm", millimeter: "mm", centimetre: "cm", centimeter: "cm", metre: "m", meter: "m", kilometre: "km", kilometer: "km" };
    const base = squared[2] ?? word[String(squared[3] ?? "").replace(/s$/, "")];
    const cubic = /[³3]\s*$/.test(s);
    if (base) return [parseFloat(squared[1]), `${base}${cubic ? "³" : "²"}`];
  }
  const unitMatch = s.match(/^(-?\d+(?:\.\d+)?)\s*(km²|cm²|m²|km\/h|m\/s|seconds?|minutes?|hours?|days?|secs?|mins?|hrs?|kilowatts?|watts?|calories?|cals?|cal|kW|mm|cm|km|mg|kg|ml|RF|m|g|l|p|W)\s*$/i);
  if (unitMatch) return [parseFloat(unitMatch[1]), unitMatch[2]];
  if (/^-?\d+(?:\.\d+)?\s*$/.test(s)) return [parseFloat(s), ""];
  // Stray trailing "o"/"O" typo on a number whose unit is supplied by the field's unit
  // suffix (e.g. "1o" typed in a metres field) — read it as the leading number.
  const strayO = s.match(/^(-?\d+(?:\.\d+)?)o+$/);
  if (strayO) return [parseFloat(strayO[1]), ""];
  return null;
};

export const UNIT_CONVERSIONS = {
  mm: [1, "mm", "length"], cm: [10, "mm", "length"], m: [1000, "mm", "length"], km: [1000000, "mm", "length"],
  "mm²": [1, "mm²", "area"], "cm²": [100, "mm²", "area"], "m²": [1000000, "mm²", "area"], "km²": [1e12, "mm²", "area"],
  "mm³": [1, "mm³", "volume_solid"], "cm³": [1000, "mm³", "volume_solid"], "m³": [1e9, "mm³", "volume_solid"], "km³": [1e18, "mm³", "volume_solid"],
  mg: [1, "mg", "weight"], g: [1000, "mg", "weight"], kg: [1000000, "mg", "weight"],
  ml: [1, "ml", "volume"], l: [1000, "ml", "volume"],
  p: [1, "p", "currency"],
  s: [1, "s", "time"], sec: [1, "s", "time"], secs: [1, "s", "time"],
  second: [1, "s", "time"], seconds: [1, "s", "time"],
  min: [60, "s", "time"], mins: [60, "s", "time"], minute: [60, "s", "time"], minutes: [60, "s", "time"],
  hr: [3600, "s", "time"], hrs: [3600, "s", "time"], hour: [3600, "s", "time"], hours: [3600, "s", "time"],
  day: [86400, "s", "time"], days: [86400, "s", "time"],
};

/**
 * Parse compound values like "1 hour 30 min", "1 m 50 cm", "2 kg 300 g".
 * Returns [canonicalValue, canonUnit, dimension] or null.
 */
export const parseCompoundValue = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return null;
  const pattern = /(-?\d+(?:\.\d+)?)\s*(km²|cm²|m²|seconds?|minutes?|hours?|days?|secs?|mins?|hrs?|mm|cm|km|mg|kg|ml|m|g|l|p)/g;
  const matches = [...s.matchAll(pattern)];
  if (matches.length < 2) return null;
  let total = 0;
  let canonUnit = null;
  let canonDim = null;
  for (const m of matches) {
    const num = parseFloat(m[1]);
    const unit = m[2];
    const conv = UNIT_CONVERSIONS[unit];
    if (!conv) return null;
    const [factor, cu, cd] = conv;
    if (canonDim === null) { canonDim = cd; canonUnit = cu; }
    else if (cd !== canonDim) return null;
    total += num * factor;
  }
  return canonDim !== null ? [total, canonUnit, canonDim] : null;
};

// "State the units of your answer" — the stem test ExtractsAnswerUnit uses to drop the unit chip.
const questionAsksToStateUnits = (questionText) => {
  const plain = plainAnswerText(questionText ?? "").toLowerCase().replace(/\s+/g, " ");
  return /\b(?:state|give|include|write|show)\b[^.]{0,40}\bunits?\b/.test(plain)
    || /\bunits?\s+of\s+your\s+answer\b/.test(plain);
};

// Decides a measured answer, or returns null to leave it to the tiers below:
//  • the stem asks the student to state the units → the unit must be there, and be equivalent;
//  • the mark scheme is an area / volume → a unit the student does name must be that dimension
//    ("30 cm" is a length, not "30 cm²"). A bare number is left alone — those boxes carry a unit chip.
const measuredUnitVerdict = (submitted, correct, questionText) => {
  const plainCorrect = plainAnswerText(correct).trim();
  const plainSubmitted = plainAnswerText(submitted).trim();
  const cParsed = parseValueWithUnit(plainCorrect);
  if (!cParsed || !cParsed[1]) return null;
  const sParsed = parseValueWithUnit(plainSubmitted);

  if (questionAsksToStateUnits(questionText)) {
    if (!sParsed || !sParsed[1]) {
      const numOk = numericValuesEquivalent(plainSubmitted, plainCorrect);
      return numOk ? null : false;
    }
    return isUnitEquivalent(plainSubmitted, plainCorrect);
  }
  if (/[²³]$/.test(cParsed[1]) && sParsed && sParsed[1] && !isUnitEquivalent(plainSubmitted, plainCorrect)) {
    return false;
  }
  return null;
};

const unitsConflict = (submitted, correct) => {
  const sp = parseValueWithUnit(plainAnswerText(submitted));
  const cp = parseValueWithUnit(plainAnswerText(correct));
  if (!sp || !cp || !sp[1] || !cp[1]) return false;
  return !isUnitEquivalent(submitted, correct);
};

const timesGlyphCalculationMatches = (submitted, correct) => {
  const norm = (v) => plainAnswerText(v).toLowerCase()
    .replace(/[×✕⋅·*]/g, "x")
    .replace(/\s+/g, "");
  const s = norm(submitted);
  const c = norm(correct);
  if (!s || s !== c) return false;
  return /^-?\d+(?:\.\d+)?(?:x-?\d+(?:\.\d+)?)+$/.test(s);
};

const submittedMatchesWorkingEquationResult = (submitted, correct) => {
  const c = plainAnswerText(correct).trim();
  const m = c.match(/^.+\s*=\s*(-?\d+(?:\.\d+)?)\s*$/);
  if (!m || !/[×x*]/.test(c)) return false;
  const s = plainAnswerText(submitted).trim();
  if (!s) return false;
  const sn = plainNumericValue(s);
  const cn = plainNumericValue(m[1]);
  return sn != null && cn != null && Math.abs(sn - cn) < 1e-9;
};

// Operation fill ("x100") vs dotted/filled equation ("45 .....x100..... = 4500").
const operationFillMatchesFilledEquation = (fill, equation) => {
  const fillPlain = plainAnswerText(fill).trim();
  if (!fillPlain || fillPlain.length > 40) return false;
  if (!/^[×x*÷+\-−]\s*-?\d+(?:\.\d+)?$/i.test(fillPlain)
    && !/^(?:multiply|times|divide|add|subtract)\s+(?:by\s+)?-?\d+(?:\.\d+)?$/i.test(fillPlain)) {
    return false;
  }
  const eqPlain = plainAnswerText(equation);
  if (!eqPlain || (!eqPlain.includes("=") && !/\.{3,}/.test(eqPlain))) return false;
  const normalize = (v) => String(v ?? "").toLowerCase()
    .replace(/[×✕⋅·*]/g, "x")
    .replace(/÷/g, "/")
    .replace(/[−–—]/g, "-")
    .replace(/\bmultiply\s+by\b|\btimes\b/g, "x")
    .replace(/\bdivide\s+by\b/g, "/")
    .replace(/\badd\b/g, "+")
    .replace(/\bsubtract\b/g, "-")
    .replace(/\s+/g, "");
  const fillNorm = normalize(fillPlain);
  const eqCollapsed = eqPlain.replace(/\.{2,}/g, " ").replace(/\s+/g, " ").trim();
  const eqNorm = normalize(eqCollapsed);
  if (!fillNorm || !eqNorm) return false;
  // Exact operation token — "x10" must not match inside "x100".
  const escaped = fillNorm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (new RegExp(`(?<![0-9.])${escaped}(?![0-9.])`).test(eqNorm)) return true;
  const opToken = eqCollapsed.match(/[×x*÷+\-−]\s*-?\d+(?:\.\d+)?/i)?.[0];
  return Boolean(opToken && normalize(opToken) === fillNorm);
};

const trueArithmeticEquations = (text) => {
  const t = plainAnswerText(text)
    .replace(/[−–—]/g, "-")
    .replace(/[×⋅·]/g, "*")
    .replace(/÷/g, "/");
  const found = [];
  const re = /(-?\d+(?:\.\d+)?)\s*([+\-*/x])\s*(-?\d+(?:\.\d+)?)\s*=\s*(-?\d+(?:\.\d+)?)/gi;
  let m;
  while ((m = re.exec(t))) {
    const a = Number(m[1]);
    const b = Number(m[3]);
    const r = Number(m[4]);
    const op = String(m[2] || "").toLowerCase();
    let value = null;
    if (op === "+") value = a + b;
    else if (op === "-") value = a - b;
    else if (op === "*" || op === "x") value = a * b;
    else if (op === "/") value = b === 0 ? null : a / b;
    if (value != null && Math.abs(value - r) < 1e-9) found.push(m[0]);
  }
  return found;
};

// Mirrors ExplanatoryAnswerMatcher::verdictWithProvingCalculationAgrees.
export const verdictWithProvingCalculationAgrees = (submitted, correct) => {
  const s = extractYesNoVerdict(submitted);
  const c = extractYesNoVerdict(correct);
  if (s == null || c == null || s !== c) return false;
  if (!trueArithmeticEquations(correct).length) return false;
  return trueArithmeticEquations(submitted).length > 0;
};

const questionImpliesPoundsAnswer = (questionText) => {
  const q = plainAnswerText(questionText).toLowerCase();
  if (!q || (!/[£$€]/.test(q) && !/\bpounds?\b/.test(q))) return false;
  if (/\bcoins?\b|\bpence\b|\b1p\b|\b2p\b|\b5p\b|\b10p\b|\b20p\b|\b50p\b/.test(q)) return false;
  return /%|\bwhat is\b|\bhow much\b|\bcost\b|\bprice\b|\btotal\b/.test(q);
};

const correctIsPoundsValue = (correct) => {
  const c = plainAnswerText(correct).trim();
  return /^£\s*\d{1,3}(?:,\d{3})+(?:\.\d+)?$/.test(c) || /^£\s*\d+(?:\.\d+)?$/.test(c);
};

const submittedLacksCurrencyUnit = (submitted) => {
  let s = plainAnswerText(submitted).trim().replace(/(?<=\d)[,\s](?=\d{3}(?:\D|$))/g, "");
  return /^\d+(?:\.\d+)?$/.test(s);
};

const parseAsMinorCurrencyUnits = (value) => {
  const t = plainAnswerText(value).trim();
  if (!t) return null;
  let m = t.match(/^[£$€]\s*(\d+)\.(\d{1,2})\s*$/u);
  if (m) return parseFloat(m[1]) * 100 + parseFloat(m[2].padEnd(2, "0"));
  m = t.match(/^[£$€]\s*(\d+)\s*$/u);
  if (m) return parseFloat(m[1]) * 100;
  m = t.match(/^(\d+)\s*(?:p|pence)\s*$/iu);
  if (m) return parseFloat(m[1]);
  m = t.match(/^(\d+)\.(\d{1,2})\s*$/u);
  if (m && parseFloat(m[1]) < 1000) {
    return parseFloat(m[1]) * 100 + parseFloat(m[2].padEnd(2, "0"));
  }
  m = t.match(/^(\d+)\s*$/u);
  if (m) return parseFloat(m[1]);
  return null;
};

const unspecifiedCurrencyMagnitudeMatch = (submitted, correct, questionText) => {
  const q = plainAnswerText(questionText).toLowerCase();
  if (q && /\bin\s+(?:pence|pounds?|euros?|cents?)\b|\bgive\s+(?:your\s+)?answer\s+in\b|\banswer\s+in\s+(?:pence|pounds?|euros?)\b/u.test(q)) {
    return false;
  }
  const s = parseAsMinorCurrencyUnits(submitted);
  const c = parseAsMinorCurrencyUnits(correct);
  if (s == null || c == null) return false;
  return Math.abs(s - c) < 0.011;
};

const submittedMatchesSchemeIgnoringPrintedStemWords = (submitted, correct, questionText) => {
  const stem = plainAnswerText(questionText).toLowerCase();
  const sub = plainAnswerText(submitted).toLowerCase();
  const corPlain = plainAnswerText(correct);
  if (!stem || !sub || !corPlain) return false;
  const stop = new Set(["the", "a", "an", "and", "or", "of", "to", "in", "on", "for", "is", "are", "was",
    "were", "be", "by", "with", "as", "at", "from", "that", "this", "it", "its", "he", "she",
    "they", "his", "her", "their", "how", "what", "many", "much", "does", "do", "did"]);
  const stemSet = new Set(
    stem.replace(/[^\p{L}\p{N}\s]/gu, " ").split(/\s+/).filter((t) => t.length >= 3 && !stop.has(t))
  );
  const subSet = new Set(sub.replace(/[^\p{L}\p{N}\s]/gu, " ").split(/\s+/).filter(Boolean));
  const reduced = corPlain.replace(/\b([\p{L}][\p{L}']*)\b/gu, (full, w) => {
    const low = w.toLowerCase();
    if (stemSet.has(low) && !subSet.has(low) && low.length >= 3) return "";
    return w;
  }).replace(/\s+/g, " ").trim();
  if (!reduced || reduced.toLowerCase() === corPlain.toLowerCase()) return false;
  if (submissionAddsWrongShapeHead(submitted, correct)) return false;
  const sn = plainNumericValue(submitted);
  const cn = plainNumericValue(reduced);
  if (sn != null && cn != null && Math.abs(sn - cn) < 1e-9) return true;
  const ns = normalizeVal(submitted);
  const nr = normalizeVal(reduced);
  return ns === nr || (nr.includes(ns) && ns.length >= 2);
};

// "watts" / "watt" / "W", "cals" / "calorie" — one unit spelt the ways parseValueWithUnit accepts.
const UNIT_SPELLING_ALIASES = { w: "watt", kilowatt: "kw", calorie: "cal" };
const sameUnitSpelling = (a, b) => {
  const canon = (u) => {
    const base = String(u ?? "").toLowerCase().replace(/(?<=[a-z]{2})s$/, "");
    return UNIT_SPELLING_ALIASES[base] ?? base;
  };
  return canon(a) !== "" && canon(a) === canon(b);
};

export const isUnitEquivalent = (submitted, correct) => {
  const sp = parseValueWithUnit(submitted);
  const cp = parseValueWithUnit(correct);

  if (sp && cp) {
    const [sNum, sUnit] = sp;
    const [cNum, cUnit] = cp;
    if (sUnit && cUnit) {
      const sc = UNIT_CONVERSIONS[sUnit];
      const cc = UNIT_CONVERSIONS[cUnit];
      if (sc && cc && sc[2] === cc[2]) return Math.abs(sNum * sc[0] - cNum * cc[0]) < 0.001;
      // Units parseValueWithUnit knows but UNIT_CONVERSIONS doesn't (km/h, m/s, W, cal, RF…) have no
      // factor to compare through, so "48 km/h" was unequal to "48 km/h" itself — unitsConflict then
      // overturned the server's Correct on Check answer (Highgate Practice Q10, Alleyn's Practice Q14).
      if (!sc && !cc && sameUnitSpelling(sUnit, cUnit)) return Math.abs(sNum - cNum) < 0.001;
      return false;
    }
    if (!sUnit && cUnit) {
      // A bare student number matches the mark-scheme NUMBER only (value-first) — it must NOT
      // be silently reinterpreted as the parent unit. That made "51" match "51000 m" (reading 51
      // as 51 km) and "1.75" match "175p" (reading 1.75 as £1.75). Mirrors the backend's
      // MeasurementUnitAnswerMatcher, which never does this parent-unit upgrade either.
      if (cp[2]) return false;
      return Math.abs(sNum - cNum) < 0.001;
    }
    if (sUnit && !cUnit) {
      if (sp[2]) return false;
      return Math.abs(sNum - cNum) < 0.001;
    }
  }

  // Compound value check: "1 hour 30 min" vs "90 min", "1 m 50 cm" vs "150 cm", etc.
  const sCompound = parseCompoundValue(submitted);
  const cCompound = parseCompoundValue(correct);
  if (sCompound !== null || cCompound !== null) {
    const resolveCanon = (compound, parsed) => {
      if (compound) return compound;
      if (parsed && parsed[1]) {
        const conv = UNIT_CONVERSIONS[parsed[1]];
        if (conv) return [parsed[0] * conv[0], conv[1], conv[2]];
      }
      return null;
    };
    const sCanon = resolveCanon(sCompound, sp);
    const cCanon = resolveCanon(cCompound, cp);
    if (sCanon && cCanon && sCanon[2] === cCanon[2]) {
      return Math.abs(sCanon[0] - cCanon[0]) < 0.001;
    }
  }

  return false;
};

/** Fractional digit count on a bare number ("4.71" → 2, "4.7" → 1, "5" → 0). Null if not numeric. */
export const decimalPlaceCount = (value) => {
  const bare = stripCommonUnit(String(value ?? "").trim().toLowerCase().replace(/[£$€¥%]/g, "")).trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(bare)) return null;
  const dot = bare.indexOf(".");
  return dot === -1 ? 0 : bare.length - dot - 1;
};

/**
 * A decimal mark scheme only tolerates extra decimal places when it LOOKS like a rounded
 * non-terminating value ("4.7" for 14/3, "4.33" for 13/3). An exact terminator ("4.5", "0.25",
 * "12.5") IS the whole answer, so a longer value that merely rounds back to it ("4.54", "0.254")
 * is a different quantity and must stay wrong. Mirrors the backend's schemeIsRoundedNonTerminating.
 */
export const schemeIsRoundedNonTerminating = (correct) => {
  const bare = stripCommonUnit(String(correct ?? "").trim().toLowerCase().replace(/[£$€¥%]/g, "")).trim();
  const m = bare.match(/^-?\d+\.(\d+)$/);
  if (!m) return false;
  const frac = m[1];
  // A single fractional digit says nothing ("5.4" is exactly 54/10 as often as it is a rounded 5.44…),
  // so only a repeated final digit ("4.33", "0.6667") counts as a rounded recurring value.
  return frac.length >= 2 && frac[frac.length - 1] === frac[frac.length - 2];
};

/**
 * Extra non-zero fractional digits beyond an exact mark scheme ("1.3541" ≠ "1.354").
 * Trailing zeros stay equivalent ("1.3540" ≡ "1.354"). Mirrors FuzzyAnswerMatching.
 */
export const extraNonZeroDecimalPlacesMismatch = (submitted, correct) => {
  const cDp = decimalPlaceCount(correct);
  const sDp = decimalPlaceCount(submitted);
  if (cDp === null || sDp === null || sDp <= cDp || cDp < 1) return false;
  if (schemeIsRoundedNonTerminating(correct)) return false;
  const cBare = stripCommonUnit(String(correct ?? "").trim().toLowerCase().replace(/[£$€¥%]/g, "")).trim();
  const sBare = stripCommonUnit(String(submitted ?? "").trim().toLowerCase().replace(/[£$€¥%]/g, "")).trim();
  if (!/^-?\d+\.\d+$/.test(cBare) || !/^-?\d+\.\d+$/.test(sBare)) return false;
  const cDot = cBare.indexOf(".");
  const sDot = sBare.indexOf(".");
  if (sBare.slice(0, sDot) !== cBare.slice(0, cDot)) return true;
  const cFrac = cBare.slice(cDot + 1);
  const sFrac = sBare.slice(sDot + 1);
  if (!sFrac.startsWith(cFrac)) return true;
  const extra = sFrac.slice(cFrac.length);
  return extra !== "" && !/^0+$/.test(extra);
};

/**
 * A recurring decimal written to a different number of places than the mark scheme: the submission
 * either continues the scheme's repeating digit ("4.3333" for "4.3", "0.3333" for "0.333") or rounds
 * the repeating expansion to fewer places ("0.33" for "0.333"). Outside a percentage a 1-d.p. scheme
 * needs a real repeating tail — one appended digit ("5.44" for "5.4 m") is a different answer.
 * Mirrors FuzzyAnswerMatching::percentOrRecurringDecimalMatch (the recurring half; the take page
 * decides these itself whenever the server returns no rule verdict).
 */
export const recurringDecimalExtensionMatch = (submitted, correct) => {
  const hasPercent = String(correct).includes("%") || String(submitted).includes("%")
    || /\bper\s*cent(?:age)?\b/i.test(String(correct) + String(submitted));
  if (hasPercent) {
    const stripPct = (v) => String(v ?? "").replace(/\bper\s*cent(?:age)?\b/gi, "").replace(/%/g, "").trim();
    const sVal = parseSimpleNumericValue(stripPct(submitted));
    const cVal = parseSimpleNumericValue(stripPct(correct));
    if (sVal !== null && cVal !== null && Math.abs(sVal - cVal) < 1e-9) return true;
  }
  const clean = (v) =>
    String(v ?? "")
      .replace(/<[^>]*>/g, "")
      .replace(/\bper\s*cent(?:age)?\b/gi, "%")
      .replace(/[%\s ]/g, "")
      .trim();
  const c = clean(correct);
  const s = clean(submitted);
  if (!/^-?\d+\.\d+$/.test(c) || !/^-?\d+\.\d+$/.test(s)) return false;
  const cFrac = c.slice(c.indexOf(".") + 1);
  const sFrac = s.slice(s.indexOf(".") + 1);
  const last = cFrac[cFrac.length - 1];
  const recurringLooking = cFrac.length >= 2
    ? cFrac[cFrac.length - 1] === cFrac[cFrac.length - 2]
    : last !== "0" && last !== "5";
  if (!recurringLooking) return false;

  if (sFrac.length > cFrac.length) {
    if (!s.startsWith(c)) return false;
    const extra = sFrac.slice(cFrac.length);
    if (cFrac.length === 1 && !hasPercent && extra.length < 2) return false;
    return extra !== "" && extra.split("").every((d) => d === last);
  }
  if (sFrac.length < cFrac.length) {
    const extended = Number(`${c.slice(0, c.indexOf("."))}.${cFrac}${last.repeat(12)}`);
    return extended.toFixed(sFrac.length) === s;
  }
  return false;
};

/**
 * Mark scheme to N d.p. ("4.7 mm") — a more precise working value that rounds to it ("4.71") is accepted.
 * Integer schemes stay strict ("5.1" ≠ "5"), and so do exact terminators ("4.54" ≠ "4.5").
 */
export const roundedDecimalPrecisionMatch = (submitted, correct) => {
  const cDp = decimalPlaceCount(correct);
  const sDp = decimalPlaceCount(submitted);
  if (cDp === null || sDp === null || cDp < 1 || sDp <= cDp) return false;
  if (!schemeIsRoundedNonTerminating(correct)) return false;
  const sn = plainNumericValue(stripCommonUnit(String(submitted ?? "").trim()));
  const cn = plainNumericValue(stripCommonUnit(String(correct ?? "").trim()));
  if (sn === null || cn === null) return false;
  const factor = 10 ** cDp;
  return Math.abs(Math.round(sn * factor) / factor - cn) < 1e-9;
};

export const questionAsksForEstimate = (questionText) => {
  const q = decodeHtmlSpaces(String(questionText ?? ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  if (!q) return false;
  return /\bestimate\b/.test(q) || /\bapproximately\b/.test(q) || /\broughly\b/.test(q);
};

/**
 * Estimate / approximately stems accept a precise calculation that rounds to an integer
 * mark-scheme estimate (e.g. Bertie bee 34.14 for 34). A non-integer scheme ("3.24") stays
 * exact — nearby values like 3.23 / "3 23" are incorrect.
 */
// A mark scheme that states a tolerance instead of a value — "answer around 130 to 140" on an
// "estimate the angle" question (Chigwell 2020 Q22). Mirrors the backend's markSchemeToleranceRange:
// a tolerance word is required, so "between 4 and 8" (a scheme whose answer is BOTH endpoints)
// stays a literal answer.
export const markSchemeToleranceRange = (correct) => {
  const text = decodeHtmlEntities(decodeHtmlSpaces(stripAnswerHtmlTags(correct))).replace(/\s+/g, " ").trim();
  if (!text || text.length > 120) return null;
  if (!/\b(?:around|about|approx(?:\.|imately)?|roughly|accepts?|allow|anything|any\s+(?:value|answer|number)|estimates?|answers?\s+(?:in|within))\b/i.test(text)) return null;
  const m = text.match(/(-?\d+(?:[.,]\d+)?)\s*(?:to|and|until|up\s+to|–|—|-)\s*(-?\d+(?:[.,]\d+)?)/i);
  if (!m) return null;
  const min = Number(String(m[1]).replace(",", "."));
  const max = Number(String(m[2]).replace(",", "."));
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return null;
  return min < max ? [min, max] : [max, min];
};

export const markSchemeToleranceAccepts = (submitted, correct) => {
  const range = markSchemeToleranceRange(correct);
  if (!range) return false;
  const value = plainNumericValue(stripCommonUnit(String(submitted ?? "").trim()));
  if (value === null || !Number.isFinite(value)) return false;
  return value >= range[0] - 1e-9 && value <= range[1] + 1e-9;
};

export const estimateNumericAnswerEquivalent = (submitted, correct, questionText) => {
  if (!questionAsksForEstimate(questionText)) return false;
  const bareSubmitted = stripCommonUnit(String(submitted ?? "").trim());
  const bareCorrect = stripCommonUnit(String(correct ?? "").trim());
  if (!isPlainNumberVal(bareSubmitted) || !isPlainNumberVal(bareCorrect)) return false;
  const s = plainNumericValue(bareSubmitted);
  const c = plainNumericValue(bareCorrect);
  if (s === null || c === null) return false;
  if (Math.abs(c - Math.round(c)) >= 0.0001) return false;
  if (Math.round(s) === Math.round(c)) return true;
  return Math.abs(s - c) <= 0.5;
};

// Split on newlines, semicolons, space-pipe-space (" | ", the frontend's multi-fill
// joiner), and commas — but NOT thousands-separator commas (digit,digit{3}non-digit).
// "B and D" is normalised to "B, D" before splitting.
const expandMultiValueAndJoiners = (v) => {
  let out = String(v ?? "").trim();
  if (!out) return out;
  let prev = "";
  while (prev !== out) {
    prev = out;
    out = out.replace(/\b([A-Za-z])\s+and\s+([A-Za-z])\b/gi, "$1, $2");
    out = out.replace(/\b(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)\b/g, "$1, $2");
  }
  return out;
};

const parseListItemsFromHtml = (raw) => {
  if (!/<li\b/i.test(raw)) return null;
  const parts = [];
  const re = /<li\b[^>]*>(.*?)<\/li>/gis;
  let m;
  while ((m = re.exec(raw)) !== null) {
    const part = decodeHtmlSpaces(String(m[1] ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    if (part) parts.push(part);
  }
  return parts.length > 0 ? parts : null;
};

export const splitSubmittedListLines = (submitted) => {
  const text = decodeHtmlEntities(String(submitted ?? "").replace(/&nbsp;/gi, " ").replace(/<[^>]*>/g, " "))
    .trim();
  if (!text) return [];
  const lines = text.split(/[\n\r]+/).map((l) => l.trim()).filter(Boolean);
  if (lines.length >= 2) return lines;
  return splitMultiValueAnswer(text);
};

export const splitMultiValueAnswer = (v) => {
  const raw = String(v ?? "");
  const liParts = parseListItemsFromHtml(raw);
  if (liParts) return liParts;
  return expandMultiValueAndJoiners(decodeHtmlSpaces(raw).replace(/<[^>]*>/g, " "))
    .split(/[\n\r;]|\s+\|\s+|,(?!\d{3}(?!\d))/)
    .map((p) => p.trim())
    .filter(Boolean);
};

// A single number mistyped with a comma decimal separator ("3,00", "£3,5", "0,918", "3,0 m") — one
// comma between digits, ignoring a leading currency symbol and a trailing unit. Not a list
// ("3, 5, 7") or thousands group ("1,200").
export const isSingleMalformedNumber = (v) => {
  let s = decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, "").trim().replace(/^[£$€¥]/, "").trim();
  s = s.replace(/\s*(?:mm²|mm2|mm³|mm3|cm²|cm2|cm³|cm3|m²|m2|m³|m3|km²|km2|mm|cm|km|kg|mg|ml|m|g|l|p|°c|°f|°)\s*$/iu, "").trim();
  if (/^-?[1-9]\d{0,2}(?:,\d{3})+(?:\.\d+)?$/.test(s)) return false;
  return /^-?\d+,\d+$/.test(s);
};

// "0,918" for 0.918 — the comma is not a decimal point. Only against a single-value numeric mark
// scheme: a comma-joined list scheme ("12, 7") is legitimately answered "12,7". Mirrors the
// submittedIsSingleMalformedNumber arm of FuzzyAnswerMatching::submissionIsDeterministicallyWrong,
// which the client was missing — so the AI panel could still overturn the rule verdict and show
// "Correct" on the Result / Review pages over a stored 0 marks.
// Also "10,274" for "10.274": looks like thousands grouping to isSingleMalformedNumber, but the
// digit groups match the mark scheme's decimal after swapping the separator.
export const submittedCommaDecimalsNumber = (submitted, correct) => {
  const c = stripCommonUnit(
    decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim().replace(/^[£$€¥]\s*/, "")
  ).replace(/\s*%$/, "").trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(c)) return false;
  if (isSingleMalformedNumber(submitted)) return true;
  if (!/^-?\d+\.\d+$/.test(c)) return false;
  let s = decodeHtmlSpaces(String(submitted ?? "")).replace(/<[^>]*>/g, "").trim().replace(/^[£$€¥]/, "").trim();
  s = s.replace(/\s*(?:mm²|mm2|mm³|mm3|cm²|cm2|cm³|cm3|m²|m2|m³|m3|km²|km2|mm|cm|km|kg|mg|ml|m|g|l|p|°c|°f|°)\s*$/iu, "").trim();
  if (!/^-?\d+,\d+$/.test(s)) return false;
  return s.replace(/,/g, "") === c.replace(/\./g, "");
};

// Mirrors ExplanatoryAnswerMatcher::extractYesNoVerdict — opposite polarity must never upgrade.
// An authored mark scheme arrives as HTML ("<p>No,&nbsp;because…</p>"); every test below is anchored
// at the start of a chunk, so the surviving tag word ("p no because…") matched none of them and the
// guard never fired for a stored scheme. Strip the markup first, exactly as the PHP side does.
export const extractYesNoVerdict = (text) => {
  const parts = decodeHtmlSpaces(stripAnswerHtmlTags(text)).split(/[.!?]+/).map((p) => p.trim()).filter(Boolean);
  const chunks = (parts.length ? parts : [String(text ?? "")]).map((p) =>
    p.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").replace(/\s+/g, " ").trim()
  );
  for (const chunk of chunks) {
    if (!chunk) continue;
    if (/^(no|nope|false)\b/u.test(chunk)) return "no";
    if (/^(yes|yeah|yep|true)\b/u.test(chunk)) return "yes";
    // "…so the statement is false" / "…it is true" — verdict after a proving calculation.
    if (/\b(?:(?:the\s+)?statement|it)\s+is\s+false\b|\bis\s+false\b/u.test(chunk)) return "no";
    if (/\b(?:(?:the\s+)?statement|it)\s+is\s+true\b|\bis\s+true\b/u.test(chunk)) return "yes";
    if (/\bnot correct\b|\bis not correct\b|\bis incorrect\b|\bincorrect\b/u.test(chunk)) return "no";
    if (/\bis correct\b/u.test(chunk) && !/\bnot\b/u.test(chunk)) return "yes";
    if (/\bit is not (?:also )?a multiple\b|\bnot a multiple\b/u.test(chunk)) return "no";
    if (/\bit is (?:also )?a multiple\b/u.test(chunk)) return "yes";
  }
  return null;
};

export const yesNoVerdictConflict = (submitted, correct) => {
  const s = extractYesNoVerdict(submitted);
  const c = extractYesNoVerdict(correct);
  return s != null && c != null && s !== c;
};

// Closed decision questions whose verdict is a domain verb rather than yes/no: "Did Lorenzo pass?"
// is answered "Lorenzo did not pass", which carries no yes/no word for extractYesNoVerdict to find.
// [deny pattern, affirm pattern] — deny is tested first, since "did not pass" contains "pass". The
// affirm patterns exclude the noun uses of the same word ("the pass mark", "the special pass"),
// which restate the question rather than decide it. Mirrors ExplanatoryAnswerMatcher::DECISION_FAMILIES.
const DECISION_FAMILIES = [
  [
    /\b(?:fail(?:s|ed|ing)?|(?:did|does|do|is|was|will|would|could|can)\s*n(?:o|')?t\s+pass|not\s+pass(?:ed|es)?)\b/u,
    /\bpass(?:es|ed)?\b(?!\s*(?:mark|marks|rate|score|grade|level|percentage|boundary))/u,
  ],
  [
    /\b(?:lose[sr]?|lost|(?:did|does|do|will|would|could|can)\s*n(?:o|')?t\s+win|not\s+win)\b/u,
    /\b(?:wins?|won)\b/u,
  ],
  [
    /\b(?:cannot|can|could|does|did|will|would)\s*n(?:o|')?t\s+afford\b|\bnot\s+afford\b|\bnot\s+(?:got\s+|have\s+)?enough\s+money\b/u,
    /\bafford(?:s|ed)?\b|\benough\s+money\b/u,
  ],
  [
    /\b(?:(?:does|did|do|will|would)\s*n(?:o|')?t\s+qualify|not\s+qualif(?:y|ies|ied))\b/u,
    /\bqualif(?:y|ies|ied)\b/u,
  ],
  [
    /\b(?:(?:will|does|do|did|can|could)\s*n(?:o|')?t\s+fit|not\s+fit|too\s+(?:big|large|small|wide|long)\b)/u,
    /\bfits?\b/u,
  ],
];

// "the special pass", "a day pass" — the noun, not the verb. Masked out before any family pattern
// runs, so "Does the special pass provide value for money?" is not read as a pass/fail decision.
// (A lookbehind in the pattern itself would be a parse error on older Safari and take the whole
// module down with it.)
const DECISION_NOUN_USES = /\b(?:the|a|an|this|that|his|her|their|my|your|its|special|bus|train|day|season|entry)\s+pass(?:es)?\b/gu;

const plainDecisionText = (text) =>
  decodeHtmlEntities(decodeHtmlSpaces(stripAnswerHtmlTags(text)))
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .replace(DECISION_NOUN_USES, " ");

const decisionFamilyForQuestion = (questionText) => {
  const q = plainDecisionText(questionText);
  // Only a CLOSED question decides something: the clause has to open with the auxiliary ("Did
  // Lorenzo pass?", "…; can Bella afford it?"). "Who will win this game?" opens with "who" and is
  // answered by a name, not a verdict.
  const clauses = q.match(/(?:^|[.!?;:,]\s*)(?:did|does|do|is|are|was|were|will|would|can|could|has|have|had)\b[^.?!]*\?/gu);
  if (!clauses) return null;
  for (const clause of clauses) {
    const family = DECISION_FAMILIES.find((f) => f[0].test(clause) || f[1].test(clause));
    if (family) return family;
  }
  return null;
};

const decisionVerdictIn = (text, family) => {
  const t = plainDecisionText(text);
  if (!t) return null;
  if (family[0].test(t)) return "no";
  if (family[1].test(t)) return "yes";
  return null;
};

// "Did Lorenzo pass?" answered with working alone ("26 out of 40 = 65%") never states the decision
// the question asked for, and AI Vision reads the agreeing numbers as a full answer. True when the
// mark scheme states a decision the submission does not — the opposite one, or none at all.
// Demote-only. Mirrors ExplanatoryAnswerMatcher::submissionMissesSchemeDecision.
export const submissionMissesSchemeDecision = (submitted, correct, questionText = "") => {
  if (String(submitted ?? "").trim() === "" || String(correct ?? "").trim() === "") return false;
  const family = decisionFamilyForQuestion(questionText);
  if (!family) return false;
  // The scheme must state the decision in the question's own terms. A bare "yes"/"no" opener
  // ("yes. DEAL 3") is not enough: naming the deal answers the question implicitly.
  const scheme = decisionVerdictIn(correct, family);
  if (scheme == null) return false;
  const student = decisionVerdictIn(submitted, family) ?? extractYesNoVerdict(submitted);
  return student !== scheme;
};

const comparisonPolarity = (text) => {
  const t = plainDecisionText(text);
  const less = /\b(?:less|lower|smaller|fewer)\s+than\b|\bbelow\b/u.test(t);
  const more = /\b(?:greater|more|higher|bigger|larger)\s+than\b|\babove\b/u.test(t);
  if (less && !more) return "less";
  if (more && !less) return "more";
  return null;
};

const evalNumericChain = (expr) => {
  if (!/^[0-9+\-*/.]+$/.test(expr)) return null;
  const tokens = expr.match(/\d+(?:\.\d+)?|[+\-*/]/g);
  if (!tokens || tokens.length < 3 || !/^\d/.test(tokens[0])) return null;
  const factors = [];
  let current = Number(tokens[0]);
  for (let i = 1; i < tokens.length; i += 2) {
    const op = tokens[i];
    const rhs = Number(tokens[i + 1]);
    if (!op || !Number.isFinite(rhs)) return null;
    if (op === "*") current *= rhs;
    else if (op === "/") {
      if (rhs === 0) return null;
      current /= rhs;
    } else {
      factors.push(current);
      factors.push(op);
      current = rhs;
    }
  }
  factors.push(current);
  let value = Number(factors[0]);
  for (let i = 1; i < factors.length; i += 2) {
    const rhs = Number(factors[i + 1]);
    if (factors[i] === "+") value += rhs;
    else value -= rhs;
  }
  return Number.isFinite(value) ? value : null;
};

const proseStatesFalseEquation = (text) => {
  const flat = plainAnswerText(text).replace(/[()]/g, "");
  const t = flat
    .replace(/[×⋅·]/g, "*")
    .replace(/÷/g, "/")
    .replace(/[−–—]/g, "-")
    .replace(/(?<=\d)\s*x\s*(?=\d)/gi, "*");
  const re = /(?<![\d.])(\d+(?:\.\d+)?(?:\s*[+\-*/]\s*\d+(?:\.\d+)?)+)\s*=\s*(-?\d+(?:\.\d+)?)(?!\d|\.\d)/g;
  let m;
  while ((m = re.exec(t))) {
    const expr = m[1].replace(/\s+/g, "");
    const value = evalNumericChain(expr);
    if (value == null) continue;
    const rhs = Number(m[2]);
    if (Math.abs(value - rhs) > 1e-9 * Math.max(1, Math.abs(value), Math.abs(rhs))) return true;
  }
  return false;
};

// "Did Lorenzo pass? Show your working." Same verdict and the same figures, with "/" for "÷"
// and the conclusion in either sentence. A missing verdict, the opposite verdict, or a
// calculation that does not hold stays wrong.
// Mirrors ExplanatoryAnswerMatcher::workedDecisionExplanationAgrees.
export const workedDecisionExplanationAgrees = (submitted, correct, questionText = "") => {
  const s = String(submitted ?? "").trim();
  const c = String(correct ?? "").trim();
  const q = String(questionText ?? "").trim();
  if (!s || !c || !q) return false;
  if (submissionMissesSchemeDecision(s, c, q)) return false;
  const family = decisionFamilyForQuestion(q);
  if (!family) return false;
  const schemeVerdict = decisionVerdictIn(c, family);
  const studentVerdict = decisionVerdictIn(s, family) ?? extractYesNoVerdict(s);
  if (schemeVerdict == null || studentVerdict !== schemeVerdict) return false;
  const schemePolarity = comparisonPolarity(c);
  const studentPolarity = comparisonPolarity(s);
  if (schemePolarity && studentPolarity && schemePolarity !== studentPolarity) return false;
  if (proseStatesFalseEquation(s)) return false;
  const figures = (text) => [...new Set((plainAnswerText(text).match(/-?\d+(?:\.\d+)?/g) || []))];
  const schemeFigures = figures(c);
  if (!schemeFigures.length) return false;
  const have = new Set(figures(s));
  return schemeFigures.every((n) => have.has(n));
};

// "Jane says … Annabel says … Who is correct?" — naming the person IS the conclusion, and the reason
// alone does not give it. The decision families above only cover closed yes/no questions.
// Demote-only. Mirrors ExplanatoryAnswerMatcher::submissionMissesSchemeNamedSubject.
export const submissionMissesSchemeNamedSubject = (submitted, correct, questionText = "") => {
  const plainCased = (v) => decodeHtmlEntities(decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, " "))
    .replace(/\s+/g, " ").trim();
  const q = plainCased(questionText);
  if (!q || !/\bwho\s+(?:do\s+you\s+think\s+)?(?:is|was|are|were)\s+(?:correct|right|wrong|incorrect)\b/i.test(q)) {
    return false;
  }
  const candidates = [...new Set(
    [...q.matchAll(/\b([A-Z][a-z]{2,})\s+(?:says?|said|thinks?|thought|claims?|is\s+correct|is\s+right|is\s+wrong)\b/g)]
      .map((m) => m[1])
  )];
  if (candidates.length < 2) return false;
  const c = plainCased(correct);
  const named = candidates.filter((name) => new RegExp(`\\b${name}\\b`, "i").test(c));
  if (named.length !== 1) return false;
  return !new RegExp(`\\b${named[0]}\\b`, "i").test(plainCased(submitted));
};

const COMPARISON_WORDS = "larger|largest|bigger|biggest|greater|greatest|smaller|smallest|less|least|lesser|heavier|heaviest|lighter|lightest|longer|longest|shorter|shortest|faster|fastest|slower|slowest|cheaper|cheapest|dearer|more expensive|closer|closest|nearer|nearest|higher|highest|lower|lowest|better|best|worse|worst|more|most|taller|tallest|wider|widest|deeper|deepest";

// "Which is larger, 75% or 5/8? Give a reason for your answer." — the question asks for two things,
// and comparing the two decimals answers one of them. Demote-only.
// Mirrors ExplanatoryAnswerMatcher::submissionMissesSchemeComparisonChoice.
export const submissionMissesSchemeComparisonChoice = (submitted, correct, questionText = "") => {
  const plain = (v) => decodeHtmlEntities(
    decodeHtmlSpaces(String(v ?? "").replace(/\\d?frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, "$1/$2"))
      .replace(/\\[()[\]]|\$/g, " ")
      .replace(/<[^>]*>/g, " ")
  ).replace(/\s+/g, " ").trim();
  const q = plain(questionText);
  const m = q.match(new RegExp(`\\bwhich\\s+(?:one\\s+)?(?:is|was|has|would\\s+be)\\s+(?:the\\s+)?(${COMPARISON_WORDS})\\b[^?]{0,40}?[,:]?\\s*(\\S[^?]{0,40}?)\\s+or\\s+(\\S[^?]{0,40}?)\\s*\\?`, "i"));
  if (!m) return false;
  // Only where the question asks for the choice AND a reason. "Which is larger: 4/7 or 5/9?" on its own
  // is answered by "4/7" and nothing else.
  if (!/\b(?:give\s+(?:a\s+)?reasons?|reason\s+for\s+your\s+answer|explain|explaining|why|justify|how\s+do\s+you\s+know|show\s+(?:your\s+)?working)\b/i.test(q)) return false;
  const comparative = m[1];
  const trim = (raw) => raw.trim().replace(/^(?:the|a|an)\s+/i, "").replace(/^[\s.,;:()]+|[\s.,;:()]+$/g, "");
  const options = [...new Set([trim(m[2]), trim(m[3])])].filter(Boolean);
  if (options.length !== 2) return false;

  const esc = (v) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const asserts = (text, option) => {
    if (!text || !option) return false;
    // A percent option keeps its sign optional — the choice box holds "70" as readily as "70%".
    const o = option.endsWith("%") ? `${esc(option.replace(/%+$/, ""))}\\s*%?` : esc(option);
    const cmp = esc(comparative);
    return new RegExp(`^\\W*${o}\\s*(?:[.;!|\\n]|,\\s*(?:because|since|as|it|this)\\b|$)`, "i").test(text)
      || new RegExp(`${o}\\s*(?:is|was|are|were)\\s+(?:the\\s+)?(?:${COMPARISON_WORDS})\\b`, "i").test(text)
      || new RegExp(`(?:the\\s+)?${cmp}\\s+(?:one\\s+)?(?:is|was)\\s+${o}(?![\\w])`, "i").test(text);
  };

  const c = plain(correct);
  const chosen = options.filter((o) => asserts(c, o));
  if (chosen.length !== 1) return false;
  const s = plain(submitted);
  // "This is because…" jumped straight to the reason (NLIGSC 2013 Q13c).
  if (/^\W*(?:this\s+is\s+because|because|since)\b/i.test(s)) return true;
  return !asserts(s, chosen[0]);
};

// Mirrors ExplanatoryAnswerMatcher::submissionMissesWorkedComparisonChoice.
export const submissionMissesWorkedComparisonChoice = (submitted, correct) => {
  const plain = (v) => decodeHtmlEntities(decodeHtmlSpaces(String(v ?? "").replace(/<[^>]*>/g, " ")))
    .replace(/\s+/g, " ").trim().toLowerCase();
  const stop = new Set(["the", "a", "an", "is", "are", "was", "were", "be", "would", "will", "to", "use",
    "using", "buy", "buying", "go", "going", "with", "and", "at", "costing", "cost", "costs", "for", "of",
    "option", "options", "deal", "deals", "choice", "choices", "one", "cheapest", "cheaper", "best",
    "better", "value", "company", "shop", "so", "therefore", "then", "it", "this", "that",
    "coaches", "coach", "buses", "bus", "firm", "school"]);
  const choiceTokens = (text) => {
    const t = plain(text);
    if (!t) return null;
    let raw = null;
    const m1 = t.match(/\b(?:cheapest|best|cheaper|better)\s+(?:deal|option|choice|value|one)?\s*(?:is|would\s+be)\s+(?:to\s+(?:use|buy|choose|pick|go\s+with)\s+)?([^,.;:]{1,40})/i);
    const m2 = t.match(/\b([\p{L}][\p{L}\p{N}'\u2019\- ]{1,40}?)\s+(?:is|are|would\s+be)\s+(?:the\s+)?(?:cheapest|best|cheaper|better)\b/iu);
    raw = (m1 && m1[1]) || (m2 && m2[1]) || null;
    if (!raw) return null;
    const tokens = [...new Set(raw.split(/[^\p{L}\p{N}]+/u).filter((w) => w.length > 2 && !stop.has(w)))];
    return tokens.length ? tokens : null;
  };
  const scheme = choiceTokens(correct);
  if (!scheme) return false;
  const amounts = [...new Set((String(correct ?? "").match(/£\s*(\d+(?:\.\d+)?)/g) || [])
    .map((m) => m.replace(/£\s*/, "")))];
  if (amounts.length < 2) return false;
  const s = plain(submitted);
  if (!s) return false;
  if (amounts.filter((a) => new RegExp(`(?<![\\d.])${a.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?!\\d|\\.\\d)`).test(s)).length < 2) {
    return false;
  }
  const student = choiceTokens(submitted);
  if (student) {
    const shared = scheme.filter((t) => student.includes(t));
    if (shared.join() === scheme.join() || shared.join() === student.join()) return false;
  }
  // A worked comparison names EVERY rival option in its arithmetic ("Orange Coaches seat 44 people …
  // £750"), so finding the scheme's choice somewhere in the answer proves nothing about which one the
  // student settled on. It counts only in a clause that DECIDES ("the best deal is Orange Coaches"),
  // or standing alone as the concluding line the mark scheme itself opens with.
  const cue = /\b(?:cheapest|cheaper|best|better|winner|wins|win|choose|chooses|chose|chosen|choice|pick|picks|picked|select|selects|selected|recommend|recommends|should|answer|go\s+with|opt(?:s|ed)?\s+for)\b/i;
  const namesChoice = (clause) => scheme.some((t) => new RegExp(`\\b${t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(clause));
  const decided = s.split(/(?<=[.!?;])\s+|\n+/).some((clause) => {
    const c = clause.trim();
    if (!c || !namesChoice(c)) return false;
    if (cue.test(c)) return true;
    // Working sentences carry figures, so a short, digit-free clause is the conclusion stated
    // without a verb — the shape the mark scheme's own first line uses.
    return c.replace(/[.!?;:]+$/, "").trim().split(/\s+/).length <= 4 && !/\d/.test(c);
  });
  return !decided;
};

// Mirrors AlgebraicExpressionMatcher equation equivalence ("2+A=R+N" ≡ "A=N+R-2").
export const algebraicEquationMatches = (submitted, correct) => {
  const norm = (v) => String(v ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/[×⋅·]/g, "*").replace(/÷/g, "/").replace(/[−–—\u2010\u2011\u2012]/g, "-")
    .toLowerCase().replace(/\s+/g, "").trim();
  const signedAtoms = (eq) => {
    if (!eq.includes("=")) return null;
    const i = eq.indexOf("=");
    const lhs = eq.slice(0, i);
    const rhs = eq.slice(i + 1);
    if (!lhs || !rhs || lhs.includes("=") || rhs.includes("=")) return null;
    const atoms = [];
    const addSide = (side, flip) => {
      const s = /^[+-]/.test(side) ? side : `+${side}`;
      for (const m of s.matchAll(/([+-])([^+-]+)/g)) {
        const neg = (m[1] === "-") !== flip;
        atoms.push(`${neg ? "-" : "+"}${m[2]}`);
      }
    };
    addSide(lhs, false);
    addSide(rhs, true);
    return atoms.sort().join("");
  };
  const a = norm(submitted);
  const b = norm(correct);
  if (!a || (!a.includes("=") && !b.includes("="))) return false;
  if (!/^[0-9a-z+\-*/^().=]+$/.test(a) || !/^[0-9a-z+\-*/^().=]+$/.test(b)) return false;
  if (/[a-z]{2,}/.test(a) || /[a-z]{2,}/.test(b)) return false;
  const ka = signedAtoms(a);
  const kb = signedAtoms(b);
  return ka != null && kb != null && ka === kb;
};

export const extractCorrectAlternatives = (cf) => {
  if (cf.includes(" (or) ")) return cf.split(" (or) ").map((p) => p.trim()).filter(Boolean);
  if (/ or /i.test(cf)) return cf.split(/ or /i).map((p) => p.trim()).filter(Boolean);
  if (cf.includes(" / ")) return cf.split(" / ").map((p) => p.trim()).filter(Boolean);
  return [cf];
};

// The stem asks for exactly ONE value ("what number should Julian choose?"), so an "X or Y" mark scheme
// is an either/or. Plural/quantity wording ("write down the even numbers", "two multiples of 2001") means
// the answer IS a list. Mirrors FuzzyAnswerMatching::questionAsksForOneValue.
export const questionAsksForOneValue = (questionText) => {
  const q = decodeHtmlSpaces(String(questionText ?? "").replace(/<[^>]*>/g, " "))
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  if (!q) return false;
  if (/\b(?:numbers|values|digits|letters|answers|all the|all of|list|both|each|every|pairs?|ways|two|three|four|five|six)\b/.test(q)) return false;
  return /\b(?:what|which|write down|give|state|name)\b[^.?]*?\b(?:a|one|the)?\s*(?:number|value|digit|letter|answer)\b/.test(q);
};

// The submission names two or more of an either/or mark scheme's alternatives ("4 and 5", "4 or 5" for
// "4 or 5") — a hedge covering every option instead of choosing one. Only where every alternative is a
// bare value (a number or a lone letter): prose alternatives are two WORDINGS of one answer ("it gets
// bigger or it increases") and a student may write both.
// Mirrors FuzzyAnswerMatching::submissionHedgesAlternatives.
export const submissionHedgesAlternatives = (submitted, alternatives) => {
  const plain = (v) => decodeHtmlSpaces(String(v ?? "").replace(/<[^>]*>/g, " ")).toLowerCase().replace(/\s+/g, " ").trim();
  const isBareValue = (v) => /^(?:-?\d+(?:[.,]\d+)?|\p{L})$/u.test(v);
  const values = new Set();
  for (const alt of alternatives) {
    const a = plain(alt);
    if (!isBareValue(a)) return false;
    values.add(a);
  }
  if (values.size < 2) return false;
  const named = new Set(
    plain(submitted)
      .split(/\s*(?:,|;|\/|&|\bor\b|\band\b)\s*|\s+/i)
      .map((t) => t.replace(/^[\s.]+|[\s.]+$/g, ""))
      .filter((t) => t !== "" && values.has(t))
  );
  return named.size > 1;
};

// A bare ordinal equals its digit ("3rd" ≈ "3"). Only collapse a standalone ordinal — NOT an ordinal
// that begins a longer phrase, or "1st November 1998" (a date) would wrongly reduce to "1" and match
// any answer starting with "1st".
export const normalizeOrdinal = (v) =>
  v.replace(/^(\d+)(?:st|nd|rd|th)$/, "$1");

// The index named by "<n>th <noun>" / "<noun> <n>th" / "<n>th" / "<n>", else null. Mirrors
// FuzzyAnswerMatching::ordinalIndexValue (PHP) so client and server agree on "9" ≈ "9th pattern".
const ordinalIndexValue = (v, requireOrdinal) => {
  const plain = String(v ?? "")
    .replace(/ /g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/[.:;]+$/, "");
  if (!plain || !/^[a-z0-9 ]+$/.test(plain)) return null;
  const words = plain.split(" ").filter((w) => w && w !== "the");
  if (words.length > 2) return null;
  let number = null;
  let hasOrdinal = false;
  let nouns = 0;
  for (const word of words) {
    const ord = word.match(/^(\d+)(?:st|nd|rd|th)$/);
    if (ord) {
      if (number !== null) return null;
      number = Number(ord[1]);
      hasOrdinal = true;
    } else if (/^\d+$/.test(word)) {
      if (number !== null) return null;
      number = Number(word);
    } else {
      // "5th June" is a date, not an index — its day number alone is not the answer.
      if (/^(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/.test(word)) return null;
      nouns++;
    }
  }
  if (number === null || nouns > 1) return null;
  return requireOrdinal && !hasOrdinal ? null : number;
};

// "Which pattern number is it?" answered "9" against a "9th pattern" mark scheme — the noun only
// restates the question, and the ordinal suffix hides the number from every numeric tier.
export const ordinalIndexAnswerMatches = (submitted, correct) => {
  const c = ordinalIndexValue(correct, true);
  if (c === null) return false;
  const s = ordinalIndexValue(submitted, false);
  return s !== null && s === c;
};

const CALENDAR_MONTHS = [
  "january", "february", "march", "april", "may", "june", "july",
  "august", "september", "october", "november", "december",
  "jan", "feb", "mar", "apr", "jun", "jul", "aug", "sep", "sept", "oct", "nov", "dec",
];

export const correctAnswerIsDate = (correct) => {
  const c = plainAnswerText(correct).toLowerCase().trim();
  if (!c) return false;
  if (/^\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}$/.test(c)) return true;
  const hasMonth = CALENDAR_MONTHS.some((m) => new RegExp(`\\b${m}\\b`, "u").test(c));
  return hasMonth && /\b\d{1,2}(?:st|nd|rd|th)?\b/u.test(c);
};

export const dateAnswersEquivalent = (submitted, correct) => {
  const parse = (v) => {
    const s = plainAnswerText(v).toLowerCase().trim();
    if (!s) return null;
    const slash = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
    if (slash) return [Number(slash[1]), Number(slash[2]), Number(slash[3])];
    let month = null;
    for (let i = 0; i < CALENDAR_MONTHS.length; i++) {
      if (new RegExp(`\\b${CALENDAR_MONTHS[i]}\\b`, "u").test(s)) {
        month = (i % 12) + 1;
        break;
      }
    }
    if (month == null) return null;
    const d = s.match(/\b(\d{1,2})(?:st|nd|rd|th)?\b/u);
    const y = s.match(/\b(\d{4})\b/);
    if (!d) return null;
    return [Number(d[1]), month, y ? Number(y[1]) : null];
  };
  const a = parse(submitted);
  const b = parse(correct);
  if (!a || !b) return false;
  if (b[2] != null && a[2] !== b[2]) return false;
  return a[0] === b[0] && a[1] === b[1];
};

export const extractAnswerCandidates = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return [];
  const candidates = [raw];
  if (raw.includes("=")) {
    const parts = raw.split("=");
    const rhs = String(parts[parts.length - 1] ?? "").trim();
    if (rhs) candidates.push(rhs);
  }
  return Array.from(new Set(candidates.filter(Boolean)));
};

// Numeric value of a plain number, accepting a real thousands separator ("1,200" / "1 200" → 1200) but
// NOT a comma-decimal typo ("8,7", "12,24") — a comma is only a separator when exactly 3 digits follow
// it. Mirrors FuzzyAnswerMatching::parseSimpleNumericValue so the client agrees with the server.
export const plainNumericValue = (v) => {
  let s = decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, "").trim().toLowerCase();
  if (s === "") return null;
  if (/,(?!\d{3}(?!\d))/.test(s)) return null;
  // Only a valid thousands grouping collapses ("1 000" → 1000). An arbitrary digit split is not a
  // number: "5 04" must not become 504, nor be read as the 5.04 the student failed to type.
  if (/\d\s+\d/.test(s) && !/^[-+]?\d{1,3}(?: \d{3})+(?:\.\d+)?$/.test(s)) return null;
  const bare = s.replace(/[,\s]/g, "");
  if (!/^[-+]?\d+(?:\.\d+)?$/.test(bare)) return null;
  const n = parseFloat(bare);
  return Number.isFinite(n) ? n : null;
};

// Mirrors FuzzyAnswerMatching::parseSimpleNumericValue — fractions, mixed numbers, trailing units.
export const parseSimpleNumericValue = (value) => {
  let v = normalizeVulgarFractions(normalizeLatexFraction(decodeHtmlSpaces(String(value ?? "").replace(/<[^>]*>/g, ""))))
    .replace(/\u00a0/g, " ")
    .trim()
    .toLowerCase();
  if (!v) return null;
  v = v.replace(/[÷\u00F7]/g, "/");
  v = v.replace(/\s*(?:mm²|mm2|mm³|mm3|cm²|cm2|cm³|cm3|m²|m2|m³|m3|km²|km2|mm|cm|km|kg|mg|ml|m|g|l|p|%|°c|°f|°)\s*$/i, "").trim();
  if (/^-?\d+,\d{1,2}$/.test(v)) return null;
  if (/,(?!\d{3}(?!\d))/.test(v)) return null;
  v = v.replace(/[,£$€¥]/g, "");
  let m = v.match(/^(-?\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    const den = +m[3];
    if (!den) return null;
    const whole = +m[1];
    return whole + (whole < 0 ? -1 : 1) * (+m[2] / den);
  }
  m = v.match(/^(-?\d+)\s*\/\s*(\d+)$/);
  if (m) {
    const den = +m[2];
    if (!den) return null;
    return +m[1] / den;
  }
  if (/^-?\d{1,3}(?: \d{3})+(?:\.\d+)?$/.test(v)) return parseFloat(v.replace(/ /g, ""));
  if (/^-?\d+(?:\.\d+)?$/.test(v)) {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const isFractionForm = (v) => {
  const t = normalizeVulgarFractions(normalizeLatexFraction(
    decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, " ")
  )).replace(/\s+/g, " ").trim().toLowerCase().replace(/[÷\u00F7]/g, "/");
  return /^-?\d+\s*\/\s*\d+$/.test(t)
    || /^-?\d+\s+\d+\s*\/\s*\d+$/.test(t)
    || /^-?\d+\s*\/\s*\d+(?:\s*(?:[a-z]{1,12}|°|%))?$/i.test(t)
    || /^-?\d+\s+\d+\s*\/\s*\d+(?:\s*(?:[a-z]{1,12}|°|%))?$/i.test(t)
    || t.includes("\\frac");
};

// Fraction ↔ rounded decimal ("1/3" ≈ "0.33" / "0.333"). ≥2 d.p. required.
export const fractionDecimalRoundingMatch = (submitted, correct, sVal = null, cVal = null) => {
  const s = sVal ?? parseSimpleNumericValue(submitted);
  const c = cVal ?? parseSimpleNumericValue(correct);
  if (s === null || c === null) return false;
  const sFrac = isFractionForm(submitted);
  const cFrac = isFractionForm(correct);
  if (sFrac === cFrac) return false;
  const decSide = sFrac ? correct : submitted;
  const fracVal = sFrac ? s : c;
  const decVal = sFrac ? c : s;
  const dp = decimalPlaceCount(decSide);
  if (dp === null || dp < 2) return false;
  const factor = 10 ** dp;
  return Math.abs(Math.round(fracVal * factor) / factor - decVal) < 1e-9;
};

export const numericValuesEquivalent = (submitted, correct) => {
  const s = parseSimpleNumericValue(submitted);
  const c = parseSimpleNumericValue(correct);
  if (s === null || c === null) return false;
  if (isPlainNumberVal(String(correct ?? "").trim()) && !isPlainNumberVal(String(submitted ?? "").trim())) return false;
  const cBare = String(correct ?? "").trim();
  if (/^0\d/.test(cBare) && String(submitted ?? "").trim() !== cBare) return false;
  if (Math.abs(s - c) < 0.0001) {
    // Float ε of 1e-4 swallows a 4th-d.p. error for a 3-d.p. scheme (1.3541 ≈ 1.354).
    if (extraNonZeroDecimalPlacesMismatch(submitted, correct)) return false;
    return true;
  }
  return fractionDecimalRoundingMatch(submitted, correct, s, c);
};

// The comparison operator an answer states, canonicalised — "=/" and "!=" are both ≠, "<=" is ≤. Null
// when the text is not (just) an operator. "Complete the expression with <, > or =" answers are decided
// EXACTLY: the fuzzy tiers drop the "/" from "=/" (reading a ≠ as "="), and tag-stripping turns a bare
// "<" into an empty string that matches anything else normalising to empty.
// Mirrors FuzzyAnswerMatching::canonicalComparisonOperator.
export const canonicalComparisonOperator = (value) => {
  const v = decodeHtmlSpaces(String(value ?? ""))
    .replace(/^<p>|<\/p>$|<br\s*\/?>/gi, "")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&ne;/gi, "≠")
    .replace(/&le;/gi, "≤")
    .replace(/&ge;/gi, "≥")
    // LaTeX spellings from the math-tools keyboard, resolved before the literal map below so the
    // remaining backslash stays meaningful: "=\" is a struck-through equals (a ≠), not an "=" plus noise.
    .replace(/\\not\s*=/gi, "≠")
    .replace(/\\neq?\b/gi, "≠")
    .replace(/\\leq?\b/gi, "≤")
    .replace(/\\geq?\b/gi, "≥")
    .replace(/\\lt\b/gi, "<")
    .replace(/\\gt\b/gi, ">")
    .replace(/[\s$]+/g, "");
  if (!v) return null;
  const map = {
    "=": "=", "<": "<", ">": ">",
    "≤": "≤", "<=": "≤", "=<": "≤",
    "≥": "≥", ">=": "≥", "=>": "≥",
    // A ≠ is drawn by striking the equals through, and students type that stroke either way.
    "≠": "≠", "!=": "≠", "=!": "≠", "=/": "≠", "/=": "≠", "=/=": "≠", "=\\": "≠", "\\=": "≠", "=\\=": "≠",
  };
  return map[v] ?? null;
};

// A mark scheme naming TWO things joined by "and" — "Birmingham and London", "3 and 5". Both are
// required ("Which TWO cities…"), so half of it — or the unfinished "Birmingham and" — is wrong. Each
// side must be a proper noun or a number, so an explanatory sentence containing "and" isn't a pair.
// Mirrors FuzzyAnswerMatching::correctIsNamedPair.
export const correctIsNamedPair = (correct) => {
  const t = decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (!t || t.includes(",") || /\bor\b/i.test(t)) return null;
  const m = t.match(/^(.+?)\s+and\s+(.+)$/i);
  if (!m) return null;
  const side = /^(?:\p{Lu}[\p{L}'’-]*(?:\s+\p{Lu}[\p{L}'’-]*)?|-?\d+(?:\.\d+)?\s*[\p{L}%°]{0,4})$/u;
  const a = m[1].trim();
  const b = m[2].trim();
  return side.test(a) && side.test(b) ? [a, b] : null;
};

// A sequence of single-letter labels — a route through stations ("A E D B C G"), a reordering
// ("B C A"), a word reflected letter by letter ("S C I T A M E H T A M"). Every letter is part of the
// answer and their ORDER is the answer, so a prefix of it ("A E") is wrong, not "most of the way
// there". Three or more letters: a two-letter answer is too easily an abbreviation.
// "to" is a free connective in prose routes ("B to F to E to D") — strip it before testing, same as
// arrows/commas are stripped in labelSequenceMatches below.
// Comma-separated routes ("D, E, B, C, E, F, C, A") are the same answer.
// Mirrors FuzzyAnswerMatching::correctIsLabelSequence.
export const correctIsLabelSequence = (correct) => {
  const t = decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (/\bor\b/i.test(t)) return null;
  const clean = t.replace(/\bto\b/gi, " ").replace(/[,\s]+/g, " ").trim();
  if (!/^(?:\p{L}\s+){2,}\p{L}$/u.test(clean)) return null;
  return clean.replace(/\s+/g, "").toUpperCase();
};

// Separators are free ("AEDBCG", "A, E, D, B, C, G", "A → E → D → B → C → G"); letters and order aren't.
export const labelSequenceMatches = (submitted, correct) => {
  const expected = correctIsLabelSequence(correct);
  if (!expected) return false;
  const s = decodeHtmlSpaces(String(submitted ?? ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\bto\b/gi, " ")
    .replace(/(?:->|→|[\s,;.\-–—>])+/gu, "")
    .toUpperCase();
  return !!s && s === expected;
};

// Hyphenated multi-letter edge route ("AB-BC-CE-EG-GF-FH"). Mirrors FuzzyAnswerMatching::correctIsEdgeRoute.
export const correctIsEdgeRoute = (correct) => {
  const t = decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, "").trim();
  if (!/^(?:\p{L}{1,4})(?:[-–—]\p{L}{1,4}){2,}$/u.test(t)) return null;
  const parts = t.split(/[-–—]+/u).map((p) => p.trim().toUpperCase()).filter(Boolean);
  return parts.length >= 3 ? parts : null;
};

// The chain of vertices an edge route visits (["AB","BC","CE"] → A,B,C,E), or null when the segments
// are not two-letter edges that join up. Mirrors FuzzyAnswerMatching::vertexPathForEdgeRoute.
export const vertexPathForEdgeRoute = (edges) => {
  const path = [];
  for (let i = 0; i < edges.length; i++) {
    const edge = String(edges[i] ?? "");
    if (edge.length !== 2) return null;
    if (i === 0) path.push(edge[0]);
    else if (path[path.length - 1] !== edge[0]) return null;
    path.push(edge[1]);
  }
  return path.length >= 3 ? path : null;
};

export const edgeRouteMatches = (submitted, correct) => {
  const expected = correctIsEdgeRoute(correct);
  if (!expected) return false;
  const s = decodeHtmlSpaces(String(submitted ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, "").trim();
  const parts = s.split(/[-–—/→>]+/u).map((p) => p.trim().toUpperCase()).filter(Boolean);
  if (parts.length === expected.length && parts.every((p, i) => p === expected[i])) return true;
  // The same route named by the villages it passes through ("A-B-C-E-G-F-H", "ABCEGFH") rather than by
  // the roads it uses — one journey, so only the order of the letters carries meaning.
  const letters = s.replace(/[^\p{L}]+/gu, "").toUpperCase();
  if (!letters) return false;
  if (letters === expected.join("")) return true;
  const vertices = vertexPathForEdgeRoute(expected);
  return !!vertices && letters === vertices.join("");
};

// Mirrors ExplanatoryAnswerMatcher::isMoneyDenominationList. The whole answer has to BE the list: a
// worked explanation that quotes several prices ("Mr Green's price per pound is 40p / 2.2 = 18.18p …
// Mrs Ball sells at 24p per pound") is prose, and read as a coin list it is deterministically wrong
// against every rewording — which kept those answers away from the semantic grader entirely.
const MONEY_TOKEN_RE = /(?:£\s*\d+(?:\.\d+)?|\d+(?:\.\d+)?\s*p)\b/gi;
const MONEY_LIST_WORDS = ["and", "or", "coin", "coins", "note", "notes", "piece", "pieces", "each", "of", "a", "an", "the", "plus"];

export const isMoneyDenominationList = (value) => {
  const v = decodeHtmlSpaces(String(value ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const tokens = v.match(MONEY_TOKEN_RE) || [];
  if (tokens.length < 2) return false;
  // Multipliers ("2 × 50p"), separators and the handful of words a coin list may carry.
  const rest = v.replace(MONEY_TOKEN_RE, " ").replace(/[\d,;+&×x*/\-.()]/gi, " ");
  return rest
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => MONEY_LIST_WORDS.includes(w));
};

export const moneyDenominationListMatches = (submitted, correct) => {
  const canon = (raw) => {
    const v = decodeHtmlSpaces(String(raw ?? "")).replace(/<[^>]*>/g, " ");
    const tokens = v.match(/(?:£\s*\d+(?:\.\d+)?|\d+(?:\.\d+)?\s*p)\b/gi) || [];
    return tokens.map((t) => {
      const x = t.replace(/\s+/g, "").toLowerCase();
      if (x.startsWith("£")) {
        const n = Number(x.slice(1));
        return `£${Number.isFinite(n) ? String(n) : x.slice(1)}`;
      }
      const n = Number(x.replace(/p$/i, ""));
      return `${Number.isFinite(n) ? String(n) : x}p`;
    }).sort();
  };
  const s = canon(submitted);
  const c = canon(correct);
  return s.length > 0 && s.length === c.length && s.every((t, i) => t === c[i]);
};

// People-name pair stems ("Which two men…", Dulwich J Q18): one name earns half marks on the server.
// Numeric pairs and city/place stems still need both.
export const namedPairAllowsSingleName = (pair, questionText) => {
  if (pair.some((item) => /\d/.test(String(item)))) return false;
  const q = decodeHtmlSpaces(String(questionText ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
  if (!q) return false;
  if (/\b(?:cities|city|towns?|places?|countries|schools?)\b/i.test(q)) return false;
  return /\b(?:men|women|man|woman|people|persons?|boys?|girls?|pupils?|students?|who)\b/i.test(q);
};

export const namedPairMatches = (submitted, pair, questionText = "") => {
  const s = decodeHtmlSpaces(String(submitted ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
  if (!s) return false;
  const matched = pair.filter((item) => {
    const needle = item.trim().toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`(?<![\\p{L}\\d])${needle}(?![\\p{L}\\d])`, "u").test(s);
  }).length;
  return matched >= 2;
};

// Mirrors FuzzyAnswerMatching::namedPairSingleNameAnswer — "julian," for "Julian and Neill" is a
// partial, not deterministically wrong.
export const namedPairSingleNameAnswer = (submitted, pair, questionText = "") => {
  if (!namedPairAllowsSingleName(pair, questionText)) return false;
  const s = decodeHtmlSpaces(String(submitted ?? "")).replace(/<[^>]*>/g, " ").trim().toLowerCase();
  const items = s.split(/\s*(?:,|;|&|\band\b)\s*/i).map((i) => i.trim()).filter(Boolean);
  return items.length === 1 && pair.some((n) => n.trim().toLowerCase() === items[0]);
};

// A sum of two or more measured values: "50p+10p", "20p + 20p + 20p", "3 + 4", "£1 + 5p".
// The suffix must be a UNIT, not an algebra variable. Comma-joined coin lists
// ("20p, 1p, 1p, 1p") are the same answer written without "+".
// Mirrors FuzzyAnswerMatching::isSumExpression.
const SUM_TERM = String.raw`(?:£\s*)?\d+(?:\.\d+)?\s*(?:p|pence|pounds?|kg|mg|g|km|cm|mm|m|ml|l|%|°c?|²|³)?`;
const MONEY_TERM = /^(?:£\s*)?\d+(?:\.\d+)?\s*(?:p|pence|pounds?)?$/i;
const looksLikeThousands = (t) => /^\d{1,3}(?:,\d{3})+(?:\.\d+)?$/.test(t);

const sumTermsOf = (value) => {
  const t = decodeHtmlSpaces(String(value ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
    .replace(/[\s,.;]+$/, "");
  if (!t) return null;
  if (t.includes("+")) {
    return t.split("+").map((p) => p.trim()).filter(Boolean);
  }
  if (/,/.test(t) && !looksLikeThousands(t)) {
    const parts = t.split(",").map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2 && parts.every((p) => MONEY_TERM.test(p) && /(?:£|p|pence|pounds?)/i.test(p))) {
      return parts;
    }
  }
  return null;
};

export const isSumExpression = (value) => {
  const terms = sumTermsOf(value);
  if (!terms || terms.length < 2) return false;
  const termRe = new RegExp(`^${SUM_TERM}$`, "i");
  return terms.every((p) => termRe.test(p));
};

// Terms of a sum, sorted — "20p + 10p + 20p" and "20p, 20p, 10p" are the same answer.
export const sumExpressionCanonical = (value) => {
  const terms = sumTermsOf(value);
  const raw = terms
    || decodeHtmlSpaces(String(value ?? "")).replace(/<[^>]*>/g, " ").toLowerCase()
      .replace(/[\s,.;]+$/, "")
      .split("+")
      .map((p) => p.trim())
      .filter(Boolean);
  return raw.map((p) => p.toLowerCase().replace(/\s+/g, "")).filter(Boolean).sort().join("+");
};

// A product of three or more bare numbers — how a prime factorisation is written. The factors ARE the
// answer, so the order is free but a missing factor is a different number: the digit-set tiers below
// ignore how many times a factor appears and matched a seven-factor product (648) to the eight-factor
// scheme (1944). Two factors is a plain multiplication whose value is what matters, so it is left out.
// Mirrors FuzzyAnswerMatching::isProductExpression.
export const isProductExpression = (value) => {
  const t = decodeHtmlSpaces(String(value ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
    .replace(/[\s,.;]+$/, "").replace(/\\times|\\cdot/g, "×");
  return !!t && /^\d+(?:\s*[×x*]\s*\d+){2,}$/i.test(t);
};

// "8 × 7 = 56" is the calculation "7 × 8 = 56" written the other way round: one multiplication or
// addition, the same two numbers, the same result (St Paul's Girls 2025 P2 Sec B Q3).
// Mirrors FuzzyAnswerMatching::commutedCalculationMatches.
export const commutedCalculationMatches = (submitted, correct) => {
  const parse = (v) => {
    const t = decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, "").replace(/\s+/g, "")
      .replace(/(?<=\d)[xX*](?=\d)/g, "×");
    const m = t.match(/^(\d+(?:\.\d+)?)([×+])(\d+(?:\.\d+)?)=(\d+(?:\.\d+)?)$/);
    return m ? `${m[2]}|${[m[1], m[3]].sort().join(",")}|${m[4]}` : null;
  };
  const s = parse(submitted);
  return s !== null && s === parse(correct);
};

// Factors sorted NUMERICALLY — a string sort puts "10" before "2".
export const productExpressionCanonical = (value) =>
  decodeHtmlSpaces(String(value ?? "")).replace(/<[^>]*>/g, " ").toLowerCase()
    .replace(/[\s,.;]+$/, "").replace(/\\times|\\cdot/g, "×")
    .split(/[×x*]/i)
    .map((p) => p.trim())
    .filter(Boolean)
    .sort((a, b) => Number(a) - Number(b))
    .join("×");

// A mark scheme that lists two or more sums ("List all the ways Penny can make 60p") — every one of
// them is part of the answer, so a subset of the list is partial, never correct.
export const correctIsSumEnumeration = (correct) => {
  const parts = splitMultiValueAnswer(String(correct ?? ""));
  return parts.length > 1 && parts.every(isSumExpression);
};

export const sumEnumerationMatches = (submitted, correct) => {
  const canonSet = (v) => Array.from(new Set(
    splitMultiValueAnswer(String(v ?? "")).map(sumExpressionCanonical).filter(Boolean)
  ));
  const expected = canonSet(correct);
  const given = canonSet(submitted);
  return given.length === expected.length && expected.every((e) => given.includes(e));
};

// Fold <sup>n</sup> / unicode superscripts / 10^{2} to caret form before tag-stripping would glue
// "1<sup>3</sup>" into "13". Mirrors AlgebraicExpressionMatcher::normalize (PHP).
export const normalizeNumericPowerExpr = (v) => {
  let t = String(v ?? "");
  t = t.replace(/<sup[^>]*>\s*([^<]*?)\s*<\/sup>/gi, "^$1");
  t = decodeHtmlEntities(decodeHtmlSpaces(t).replace(/<[^>]*>/g, " "));
  t = normalizeLatexFraction(t);
  const supMap = {
    "⁰": "^0", "¹": "^1", "²": "^2", "³": "^3", "⁴": "^4",
    "⁵": "^5", "⁶": "^6", "⁷": "^7", "⁸": "^8", "⁹": "^9",
  };
  t = t.replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]/g, (ch) => supMap[ch] ?? ch);
  t = t.replace(/\^(\d)\^(\d)/g, "^$1$2");
  t = t.replace(/\^\s*\{\s*([^{}]*)\s*\}/g, "^$1");
  t = t.replace(/[×⋅·]/g, "*").replace(/÷/g, "/").replace(/[−–—\u2010\u2011\u2012]/g, "-");
  t = t.replace(/\s+/g, " ").trim().toLowerCase().replace(/[=.\s]+$/g, "");
  return t;
};

// Mirrors AlgebraicExpressionMatcher (PHP): algebraic mark schemes are graded by evaluating both
// sides at sample points. Without this, the compact/digit tiers strip variables and unicode powers
// so "(2x-3)³" collapses to the same skeleton as "(2x-3)²" and is marked correct.
const ALGEBRAIC_SAMPLES = [2.0, 3.5, -4.0, 7.0, 11.5];

// "3x/5 days" — the unit is the answer box's suffix, not part of the expression. Its letters made
// isAlgebraicExpression reject the whole answer as prose, so "0.6x" for "3x/5 days" never reached the
// evaluator. Mirrors AlgebraicExpressionMatcher::splitTrailingUnit (PHP) — keep both in sync.
const ALGEBRAIC_UNIT_SUFFIX = /\s+(days?|weeks?|months?|years?|hours?|hrs?|minutes?|mins?|seconds?|secs?|km|cm|mm|kg|ml|metres?|meters?|litres?|liters?|grams?|kilograms?|pounds?|pence|degrees?)\.?$/i;

const splitTrailingAlgebraicUnit = (text) => {
  const t = decodeHtmlEntities(decodeHtmlSpaces(String(text ?? "").replace(/<[^>]*>/g, " "))).replace(/\s+/g, " ").trim();
  const m = t.match(ALGEBRAIC_UNIT_SUFFIX);
  if (m) {
    const rest = t.slice(0, t.length - m[0].length).trim();
    // Nothing but the unit is not an expression, and a bare quantity ("5 days") must not become
    // algebra either — both are left to the numeric / measurement tiers.
    if (rest && /[a-z]/i.test(rest)) return [rest, m[1].toLowerCase()];
  }
  return [t, ""];
};

export const isAlgebraicExpression = (correctRaw) => {
  const [correct] = splitTrailingAlgebraicUnit(correctRaw);
  const t = normalizeNumericPowerExpr(correct);
  if (!t || !/[a-z]/.test(t)) return false;
  if (/^-?\d+(?:\.\d+)?\s*(?:mm|cm|m|km)\s*\^[23]$/.test(t)) return false;
  if (/\b(?:add|subtract|multiply|divide|plus|minus|times|the|is|of|and|then|answer)\b/.test(t)) return false;
  if (!/^[0-9a-z+\-*/^().= ]+$/.test(t)) return false;
  if (/[a-z]{2,}/.test(t)) return false;
  if (!/[\d+\-*/^=]/.test(t)) return false;
  const hasVariableTerm = /\d[a-z]|[a-z]\s*[+\-*/^=]|[+\-*/^=]\s*[a-z]|\)\s*[a-z]|[a-z]\s*\(/.test(t);
  const hasOperator = /[+\-*/^=]|\(/.test(t);
  return hasVariableTerm && hasOperator;
};

const algebraicEquationToDifference = (normalized) => {
  if (!normalized.includes("=")) return normalized;
  const parts = normalized.split("=");
  if (parts.length !== 2) return null;
  const lhs = parts[0].trim();
  const rhs = parts[1].trim();
  if (!lhs || !rhs || lhs.includes("=") || rhs.includes("=")) return null;
  return `((${lhs})-(${rhs}))`;
};

const evaluateAlgebraicExpr = (expr, env) => {
  const s = String(expr ?? "").replace(/\s+/g, "");
  if (!s || !/^[0-9a-z+\-*/^().]+$/.test(s)) return null;
  let pos = 0;
  const peek = () => (pos < s.length ? s[pos] : "");
  const parseExpr = () => {
    let value = parseTerm();
    if (value === null) return null;
    while (peek() === "+" || peek() === "-") {
      const op = peek();
      pos++;
      const rhs = parseTerm();
      if (rhs === null) return null;
      value = op === "+" ? value + rhs : value - rhs;
    }
    return value;
  };
  const parseTerm = () => {
    let value = parseFactor();
    if (value === null) return null;
    while (true) {
      const c = peek();
      if (c === "*" || c === "/") {
        pos++;
        const rhs = parseFactor();
        if (rhs === null) return null;
        if (c === "/") {
          if (Math.abs(rhs) < 1e-12) return null;
          value /= rhs;
        } else {
          value *= rhs;
        }
        continue;
      }
      if (c === "(" || /[a-z0-9]/.test(c)) {
        const rhs = parseFactor();
        if (rhs === null) return null;
        value *= rhs;
        continue;
      }
      return value;
    }
  };
  const parseFactor = () => {
    const c = peek();
    if (c === "+" || c === "-") {
      pos++;
      const v = parseFactor();
      return v === null ? null : (c === "-" ? -v : v);
    }
    let base = null;
    if (c === "(") {
      pos++;
      base = parseExpr();
      if (base === null || peek() !== ")") return null;
      pos++;
    } else if (/\d|\./.test(c)) {
      const start = pos;
      while (pos < s.length && /[\d.]/.test(s[pos])) pos++;
      const num = Number(s.slice(start, pos));
      if (!Number.isFinite(num)) return null;
      base = num;
    } else if (/[a-z]/.test(c)) {
      pos++;
      if (!(c in env)) return null;
      base = env[c];
    } else {
      return null;
    }
    if (peek() === "^") {
      pos++;
      const exp = parseFactor();
      if (exp === null) return null;
      base **= exp;
    }
    return base;
  };
  const value = parseExpr();
  return (value !== null && pos === s.length && Number.isFinite(value)) ? value : null;
};

const foldTimesLetterX = (normalized) =>
  String(normalized ?? "").replace(/(?<=[0-9a-z)])\s*x\s*(?=[0-9a-z(])/gi, "*");

export const algebraicExpressionMatches = (submittedRaw, correctRaw) => {
  const [submitted, submittedUnit] = splitTrailingAlgebraicUnit(submittedRaw);
  const [correct, correctUnit] = splitTrailingAlgebraicUnit(correctRaw);
  // An expression in days is not the same answer in hours.
  if (submittedUnit && correctUnit && submittedUnit !== correctUnit) return false;
  let a = normalizeNumericPowerExpr(submitted);
  const b = normalizeNumericPowerExpr(correct);
  if (!a) return false;
  // "nx(n-1)" for "n(n-1)" — fold typed x as × when the scheme does not use x as a variable.
  const correctVars = [...new Set(b.match(/[a-z]/g) || [])];
  const schemeTimesBetweenLetters = /[a-z]\s*\*\s*[a-z]/i.test(b);
  const studentLetterXBetweenLetters = /[a-z]\s*x\s*[a-z]/i.test(a);
  if (correctVars.length && !correctVars.includes("x")
      && !(schemeTimesBetweenLetters && studentLetterXBetweenLetters)) {
    a = foldTimesLetterX(a);
  }
  if (a.replace(/\s+/g, "") === b.replace(/\s+/g, "")) return true;

  const aExpr = algebraicEquationToDifference(a);
  const bExpr = algebraicEquationToDifference(b);
  if (aExpr === null || bExpr === null) return false;
  if (aExpr.replace(/\s+/g, "") === bExpr.replace(/\s+/g, "")) return true;

  const vars = [...new Set((aExpr + bExpr).match(/[a-z]/g) || [])];
  if (!vars.length || vars.length > 3) return false;

  const proportional = a.includes("=") && b.includes("=");
  let ratio = null;

  for (let si = 0; si < ALGEBRAIC_SAMPLES.length; si++) {
    const env = {};
    vars.forEach((v, vi) => {
      env[v] = ALGEBRAIC_SAMPLES[(si + vi * 2) % ALGEBRAIC_SAMPLES.length] + vi * 0.25;
    });
    const va = evaluateAlgebraicExpr(aExpr, env);
    const vb = evaluateAlgebraicExpr(bExpr, env);
    if (va === null || vb === null) return false;
    if (!proportional) {
      if (Math.abs(va - vb) > 1e-6 * Math.max(1.0, Math.abs(vb))) return false;
      continue;
    }
    if (Math.abs(vb) <= 1e-9 || Math.abs(va) <= 1e-9) {
      if (Math.abs(va) > 1e-9 || Math.abs(vb) > 1e-9) return false;
      continue;
    }
    const sampleRatio = va / vb;
    if (ratio === null) {
      ratio = sampleRatio;
      continue;
    }
    if (Math.abs(sampleRatio - ratio) > 1e-6 * Math.max(1.0, Math.abs(ratio))) return false;
  }
  return true;
};

/**
 * The mark scheme is a chain ("1²+2²+3²+4² = 30 = ⅙ × 4 × 5 × 9") and the student
 * entered only part of it. A single equation ("180+180=360") is not a chain.
 */
export const equationLineIsTruncated = (submitted, correct) => {
  const compact = (raw) => normalizeNumericPowerExpr(raw).replace(/\s+/g, "").replace(/×/g, "*").replace(/[−–—]/g, "-");
  const s = compact(submitted);
  if (!s || !/^[\d()+\-*/.^=]+$/.test(s)) return false;
  const chains = [];
  const whole = compact(correct);
  if (/^[\d()+\-*/.^=]+$/.test(whole) && (whole.match(/=/g) || []).length >= 2) chains.push(whole);
  const normalized = normalizeNumericPowerExpr(correct);
  const runs = normalized.match(/[\d\s()+\-*/.^=]{6,}/g) || [];
  runs.forEach((run) => {
    const chain = run.replace(/\s+/g, "").replace(/\.+$/, "");
    if ((chain.match(/=/g) || []).length >= 2) chains.push(chain);
  });
  return chains.some((chain) => {
    if (s === chain || (s.match(/=/g) || []).length >= (chain.match(/=/g) || []).length) return false;
    let offset = 0;
    while (offset <= chain.length) {
      const at = chain.indexOf(s, offset);
      if (at < 0) return false;
      const before = at > 0 ? chain[at - 1] : "";
      const after = chain[at + s.length] || "";
      const extendsNumber = /[0-9./]/.test(before) || /[0-9./]/.test(after);
      if (!extendsNumber) return true;
      offset = at + 1;
    }
    return false;
  });
};

export const isNumericPowerExpression = (correct) => {
  const t = normalizeNumericPowerExpr(correct);
  return t !== ""
    && t.includes("^")
    && /^[0-9+\-*/^().= ]+$/.test(t)
    && /\d/.test(t);
};

const powerCountOf = (normalized) => (normalized.match(/\^/g) || []).length;

const equationSegmentsOf = (normalized) =>
  normalized.split("=").map((p) => p.trim()).filter((p) => p !== "");

// A leading sign is not a binary operator.
const operatorMultisetOf = (normalized) => {
  const ops = [...normalized.matchAll(/(?<=[\d).])\s*([+\-*/])/g)].map((m) => m[1]);
  ops.sort();
  return ops.join(",");
};

const exponentMultisetOf = (normalized) => {
  const exps = [...normalized.matchAll(/\^(\d+(?:\.\d+)?)/g)].map((m) => m[1]);
  exps.sort();
  return exps.join(",");
};

// Lightweight evaluator for digit/operator/bracket/^ expressions (no variables). Returns null if unparseable.
const evalNumericPowerExpr = (expr) => {
  const s = String(expr ?? "").replace(/\s+/g, "");
  if (!s || !/^[0-9+\-*/^().]+$/.test(s)) return null;
  let pos = 0;
  const peek = () => (pos < s.length ? s[pos] : "");
  const parseExpr = () => {
    let value = parseTerm();
    if (value === null) return null;
    while (peek() === "+" || peek() === "-") {
      const op = peek();
      pos++;
      const rhs = parseTerm();
      if (rhs === null) return null;
      value = op === "+" ? value + rhs : value - rhs;
    }
    return value;
  };
  const parseTerm = () => {
    let value = parseFactor();
    if (value === null) return null;
    while (true) {
      const c = peek();
      if (c === "*" || c === "/") {
        pos++;
        const rhs = parseFactor();
        if (rhs === null) return null;
        if (c === "/") {
          if (Math.abs(rhs) < 1e-12) return null;
          value /= rhs;
        } else {
          value *= rhs;
        }
        continue;
      }
      if (c === "(" || /\d/.test(c)) {
        const rhs = parseFactor();
        if (rhs === null) return null;
        value *= rhs;
        continue;
      }
      return value;
    }
  };
  const parseFactor = () => {
    const c = peek();
    if (c === "+" || c === "-") {
      pos++;
      const v = parseFactor();
      return v === null ? null : (c === "-" ? -v : v);
    }
    let base = null;
    if (c === "(") {
      pos++;
      base = parseExpr();
      if (base === null || peek() !== ")") return null;
      pos++;
    } else if (/\d|\./.test(c)) {
      const start = pos;
      while (pos < s.length && /[\d.]/.test(s[pos])) pos++;
      const num = Number(s.slice(start, pos));
      if (!Number.isFinite(num)) return null;
      base = num;
    } else {
      return null;
    }
    if (peek() === "^") {
      pos++;
      const exp = parseFactor();
      if (exp === null) return null;
      base **= exp;
    }
    return base;
  };
  const value = parseExpr();
  return (value !== null && pos === s.length && Number.isFinite(value)) ? value : null;
};

// The value of each "=" segment, or null when any of them doesn't parse. A chain states one value
// several ways ("50000 × 2 = 100000"), so matching ANY segment is enough to say the sums agree.
const calculationValuesOf = (normalized) => {
  const out = [];
  for (const seg of equationSegmentsOf(normalized)) {
    const v = evalNumericPowerExpr(seg);
    if (v === null) return null;
    out.push(v);
  }
  return out.length ? out : null;
};

// The mark scheme is a bracketed calculation — "(552 + 448) × (552 - 448)", "(30 × 10) ÷ 50" — where the
// brackets carry the meaning. Mirrors AlgebraicExpressionMatcher::isBracketedCalculation.
export const isBracketedCalculation = (correct) => {
  const t = normalizeNumericPowerExpr(correct);
  return t !== ""
    && t.includes("(")
    && /^[0-9+\-*/^().= ]+$/.test(t)
    && /[+\-*/]/.test(t)
    && calculationValuesOf(t) !== null;
};

// The submission is a calculation worth a DIFFERENT amount from a bracketed mark scheme — the signature
// of dropped or moved brackets ("25001 - 24999 × 25001 + 24999" for "(25001 + 24999) × (25001 - 24999)"),
// which every tier that compares the digits and symbols present reads as the same sum. Brackets that make
// no difference to the value ("3 + (4 × 5)") leave the two equal and are never downgraded.
// Mirrors AlgebraicExpressionMatcher::bracketedCalculationMismatch.
export const bracketedCalculationMismatch = (submitted, correct) => {
  if (!isBracketedCalculation(correct)) return false;
  const a = normalizeNumericPowerExpr(submitted);
  if (a === "" || !/^[0-9+\-*/^().= ]+$/.test(a)) return false;
  const want = calculationValuesOf(normalizeNumericPowerExpr(correct));
  if (!want) return false;
  // A submission built only of digits, operators and brackets that still doesn't parse has a bracket
  // missing or misplaced ("8.5+7.5)×8.5-7.5") — the same defect as dropping them, and the token tiers
  // read it as the mark scheme's own sum because they only count symbols.
  const got = calculationValuesOf(a);
  if (!got) return /\d/.test(a);
  return !got.some((x) => want.some((y) => Math.abs(x - y) <= 1e-9 * Math.max(1, Math.abs(y))));
};

// The factors of a product of whole-number powers, expanded and sorted: "2² × 3²" and "2 × 2 × 3 × 3"
// both give [2, 2, 3, 3]. Null when the value is not such a product. Mirrors productChainFactors.
const SUPERSCRIPT_DIGITS = { "⁰": "0", "¹": "1", "²": "2", "³": "3", "⁴": "4", "⁵": "5", "⁶": "6", "⁷": "7", "⁸": "8", "⁹": "9" };
export const expandedProductFactors = (value) => {
  const t = String(value ?? "")
    // A mark scheme authored in the rich editor carries its powers as <sup>2</sup>.
    .replace(/<sup>\s*(\d+)\s*<\/sup>/gi, "^$1")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]+/g, (m) => "^" + [...m].map((c) => SUPERSCRIPT_DIGITS[c]).join(""))
    .replace(/\s+/g, "");
  if (!/^\d+(?:\^\d+)?(?:[×xX*·]\d+(?:\^\d+)?)+$/.test(t)) return null;
  const out = [];
  for (const term of t.split(/[×xX*·]/)) {
    const [base, power = "1"] = term.split("^");
    const k = Number(power);
    if (!(k >= 1 && k <= 12)) return null;
    for (let i = 0; i < k; i += 1) out.push(Number(base));
  }
  return out.sort((p, q) => p - q);
};

// Mirrors AlgebraicExpressionMatcher::matchesNumericPower — form matters, not just evaluated value.
export const matchesNumericPower = (submitted, correct) => {
  // "2 × 2 × 3 × 3" for "2² × 3²": the same prime factorisation, written out instead of in index form
  // (Sevenoaks Y7 2017 Q8a). A dropped or extra factor still differs.
  const expandedA = expandedProductFactors(submitted);
  const expandedB = expandedProductFactors(correct);
  if (expandedA && expandedB && expandedA.join(",") === expandedB.join(",")) return true;
  const a = normalizeNumericPowerExpr(submitted);
  const b = normalizeNumericPowerExpr(correct);
  if (!a || !a.includes("^")) return false;
  if (a.replace(/\s+/g, "") === b.replace(/\s+/g, "")) return true;
  // "1+3+5+7=16=4²" is a chain of equal expressions, not one expression — the working on each side of
  // every "=" is part of the answer, so it is graded segment by segment. Mirrors
  // AlgebraicExpressionMatcher::matchesEquationChain.
  if (a.includes("=") || b.includes("=")) {
    const segsA = equationSegmentsOf(a);
    const segsB = equationSegmentsOf(b);
    if (!segsA.length || segsA.length !== segsB.length) return false;
    return segsB.every((segB, i) => {
      const segA = segsA[i];
      if (segA.replace(/\s+/g, "") === segB.replace(/\s+/g, "")) return true;
      if (powerCountOf(segA) !== powerCountOf(segB)) return false;
      if (exponentMultisetOf(segA) !== exponentMultisetOf(segB)) return false;
      if (operatorMultisetOf(segA) !== operatorMultisetOf(segB)) return false;
      const va = evalNumericPowerExpr(segA);
      const vb = evalNumericPowerExpr(segB);
      if (va === null || vb === null) return false;
      return Math.abs(va - vb) <= 1e-9 * Math.max(1, Math.abs(vb));
    });
  }
  if (powerCountOf(a) !== powerCountOf(b)) return false;
  if (exponentMultisetOf(a) !== exponentMultisetOf(b)) return false;
  const va = evalNumericPowerExpr(a);
  const vb = evalNumericPowerExpr(b);
  if (va === null || vb === null) return false;
  return Math.abs(va - vb) <= 1e-9 * Math.max(1, Math.abs(vb));
};

// "33 %" / "33 percent" / "33 per cent" → "33%". Only rewrites a sign or word that is already there.
const canonicalPercentSign = (v) =>
  String(v ?? "").replace(/\s*(?:%|\bper\s*cent(?:age)?\b)/gi, "%").trim();

/** Order-of-size lists: fraction form must stay a fraction (0.4 ≠ 2/5). */
export const orderingListPartFormMismatch = (submitted, correct) => {
  const sFrac = valueIsFraction(submitted);
  const cFrac = valueIsFraction(correct);
  if (sFrac === cFrac) return false;
  const s = parseSimpleNumericValue(submitted);
  const c = parseSimpleNumericValue(correct);
  if (s == null || c == null || Number.isNaN(s) || Number.isNaN(c)) return false;
  return Math.abs(s - c) < 0.0001;
};

/** Stem where rearranging given values IS the task (mirrors mobile isExplicitOrderingListStem). */
export const isExplicitOrderingListStem = (questionText) => {
  const context = decodeHtmlSpaces(String(questionText || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!context) return false;
  return /\bin\s+(?:ascending|descending|increasing|decreasing|rising|falling|numerical|alphabetical|reverse|size)?\s*order\b/i.test(context)
    || /\b(?:rising|falling|ascending|descending)\s+order\b/i.test(context)
    || /\border\s+of\b/i.test(context)
    || /\bsmallest\s+to\s+largest\b/i.test(context)
    || /\blargest\s+to\s+smallest\b/i.test(context)
    || /\b(?:arrange|rearrange|rank|sort|reorder)\b/i.test(context)
    || /\bstarting\s+with\s+the\s+(?:lowest|highest|smallest|largest|biggest|first)\b/i.test(context)
    || /\bfrom\s+(?:lowest|highest|smallest|largest|biggest)\s+to\b/i.test(context);
};

/** Next-term / continue-the-sequence blanks — including number runs that only show blanks on the leaf. */
export const looksLikeSequenceContinuationStem = (questionText) => {
  const text = decodeHtmlSpaces(String(questionText || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  if (!text) return false;
  if (/\bnext\s+(?:\w+\s+){0,2}(?:numbers?|terms?|values?|digits?|letters?|shapes?)\b/.test(text)
    || /\b(?:sequence|pattern|series)\b[^.]{0,60}\bnext\b/.test(text)
    || /\bnext\b[^.]{0,60}\b(?:sequence|pattern|series)\b/.test(text)
    || /\bcontinue\s+(?:the\s+)?(?:sequence|pattern|series)\b/.test(text)
    || /\bfollowing\s+sequences?\b/.test(text)
    || /\bwrite\s+(?:down\s+)?the\s+next\b/.test(text)) {
    return true;
  }
  const numberCount = (text.match(/(?<![a-z])-?\d+(?:\.\d+)?(?![a-z])/g) || []).length;
  const blankGroups = (text.match(/(?:_{2,}|□+|…+|\.{3,}|\[\s*\]|\(\s*\))/g) || []).length;
  if (numberCount < 3 || blankGroups < 1) return false;
  const residue = text
    .replace(/-?\d+(?:\.\d+)?|_{2,}|□+|…+|\.{3,}|\[\s*\]|\(\s*\)|[\s,;:×x*+\-–—]+/g, "")
    .trim();
  return residue.length <= 16;
};

export const singleValueMatches = (submitted, correct, questionText = "") => {
  submitted = canonicalizeDegreeGlyphs(submitted);
  correct = canonicalizeDegreeGlyphs(correct);
  // Order-of-size: keep each stem form (fraction vs decimal) — before value tiers equate them.
  if (isExplicitOrderingListStem(questionText) && orderingListPartFormMismatch(submitted, correct)) {
    return false;
  }
  if (labelAnswerRepeatsStemNoun(submitted, correct, questionText)) return true;
  // The whole answer IS a unit noun ("Andy is 183 ___ tall" -> "centimetres") — "cm" must match.
  if (unitNounEquivalent(submitted, correct)) return true;
  // The operator IS the answer ("complete with <, > or =") — compare it exactly, before the tiers below
  // normalise "=/" into "=" or reduce a bare "<" to nothing.
  const expectedOperator = canonicalComparisonOperator(correct);
  if (expectedOperator) return canonicalComparisonOperator(submitted) === expectedOperator;

  // Power mark schemes are decided on form before sum / unit / digit-reducing tiers.
  if (isNumericPowerExpression(correct)) {
    return matchesNumericPower(submitted, correct);
  }

  // Algebraic mark schemes ("(2x-3)²") — evaluate equivalence; never fall through to digit tiers
  // that drop unicode powers and treat "(2x-3)³" as the same answer.
  if (isAlgebraicExpression(correct)) {
    return algebraicExpressionMatches(submitted, correct);
  }

  // "15/2" for "7 1/2" — the same value in the form these papers rule out; every numeric tier below
  // compares by value and accepts it. "November 1" for a bare "November" month scheme, likewise.
  if (submittedIsImproperFractionForMixedScheme(submitted, correct, questionText)) return false;
  if (submittedAddsDayNumberToMonthScheme(submitted, correct)) return false;
  if (correctAnswerIsDate(correct)) return dateAnswersEquivalent(submitted, correct);
  // "yes" against a "yes. DEAL 3" scheme — the deal it asked for was never named.
  if (verdictSchemeHalfAnswered(submitted, correct, questionText)) return false;
  // A "N <count noun>" scheme has exactly two right answers: the count alone or the count with that
  // same noun. "6 m" / "6 kg" / "6 apples" name a different quantity every numeric tier reduces to 6.
  if (correctRequiresTrailingCountNoun(correct)) {
    if (submittedIsPlainNumber(submitted)) {
      if (!stemCountsSchemeNoun(correct, questionText)) return false;
    } else if (!submittedCarriesSchemeCountNoun(submitted, correct)) return false;
  }

  // "5 04" for "£5.04", "3 23" for "3.24" — the space is not a decimal point. Decided before the
  // clock-time and numeric tiers, which both read it as the missing separator.
  if (submittedSpacesDecimalPoint(submitted, correct)) return false;

  // "Write your answer using the 24-hour clock" answered "5.13 p.m" — timesMatch reconciles the two
  // notations into one instant, so it read as the mark scheme.
  if (questionRequires24HourAnswer(questionText) && submissionBreaks24HourForm(submitted, correct)) {
    return false;
  }

  // "33 %", "33&nbsp;%" and "33 percent" all ARE the mark scheme's "33%" — no tier below normalises the
  // sign, so a space in front of it (the math-tools palette inserts one) graded wrong on the take page
  // while the backend, which strips it, said correct. The sign is still required: a bare "33" is left
  // untouched here and still fails against a "33%" scheme.
  const s = canonicalPercentSign(normalizeVal(stripMarkSchemeOperatorPrefix(submitted)));
  const c = canonicalPercentSign(normalizeVal(stripMarkSchemeOperatorPrefix(correct)));
  if (!s || !c) return false;

  // "counter-clockwise" is the OPPOSITE of "clockwise", and "(30 × 10) \ 50" is not "÷ 50" — both are
  // wrong before any tier below gets to normalise them into a match.
  if (rotationDirectionConflict(submitted, correct) || hasStrayBackslash(submitted)) return false;
  if (rotationDirectionsAgree(submitted, correct)) return true;

  // "Likely" for a "Very likely" mark scheme — a neighbouring rung of the printed probability scale.
  if (likelihoodLabelConflict(submitted, correct)) return false;

  // Two sums differ by how many times a term appears ("20p + 20p" is not "20p + 20p + 20p"), which the
  // unit-stripping and no-space tiers below throw away. Compare the terms as a multiset instead.
  // Authoritative when the mark scheme is a sum: a dropped "+" is wrong.
  if (isSumExpression(correct)) {
    return isSumExpression(submitted) && sumExpressionCanonical(submitted) === sumExpressionCanonical(correct);
  }

  // A prime factorisation is a multiset of factors: order free, but a missing factor is a different
  // number. Mirrors the backend's product tier.
  if (isProductExpression(correct)) {
    return isProductExpression(submitted) && productExpressionCanonical(submitted) === productExpressionCanonical(correct);
  }

  // Coin lists that name a £ amount — "1p" is not "£1".
  if (isMoneyDenominationList(correct)) {
    return moneyDenominationListMatches(submitted, correct);
  }

  // Hyphenated edge routes — every segment in order.
  if (correctIsEdgeRoute(correct)) {
    return edgeRouteMatches(submitted, correct);
  }

  // Dot notation is ambiguous: "4.33" is both 4:33 and 13/3 rounded to 2 d.p. The clock branch owns
  // every d.dd scheme, so a division that doesn't terminate marked its own longer form ("4.3333")
  // wrong. Settle the decimal reading first, for a scheme with no colon and no am/pm to make it
  // unambiguously a time.
  if (!/:/.test(c) && !/[ap]\.?\s*m\b/i.test(c)
    && (roundedDecimalPrecisionMatch(submitted, correct) || recurringDecimalExtensionMatch(submitted, correct))) {
    return true;
  }

  if (correctIsClockTime(c)) return timesMatch(submitted, correct, questionAsksForClockTime(questionText));

  if (s === c) return true;

  // The mark scheme is JUST a number (a count/value, no unit) — the answer must be a plain number too.
  // Reject anything with an appended unit or word ("18m", "18 cm", "18 apples") that the unit-stripping
  // tiers below would otherwise reduce to the bare number and accept.
  if (isPlainNumberVal(c) && !isPlainNumberVal(s)) return false;

  // A leading zero ("0800", "0730") marks the value as a 24-hour time / code, not an arithmetic
  // quantity — the numeric-equality tier below reads "800" and "0800" as the same number and would
  // accept a dropped leading digit as if it were a harmless thousands-formatting difference.
  if (isPlainNumberVal(c) && /^0\d/.test(c) && s !== c) return false;

  // Both sides are plain numbers — compare by value so a thousands separator ("1,200" vs "1200") matches.
  // The comma-decimal guard in plainNumericValue keeps "8,7" from being read as 87.
  if (isPlainNumberVal(c) && isPlainNumberVal(s)) {
    const sn = plainNumericValue(s);
    const cn = plainNumericValue(c);
    if (sn !== null && cn !== null && Math.abs(sn - cn) < 1e-9) return true;
  }

  if (unitsConflict(submitted, correct)) return false;
  if (correctIsPoundsValue(correct) && submittedLacksCurrencyUnit(submitted)
      && !questionImpliesPoundsAnswer(questionText)) {
    const pounds = parseSimpleNumericValue(correct);
    if (pounds == null || Math.abs(pounds) < 100) return false;
  }
  if (unspecifiedCurrencyMagnitudeMatch(submitted, correct, questionText)) return true;
  if (submittedMatchesSchemeIgnoringPrintedStemWords(submitted, correct, questionText)) return true;

  if (numericValuesEquivalent(submitted, correct)) return true;

  // Mark scheme to N d.p. ("4.7") — a more precise working value that rounds to it ("4.71") is accepted.
  if (roundedDecimalPrecisionMatch(submitted, correct)) return true;

  // A recurring decimal written to more or fewer places than the scheme ("4.3333" / "0.33" for "0.333").
  if (recurringDecimalExtensionMatch(submitted, correct)) return true;

  if (timesGlyphCalculationMatches(submitted, correct)) return true;
  if (submittedMatchesWorkingEquationResult(submitted, correct)) return true;
  if (operationFillMatchesFilledEquation(submitted, correct)
    || operationFillMatchesFilledEquation(correct, submitted)) return true;

  // Strip prime/apostrophe notation so A′ matches A (coordinate geometry labelling).
  const sP = stripPrime(s);
  const cP = stripPrime(c);
  if (sP && cP && sP === cP) return true;

  const sF = normalizeVal(normalizeLatexFraction(s));
  const cF = normalizeVal(normalizeLatexFraction(c));
  if (sF === cF) return true;
  if (timesMatch(sF, cF)) return true;
  const sU = stripCommonUnit(sF);
  const cU = stripCommonUnit(cF);
  if (sU !== "" && sU === cU && !unitsConflict(submitted, correct)) return true;
  if (isUnitEquivalent(submitted, correct)) return true;
  const sO = normalizeOrdinal(sF);
  const cO = normalizeOrdinal(cF);
  if (sF !== "" && sO === cO) return true;
  if (ordinalIndexAnswerMatches(submitted, correct)) return true;

  const correctAlts = extractCorrectAlternatives(cF);
  const altMatcher = (sp, ca) =>
    sp === ca ||
    stripPrime(sp) === stripPrime(ca) ||
    timesMatch(sp, ca) ||
    (stripCommonUnit(sp) !== "" && stripCommonUnit(sp) === stripCommonUnit(ca)) ||
    normalizeOrdinal(sp) === normalizeOrdinal(ca) ||
    isUnitEquivalent(sp, ca);

  if (correctAlts.some((ca) => altMatcher(sF, ca))) return true;
  if (correctAlts.length > 1) return false;

  const submittedCandidates = extractAnswerCandidates(submitted)
    .map((v) => normalizeVal(stripMarkSchemeOperatorPrefix(v)))
    .filter(Boolean);
  for (const sc of submittedCandidates) {
    if (sc === cF) return true;
    if (timesMatch(sc, cF)) return true;
    if (stripCommonUnit(sc) !== "" && stripCommonUnit(sc) === stripCommonUnit(cF)) return true;
    if (normalizeOrdinal(sc) === normalizeOrdinal(cF)) return true;
    if (isUnitEquivalent(sc, cF)) return true;
  }

  // A lone number mistyped with a comma decimal separator ("3,00", "£3,5") must NOT be salvaged by
  // splitting on the comma and matching its first fragment ("3") — it's one malformed number, not a
  // value list. Skip the fragment fallback for it so "3,00" fails against a "£3.00"/"3" mark scheme.
  if (!isSingleMalformedNumber(submitted)) {
    const submittedParts = splitMultiValueAnswer(sF);
    if (submittedParts.some((sp) => altMatcher(sp, cF))) return true;
  }

  const sN = normalizeHyphensAndBrackets(submitted);
  const cN = normalizeHyphensAndBrackets(correct);
  if (sN && cN && sN === cN) return true;
  if (extractCorrectAlternatives(cN).some((ca) => sN === ca)) return true;

  // Last resort: a word-boundary typo ("Super market" vs "Supermarket") — compare with all
  // whitespace removed. Tried only after every other tier fails, so genuinely distinct
  // multi-word answers (which would already have matched above if equivalent) are unaffected.
  // Word answers only: collapsing spaces between digits changes numeric meaning, so a bare fraction
  // "17/11" must not match the mixed number "1 7/11" (a different value, 17/11 ≠ 18/11) by fusing
  // away the whole-number/fraction separator space. Mirrors the equivalent guard in
  // FuzzyAnswerMatching::isFuzzyTextMatchSingle (PHP) so the two stay in agreement.
  const sNoSpace = s.replace(/\s+/g, "");
  const cNoSpace = c.replace(/\s+/g, "");
  if (sNoSpace && cNoSpace && sNoSpace === cNoSpace
    && /\p{L}/u.test(sNoSpace) && /\p{L}/u.test(cNoSpace)
    && !/\d\s+\d/.test(s) && !/\d\s+\d/.test(c)) return true;

  return false;
};

export const isAnswerPartial = (submitted, correct, questionText = "") => {
  if (submitted == null || submitted === "" || submitted === "—") return false;
  if (correct == null || correct === "" || correct === "—") return false;
  if (countQuestionNumericAnswerEquivalent(submitted, correct, questionText)) return false;
  if (questionAsksToStateUnits(questionText)) {
    const cParsed = parseValueWithUnit(plainAnswerText(correct).trim());
    const sParsed = parseValueWithUnit(plainAnswerText(submitted).trim());
    if (cParsed && cParsed[1] && (!sParsed || !sParsed[1])
      && numericValuesEquivalent(submitted, correct)) return true;
  }
  const correctStr = String(correct);
  if (extractCorrectAlternatives(correctStr).length > 1) return false;
  const correctParts = splitMultiValueAnswer(correctStr);
  if (correctParts.length <= 1) return false;
  const submittedParts = splitMultiValueAnswer(String(submitted));
  if (submittedParts.length >= correctParts.length) return false;
  return submittedParts.some((sp) => correctParts.some((cp) => singleValueMatches(sp, cp)));
};

// A review-row answer value may be a multi-fill array or a TypeDraw { text, drawing } object
// rather than a plain string — reduce it to the comparable text form.
export const answerValueText = (value) => {
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    if ("text" in value || "drawing" in value) return value.text ?? "";
  }
  return value;
};

// Array-aware wrappers used by review pages, where multi-fill answers arrive as arrays that
// must match positionally (blank i ↔ correct[i]) — a value in the wrong box scores nothing.
export const isAnswerCorrectFlexible = (submitted, correct, options = {}) => {
  const submittedVal = answerValueText(submitted);
  if (submittedVal == null || submittedVal === "" || submittedVal === "—") return false;
  if (correct == null || correct === "" || correct === "—") return false;
  // Each blank is compared with isAnswerCorrect, not singleValueMatches: ONE blank can hold a list of
  // values ("66°, 66°" for "the two other angles"), and only isAnswerCorrect matches those as a set.
  // singleValueMatches compares each of the student's values against the whole mark scheme, so it
  // failed even the authored answer — and the blanks stay positional either way (blank i ↔ correct[i]),
  // so a value in the wrong box still scores nothing.
  if (Array.isArray(submittedVal) && Array.isArray(correct)) {
    if (correct.length === 0) return false;
    return correct.every((c, i) => isAnswerCorrect(submittedVal[i] ?? "", c, options));
  }
  if (Array.isArray(submittedVal)) return isAnswerCorrect(submittedVal[0] ?? "", correct, options);
  if (Array.isArray(correct)) return isAnswerCorrect(submittedVal, correct[0] ?? "", options);
  return isAnswerCorrect(submittedVal, correct, options);
};

export const isAnswerPartialFlexible = (submitted, correct) => {
  const submittedVal = answerValueText(submitted);
  if (Array.isArray(submittedVal) || Array.isArray(correct)) {
    const sub = Array.isArray(submittedVal) ? submittedVal : [submittedVal];
    const cor = Array.isArray(correct) ? correct : [correct];
    if (cor.length <= 1) return false;
    let matched = 0;
    cor.forEach((c, i) => {
      if (singleValueMatches(sub[i] ?? "", c) || markSchemeToleranceAccepts(sub[i] ?? "", c)) matched++;
    });
    return matched > 0 && matched < cor.length;
  }
  return isAnswerPartial(submittedVal, correct);
};

// The question demands the fraction be reduced ("Write your answer in its simplest form", "give your
// answer in its lowest terms"). Simplifying IS the task, so an unreduced equivalent — 6/8 for 3/4 — is
// a wrong answer despite having the same numeric value, and the value-equivalence tiers must not
// accept it. Mirrors FuzzyAnswerMatching::questionAsksForSimplestForm on the backend.
export const questionAsksForSimplestForm = (question) => {
  if (!question) return false;
  const text = decodeHtmlSpaces(String(question.question_text || question.text || question.question || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (!text) return false;
  return /\bsimplest\s+form\b/.test(text)
    || /\bin\s+(?:its|your|the\s+)?lowest\s+terms\b/.test(text)
    || /\blowest\s+form\b/.test(text)
    || /\b(?:give|write|leave|express|show)\b[^.]{0,48}\bsimplif(?:y|ied)\b/.test(text)
    || /\bsimplify\s+(?:your\s+)?answer\b/.test(text)
    // A bare imperative — "Simplify 54/72" — never says the words "simplest form", but reducing IS
    // the whole task, so an unreduced equivalent is just as wrong here. Only the leading instruction
    // counts; a stem that merely mentions simplifying somewhere in its prose does not.
    || /^\s*(?:\(?[a-z0-9]{1,3}[).]\s*)?simplif(?:y|ied)\b/.test(text)
    || /\bfully\s+reduced\b/.test(text);
};

// The question demands the answer be WRITTEN as a fraction ("What fraction of …?", "give your answer as
// a fraction"). Expressing it IS the task, so the equal-valued decimal (0.25 for 1/4) is the wrong form —
// but the client's value-equality fraction tier accepts it, and so does the backend's AI grader. Distinct
// from questionAsksForSimplestForm, which polices how far a fraction is reduced.
// Mirrors FuzzyAnswerMatching::questionAsksForFractionForm.
export const questionAsksForFractionForm = (question) => {
  if (!question) return false;
  const raw = typeof question === "string"
    ? question
    : (question.question_text || question.text || question.question || "");
  const text = decodeHtmlSpaces(String(raw))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (!text) return false;
  return /\b(?:what|which)\s+(?:of\s+(?:these|the\s+following)\s+)?fractions?\b/.test(text)
    || /\bas\s+a\s+fraction\b/.test(text)
    || /\bin\s+fraction\s+form\b/.test(text)
    // "Identify a fraction that lies between 2/6 and 3/6" asks for the fraction as plainly as
    // "write down a fraction" does; without these verbs the decimal-for-fraction guard never fired.
    || /\b(?:write|give|name|state|find|identify|choose|select|pick|suggest)\s+(?:down\s+)?(?:me\s+)?(?:a|an|any|one|another)\s+fraction\b/.test(text);
};

const plainAnswerText = (v) =>
  decodeHtmlEntities(decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, " "))
    // U+2010..U+2012 / U+2212 are hyphens the math toolbar and pasted stems emit; no numeric or
    // algebraic tier reads them as minus, so "(n\u20111)" failed against a scheme written "(n-1)".
    .replace(/[\u2010\u2011\u2012\u2212]/g, "-")
    .replace(/\s+/g, " ").trim();

// A percentage mark scheme ("33%") answered with the bare number ("33"): 33 and 33% are different
// quantities, and when the answer is one of a listed set ("… 0.35, 33%") the sign IS the value's
// identity — but every numeric tier strips the % and accepted it. The sign is only optional where the
// question asks FOR a percentage (a fraction/decimal/percentage conversion table, whose column header
// carries it). Mirrors FuzzyAnswerMatching::correctIsBarePercentValue + questionExpectsPercentageAnswer.
export const submittedMissesPercentSign = (submitted, correct, question) => {
  const c = plainAnswerText(correct);
  if (!/^-?\d+(?:\.\d+)?\s*%$/.test(c)) return false;
  const s = plainAnswerText(submitted);
  if (s === "" || s.includes("%") || /\bper\s*cent(?:age)?\b/i.test(s)) return false;
  const answerData = question?.answer_data || question?.answerData || {};
  if (String(answerData.answer_unit ?? "").trim() === "%") return false;
  const blankUnits = answerData.blank_units;
  if (Array.isArray(blankUnits) && blankUnits.some((u) => String(u?.unit ?? "").trim() === "%")) return false;
  const text = plainAnswerText(question?.question_text || question?.text || question?.question || "").toLowerCase();
  if (!text) return false;
  return !(/(?:as|in|into|to)\s+(?:a\s+)?percent(?:age)?/.test(text)
    || /what\s+percent(?:age)?/.test(text)
    || /what\s+%/.test(text)
    || /percent(?:age)?\s+(?:of|is|equivalent|column)/.test(text)
    || /%\s+of/.test(text)
    || /complete\s+the\s+(?:table|chart)/.test(text)
    || /fill\s+in\s+the\s+(?:table|missing|gaps)/.test(text)
    || /fill\s+in\s+(?:the\s+)?spaces?\s+in\s+(?:the\s+)?(?:following\s+)?table/.test(text)
    || /missing\s+(?:value|number|percentage)s?/.test(text)
    || /written\s+out\s+as\s+a\s+fraction/.test(text)
    || /fraction[,\s]+a\s+decimal\s+and\s+a\s+percentage/.test(text)
    || /convert/.test(text));
};

// Mirrors FuzzyAnswerMatching::submittedMissesPiFactor.
export const submittedMissesPiFactor = (submitted, correct, questionText = "") => {
  let c = plainAnswerText(correct).replace(/\\pi\b/gi, "π");
  if (!/\d\s*π|π/.test(c)) return false;
  if (!/-?\d+(?:\.\d+)?\s*π|π\s*-?\d|(?:^|[^a-z])π(?:[^a-z]|$)/i.test(c)) return false;
  const q = plainAnswerText(questionText).toLowerCase();
  const stemAsksPi = q !== "" && /in\s+terms\s+of\s*(?:π|pi\b)|\\pi|\bpi\b|π/.test(q);
  const schemeHasCoeffPi = /-?\d+(?:\.\d+)?\s*π/.test(c);
  if (!stemAsksPi && !schemeHasCoeffPi) return false;
  let s = plainAnswerText(submitted).replace(/\\pi\b/gi, "π").replace(/\bpi\b/gi, "π");
  return !/π/.test(s);
};

// Mirrors FuzzyAnswerMatching::submittedMissesRatioSeparator.
export const questionAsksForRatio = (questionText) => {
  const text = plainAnswerText(questionText).toLowerCase();
  if (!text || questionAsksForClockTime(text)) return false;
  return /\bratios?\b|\bscale\b/.test(text);
};

export const submittedMissesRatioSeparator = (submitted, correct, questionText = "") => {
  const c = plainAnswerText(correct);
  if (!/^-?\d+(?:\.\d+)?\s*:\s*-?\d+(?:\.\d+)?$/.test(c)) return false;
  // "08:02" is a clock time, not the ratio 8:2 — every colon time scheme matched the shape above.
  // A ratio / scale stem settles it the other way: "1.50" is not the dotted form of a "1:50" scale.
  if (parseClockTime(c) !== null && !questionAsksForRatio(questionText)) return false;
  const s = plainAnswerText(submitted);
  return !/^-?\d+(?:\.\d+)?\s*:\s*-?\d+(?:\.\d+)?$/.test(s);
};

// "1 pig" / "2 cows" — trailing count noun is part of the answer. Mirrors
// FuzzyAnswerMatching::correctRequiresTrailingCountNoun + submittedIsPlainNumber.
export const submittedIsPlainNumber = (v) => isPlainNumberVal(plainAnswerText(v));

export const correctRequiresTrailingCountNoun = (correct) => {
  const v = plainAnswerText(correct).toLowerCase();
  const m = v.match(/^(-?\d+(?:\.\d+)?)\s+([a-z][a-z'-]*)$/i);
  if (!m) return false;
  // A one-letter tail is a unit abbreviation, never a count noun ("11.56 s"), and cooking measures
  // are units the recipe table supplies.
  if (m[2].length < 2 || ["p", "c", "percent", "percentage", "pence"].includes(m[2])) return false;
  // "2.00 pm" is a clock time, not two of a thing called "pm" — read as a count noun it demanded the
  // meridiem back character-for-character, so "2 p.m" was marked wrong for the dots. correctIsClockTime
  // owns these schemes.
  if (correctIsClockTime(v)) return false;
  if (/^(?:cups?|teaspoons?|tablespoons?|tsps?|tbsps?|pints?|quarts?|gallons?|ounces?|oz|inch(?:es)?|feet|foot|yards?|stones?|tonnes?|tons?)$/.test(m[2])) return false;
  // The same unit vocabulary FuzzyAnswerMatching::valueHasMeasurementUnit uses. stripCommonUnit below
  // knows a narrower set, and the gap ("250 grams") had the client blocking what the server accepted.
  if (/^(?:mm|cm|km|kg|mg|ml|cl|m|g|l|met(?:re|er)s?|kilomet(?:re|er)s?|millimet(?:re|er)s?|centimet(?:re|er)s?|gram(?:me)?s?|kilogram(?:me)?s?|milligram(?:me)?s?|lit(?:re|er)s?|millilit(?:re|er)s?|miles?|minutes?|mins?|seconds?|secs?|hours?|hrs?|hr|degrees?|mph)$/.test(m[2])) return false;
  // Measurement units are optional elsewhere — not required count nouns.
  const withUnit = `${m[1]} ${m[2]}`;
  if (stripCommonUnit(withUnit) !== withUnit && isPlainNumberVal(stripCommonUnit(withUnit))) return false;
  return true;
};

// The submission says the mark scheme's own count noun ("6 pupils" / "6 pupil" for "6 pupils").
// Mirrors FuzzyAnswerMatching::submittedCarriesSchemeCountNoun.
export const submittedCarriesSchemeCountNoun = (submitted, correct) => {
  const m = plainAnswerText(correct).toLowerCase().match(/^-?\d+(?:\.\d+)?\s+([a-z][a-z'-]*)$/);
  if (!m) return false;
  const noun = singularizeCountNoun(m[1]);
  return new RegExp(`(?:^|[^a-z0-9])${noun}(?:e?s)?(?![a-z0-9])`).test(plainAnswerText(submitted).toLowerCase());
};

const singularizeCountNoun = (word) => {
  const w = String(word ?? "").toLowerCase().trim();
  if (/(?:s|x|ch|sh)es$/.test(w)) return w.slice(0, -2);
  return /[^s]s$/.test(w) ? w.slice(0, -1) : w;
};

// Whether the trailing count noun only restates the question, so a bare number is the whole answer
// ("How many tiles did Burt use?" → "631" for "631 tiles"; "Write down the mode of Darshan's five
// scores" → "8" for "8 marks"). The noun stays compulsory only where a "how many <noun>" clause names
// a DIFFERENT counted thing — "How many cows can I get, and what do I have left over?" still requires
// the leftover blank's "1 pig".
// Mirrors FuzzyAnswerMatching::stemCountsSchemeNoun.
export const stemCountsSchemeNoun = (correct, questionText) => {
  const stem = plainAnswerText(questionText).toLowerCase();
  if (!stem) return false;
  const m = plainAnswerText(correct).toLowerCase().match(/^-?\d+(?:\.\d+)?\s+([a-z][a-z'-]*)$/);
  if (!m) return false;
  const noun = singularizeCountNoun(m[1]);
  const nounPattern = new RegExp(`(?:^|[^a-z0-9])${noun}(?:e?s)?(?![a-z0-9])`);
  const hits = [...stem.matchAll(/\b(?:how (?:many|much)|number of)\b/g)];
  if (!hits.length) return true;
  const auxiliaries = ["were", "was", "are", "is", "did", "do", "does", "will", "would", "can",
    "could", "should", "must", "have", "has", "had", "in", "of", "left", "went", "got", "bought"];
  const askedNouns = [];
  let outside = stem;
  for (const hit of hits) {
    const scope = stem.slice(hit.index + hit[0].length).split(/[?.;:,]|\band\b/)[0] || "";
    if (nounPattern.test(scope)) return true;
    outside = outside.replace(hit[0] + scope, " ");
    // The word the clause counts, past any modifier ("how many MORE students"). A verb form is not
    // one — "how many landed buttered side up?" elides its noun just as "how many were" does.
    const fw = scope.match(/^\s*((?:(?:of|the|these|those|his|her|their|its|all|more|fewer|less|other|extra|such)\s+)*)([a-z][a-z'-]*)/);
    if (fw && !auxiliaries.includes(fw[2]) && !/(?:ed|ing)$/.test(fw[2])) {
      askedNouns.push(singularizeCountNoun(fw[2]));
    }
  }
  // Both things named in the stem's own narrative, not just in the question: that is the shape where
  // the noun disambiguates ("21 chickens and 10 pigs … how many cows"). A question-only synonym
  // ("140 customers … how many people?") names one thing twice.
  for (const asked of askedNouns) {
    if (asked !== noun
      && new RegExp(`(?:^|[^a-z0-9])${asked}(?:e?s)?(?![a-z0-9])`).test(outside)
      && nounPattern.test(outside)) return false;
  }
  return true;
};

// A value that IS a fraction — "3/4", "1 1/2", "\frac{3}{4}" — as opposed to its decimal value.
// Mirrors FuzzyAnswerMatching::valueIsFraction.
export const valueIsFraction = (v) => isFractionForm(v);

// A decimal (or other non-fraction) where the question asked for a fraction in simplest
// form. 1.5 equals 1½, but it is not that written form.
export const decimalMissesRequiredFractionForm = (submitted, correct, questionText = "") => {
  const stem = String(questionText || "");
  if (!stem.trim()) return false;
  if (String(submitted ?? "").includes("|") || String(correct ?? "").includes("|")) return false;
  if (!questionAsksForSimplestForm({ question_text: stem }) && !questionAsksForFractionForm(stem)) return false;
  return valueIsFraction(correct) && !valueIsFraction(submitted);
};

// A backslash that isn't opening a LaTeX command or delimiter. There is no arithmetic a student can
// mean by it: "(30 × 10) \ 50" is not "(30 × 10) ÷ 50".
export const hasStrayBackslash = (v) => /\\(?![a-zA-Z()[\]{}\\])/.test(plainAnswerText(v));

// clockwise vs anticlockwise/counter-clockwise — the same word set, the opposite answer. Every
// word-containment tier reads "clockwise" as a substring of "counter-clockwise" and says yes.
const rotationDirection = (v) => {
  const t = plainAnswerText(v).toLowerCase().replace(/[^a-z]/g, "");
  if (!t) return null;
  // Checked first: "anticlockwise" and "counterclockwise" both CONTAIN "clockwise".
  if (t.includes("anticlockwise") || t.includes("counterclockwise") || t.includes("ccw")) return "anticlockwise";
  if (t.includes("clockwise") || t === "cw") return "clockwise";
  return null;
};

export const rotationDirectionConflict = (submitted, correct) => {
  const s = rotationDirection(submitted);
  const c = rotationDirection(correct);
  return s !== null && c !== null && s !== c;
};

// The same families are also synonyms of each other ("anticlockwise" = "counter-clockwise" = "ccw"),
// which no tier below knows. Only when the whole value IS the direction word: "turns clockwise 5 times"
// carries more than the direction, so it stays for the normal tiers to judge.
const pureRotationDirection = (v) => {
  const t = plainAnswerText(v).toLowerCase().replace(/[^a-z]/g, "");
  return ["anticlockwise", "counterclockwise", "ccw", "clockwise", "cw"].includes(t) ? rotationDirection(t) : null;
};

export const rotationDirectionsAgree = (submitted, correct) => {
  const s = pureRotationDirection(submitted);
  return s !== null && s === pureRotationDirection(correct);
};

// The probability scale is a closed vocabulary printed in the question's word box whose rungs differ
// only by a qualifier — "likely" sits inside "very likely". Every containment tier reads the weaker
// label as agreeing with the stronger one, so scale answers are settled on the vocabulary alone.
// Mirrors ExplanatoryAnswerMatcher::likelihoodLabelConflict.
const LIKELIHOOD_LABELS = {
  impossible: "impossible", "no chance": "impossible", never: "impossible",
  "very unlikely": "very unlikely", unlikely: "unlikely",
  evens: "evens", even: "evens", "even chance": "evens", "equally likely": "evens",
  "50 50": "evens", "fifty fifty": "evens",
  likely: "likely", "very likely": "very likely",
  certain: "certain", definite: "certain", always: "certain",
};

const likelihoodLabel = (v) => {
  const t = plainAnswerText(v).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  return LIKELIHOOD_LABELS[t] ?? null;
};

export const likelihoodLabelConflict = (submitted, correct) => {
  const s = likelihoodLabel(submitted);
  if (s === null) return false;
  // "Certain or Very likely" — every alternative must be a scale label, or this isn't a scale answer.
  const alts = plainAnswerText(correct).split(/\s+\(?or\)?\s+|\s+\/\s+/i).map(likelihoodLabel);
  return alts.length > 0 && !alts.includes(null) && !alts.includes(s);
};

// A range mark scheme ("between 4 and 8 …") is only answered by BOTH endpoints — naming one ("4", or
// the unfinished "4 and") is a different, wrong claim about the range.
export const submittedMissesRangeEndpoints = (submitted, correct) => {
  const c = plainAnswerText(correct).toLowerCase();
  // "Any decimal or fraction between 0.3 and 0.4" states the ACCEPTING SET, not the answer: one value
  // inside it ("0.35") is a complete answer, and demanding both endpoints fails every right one.
  if (/\b(?:any|anything|all)\b[^.]{0,60}\b(?:between|from)\s+-?\d/.test(c)) return false;
  const m = c.match(/\b(?:between|from)\s+(-?\d+(?:\.\d+)?)\s*(?:and|to|–|-)\s*(-?\d+(?:\.\d+)?)/);
  if (!m) return false;
  const s = plainAnswerText(submitted);
  return [m[1], m[2]].some((endpoint) => {
    const esc = endpoint.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return !new RegExp(`(?<![\\d.])${esc}(?![\\d.])`).test(s);
  });
};

// A range mark scheme is a RELATION, not a pair of numbers: "8 and 4" carries both endpoints of
// "between 4 and 8" and states nothing about the range. Writing the range out ("4, 5, 6, 7, 8") is the
// same statement and stays accepted. Mirrors FuzzyAnswerMatching::submittedMissesRangeRelation.
export const submittedMissesRangeRelation = (submitted, correct) => {
  const c = plainAnswerText(correct).toLowerCase();
  if (/\b(?:any|anything|all)\b[^.]{0,60}\b(?:between|from)\s+-?\d/.test(c)) return false;
  const m = c.match(/\b(?:between|from)\s+(-?\d+(?:\.\d+)?)\s*(?:and|to|–|-)\s*(-?\d+(?:\.\d+)?)/);
  if (!m) return false;
  const s = plainAnswerText(submitted).toLowerCase();
  if (!s.trim()) return false;
  if (/\bbetween\b|\bfrom\b|\bto\b|\binclusive\b|\bat\s+least\b|\bat\s+most\b|\bor\s+(?:more|fewer|less)\b|[≤≥<>]|\d\s*[-–—~]\s*\d/.test(s)) return false;
  let lo = parseFloat(m[1]);
  let hi = parseFloat(m[2]);
  if (lo > hi) [lo, hi] = [hi, lo];
  if (Number.isInteger(lo) && Number.isInteger(hi) && hi - lo <= 20) {
    let allListed = true;
    for (let i = lo; i <= hi; i++) {
      if (!new RegExp(`(?<![\\d.])${i}(?![\\d.])`).test(s)) { allListed = false; break; }
    }
    if (allListed) return false;
  }
  return true;
};

// A submission cut off mid-phrase ("4 and", "between 4 and") — the student never finished it. A
// sentence-completion mark scheme ends on the same connective by design ("is less likely to happen
// than", picked from a printed word box), so the student's exact answer is not a fragment.
// Mirrors FuzzyAnswerMatching::submittedIsDanglingFragment.
export const submittedIsDanglingFragment = (submitted, correct = "") => {
  const tail = (v) => plainAnswerText(v).toLowerCase().replace(/[\s.,;:]+$/, "");
  const s = tail(submitted);
  const m = s === "" ? null : s.match(/\b(and|or|to|of|with|than|between|from|plus|minus|because)$/);
  if (!m) return false;
  const c = correct ? tail(correct) : "";
  return c === "" || !new RegExp(`\\b${m[1]}$`).test(c);
};

const NUMBER_WORD_UNITS = {
  zero: 0, nought: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9,
  ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17,
  eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40, fourty: 40, fifty: 50, sixty: 60,
  seventy: 70, eighty: 80, ninety: 90,
};
const NUMBER_WORD_SCALES = { thousand: 1000, million: 1000000, billion: 1000000000 };

// A whole number written out in words ("ninety-two thousand five hundred") as its value, or null when
// the text is not one. Mirrors FuzzyAnswerMatching::numberWordsValue.
export const numberWordsValue = (text) => {
  // Strip leading/trailing punctuation per word so "seven." still parses — otherwise a trailing
  // full stop nulls the whole value and containment tiers accept "fifty-four" for "fifty-five".
  const words = plainAnswerText(text).toLowerCase().replace(/[-,]/g, " ").split(/\s+/)
    .map((w) => w.replace(/^[^\p{L}]+|[^\p{L}]+$/gu, ""))
    .filter((w) => w !== "" && w !== "and");
  if (words.length === 0) return null;
  let total = 0;
  let current = 0;
  for (const word of words) {
    if (word in NUMBER_WORD_UNITS) { current += NUMBER_WORD_UNITS[word]; continue; }
    if (word === "hundred") { current = Math.max(1, current) * 100; continue; }
    if (!(word in NUMBER_WORD_SCALES)) return null;
    total += Math.max(1, current) * NUMBER_WORD_SCALES[word];
    current = 0;
  }
  return total + current;
};

// "Give your answer in words" answered "Ninety-two thousand" for "ninety-two thousand five hundred" —
// a prefix of the mark scheme, which every containment tier reads as agreeing with it.
export const submittedMissesNumberWordsScheme = (submitted, correct) => {
  const c = numberWordsValue(correct);
  if (c === null) return false;
  const s = numberWordsValue(submitted);
  return s !== null && s !== c;
};

// A place/person/title mark scheme ("Dunton Green", "Top of the Pops") answered with only part of it
// ("Dunton", "Top"). Content words only — lowercase joiners ("of", "the") are neither required nor
// counted. Mirrors ExplanatoryAnswerMatcher::submittedDropsProperNounWord.
const NAME_JOINER_WORDS = new Set(["of", "the", "a", "an", "and", "in", "on", "at", "to", "for", "de", "la", "von"]);

const properNounContentWords = (value) => {
  const words = plainAnswerText(value).replace(/ /g, " ").replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
  if (words.length < 2 || words.length > 6) return [];
  const content = [];
  for (const word of words) {
    if (NAME_JOINER_WORDS.has(word.toLowerCase())) continue;
    if (!/^\p{Lu}[\p{L}'’-]*$/u.test(word)) return [];
    content.push(word);
  }
  return content;
};

export const submittedDropsProperNounWord = (submitted, correct) => {
  if (correctIsNamedPair(correct)) return false;
  const cContent = properNounContentWords(correct);
  if (cContent.length < 2 || cContent.length > 4) return false;
  const head = cContent[cContent.length - 1].toLowerCase();
  if (["triangle", "quadrilateral", "number", "numbers", "angle", "angles", "shape", "shapes",
    "numeral", "digit", "line", "factor", "multiple", "fraction", "decimal", "value", "unit",
    "figure", "sequence", "pattern", "sum", "product"].includes(head)) return false;
  const sWords = plainAnswerText(submitted).replace(/ /g, " ").replace(/\s+/g, " ").trim()
    .split(" ").filter(Boolean)
    .map((w) => w.toLowerCase())
    .filter((w) => !NAME_JOINER_WORDS.has(w));
  if (sWords.length === 0) return false;
  const cLower = cContent.map((w) => w.toLowerCase());
  // "Train D" answered "d" — the letter is the answer; the noun only names the option kind.
  const label = cContent[cContent.length - 1];
  if (/^\p{L}$/u.test(label) && sWords.length === 1 && /^\p{L}$/u.test(sWords[0])
    && sWords[0] === label.toLowerCase()) {
    return false;
  }
  return sWords.length < cLower.length && sWords.every((w) => cLower.includes(w));
};

// Mirrors FuzzyAnswerMatching::labelAnswerRepeatsStemNoun — "d" ≡ "Train D" when "train" is in the stem.
export const labelAnswerRepeatsStemNoun = (submitted, correct, questionText = "") => {
  const stem = plainAnswerText(questionText).toLowerCase().replace(/\s+/g, " ").trim();
  if (!stem) return false;
  const plain = (v) => plainAnswerText(v).toLowerCase().replace(/\s+/g, " ").trim().replace(/[.,;:!?]+$/g, "");
  const s = plain(submitted);
  const c = plain(correct);
  if (!s || !c) return false;
  let label = null;
  let phrase = null;
  if (/^\p{L}$/u.test(c)) { label = c; phrase = s; }
  else if (/^\p{L}$/u.test(s)) { label = s; phrase = c; }
  else return false;
  if (phrase === label) return false;
  const words = phrase.split(" ").filter((w) => w && w !== "the");
  if (!words.length || words.length > 3) return false;
  let labelSeen = 0;
  for (const word of words) {
    if (/^\p{L}$/u.test(word)) {
      if (word !== label) return false;
      labelSeen++;
      continue;
    }
    if (!/^\p{L}+$/u.test(word)) return false;
    const singular = word.replace(/(?:es|s)$/u, "");
    const stemHas = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:e?s)?\\b`, "u").test(stem)
      || (singular && new RegExp(`\\b${singular.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:e?s)?\\b`, "u").test(stem));
    if (!stemHas) return false;
  }
  return labelSeen === 1;
};

const SHAPE_FAMILY_HEAD_NOUNS = ["triangle", "quadrilateral", "trapezium", "trapezoid",
  "parallelogram", "rhombus", "rectangle", "hexagon", "pentagon", "kite", "square", "circle"];
const SHAPE_HEAD_ALIASES = { trapezium: ["trapezium", "trapezoid"], trapezoid: ["trapezium", "trapezoid"] };
const CLASSIFIER_HEAD_NOUNS = [
  "number", "numeral", "digit", "angle", "shape", "line", "factor", "multiple",
  "fraction", "decimal", "value", "unit", "figure", "triangle", "quadrilateral",
  "sequence", "pattern", "sum", "product",
];
const CLASSIFIER_STOP_WORDS = new Set([
  "the", "a", "an", "and", "or", "of", "to", "in", "on", "for", "is", "are", "be", "as",
]);

const shapeWords = (text) => plainAnswerText(text)
  .replace(/[^\p{L}\p{N}]+/gu, " ")
  .trim()
  .toLowerCase()
  .split(" ")
  .filter(Boolean)
  .map((w) => (/[^s]s$/.test(w) ? w.slice(0, -1) : w));

const classifierPhraseWords = (text) => {
  const words = plainAnswerText(text)
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .toLowerCase()
    .split(" ")
    .filter(Boolean);
  if (words.length < 2 || words.length > 4) return [];
  if (words.some((w) => w.length < 3 || !/^\p{L}+$/u.test(w) || CLASSIFIER_STOP_WORDS.has(w))) {
    return [];
  }
  return words;
};

const singularizeClassifierWord = (word) => {
  if (/(?:ses|xes|zes|ches|shes)$/i.test(word)) return word.slice(0, -2);
  if (/[^s]s$/i.test(word)) return word.slice(0, -1);
  return word;
};

const classifierQualifiers = (correct) => {
  const words = classifierPhraseWords(correct);
  if (!words.length) return [];
  const head = singularizeClassifierWord(words[words.length - 1]);
  if (SHAPE_FAMILY_HEAD_NOUNS.includes(head)) return [];
  if (!CLASSIFIER_HEAD_NOUNS.includes(head)) return [];
  return words.slice(0, -1).map(singularizeClassifierWord);
};

const wordMatchesClassifierQualifier = (word, qualifier) => {
  if (word === qualifier) return true;
  if (qualifier.length < 4) return false;
  if (Math.abs(word.length - qualifier.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < word.length && j < qualifier.length) {
    if (word[i] === qualifier[j]) {
      i += 1;
      j += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) return false;
    if (word.length > qualifier.length) i += 1;
    else if (word.length < qualifier.length) j += 1;
    else {
      i += 1;
      j += 1;
    }
  }
  edits += word.length - i + (qualifier.length - j);
  return edits <= 1;
};

// "Obtuse angle" / "square number" answered with only the head noun ("angle", "numbers").
// Mirrors ExplanatoryAnswerMatcher::submissionDropsPhraseQualifier.
export const submissionDropsPhraseQualifier = (submitted, correct) => {
  const qualifiers = classifierQualifiers(correct);
  if (!qualifiers.length) return false;
  const sWords = shapeWords(submitted);
  return qualifiers.some(
    (qualifier) => !sWords.some((word) => wordMatchesClassifierQualifier(word, qualifier))
  );
};

// The mark scheme names a specialized member of a shape family ("Isosceles right-angled triangle") and
// the submission names less of it — the bare family ("triangle") or only some of the scheme's
// qualifiers ("Isosceles triangle"). Never a full match; the server decides partial vs zero, the client
// may only refuse to upgrade. Mirrors ExplanatoryAnswerMatcher.
export const isGeneralShapeForSpecializedScheme = (submitted, correct) => {
  const cWords = shapeWords(correct);
  if (cWords.length < 2 || cWords.length > 4) return false;
  if (cWords.some((w) => w.length < 3)) return false;
  const head = cWords[cWords.length - 1];
  if (!SHAPE_FAMILY_HEAD_NOUNS.includes(head)) return false;
  const sWords = shapeWords(submitted);
  if (!sWords.length || sWords.length > 4) return false;
  const sHead = sWords[sWords.length - 1];
  if (!(SHAPE_HEAD_ALIASES[head] || [head]).includes(sHead)) return false;
  const own = [...new Set(sWords.slice(0, -1))];
  if (!own.length) return true;
  const scheme = [...new Set(cWords.slice(0, -1))];
  if (own.some((w) => !scheme.includes(w))) return false;
  return own.length < scheme.length;
};

export const submissionAddsWrongShapeHead = (submitted, correct) => {
  const headsIn = (text) => [...new Set(shapeWords(text).filter((w) => SHAPE_FAMILY_HEAD_NOUNS.includes(w)))];
  const listed = (head, heads) => heads.some((h) => (SHAPE_HEAD_ALIASES[head] || [head]).some(
    (a) => (SHAPE_HEAD_ALIASES[h] || [h]).includes(a)
  ));
  const sHeads = headsIn(submitted);
  if (!sHeads.length) return false;
  const cHeads = headsIn(correct);
  if (cHeads.length && sHeads.some((h) => !listed(h, cHeads))) return true;
  if (cHeads.length) return false;
  const cTokens = plainAnswerText(correct).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim().split(" ").filter(Boolean);
  const triangleQualifiers = ["isosceles", "equilateral", "scalene", "acute", "obtuse"];
  if (!cTokens.some((w) => triangleQualifiers.includes(w))) return false;
  return sHeads.some((h) => h !== "triangle");
};

// A mark scheme offering alternative TUPLES of values — "12,8 or 12,4 or 4,8" for "write down a pair of
// possible numbers". Each alternative is a fixed-size list, so the answer is exactly one of them: how many
// values ("a pair" is two) is part of the answer, and which values go together is too. Null for every
// other shape of mark scheme, which other tiers own.
// Mirrors FuzzyAnswerMatching::correctIsValueTupleChoice.
export const correctIsValueTupleChoice = (correct) => {
  const alternatives = extractCorrectAlternatives(plainAnswerText(correct));
  if (alternatives.length < 2) return null;
  const tuples = [];
  for (const alt of alternatives) {
    const values = String(alt).split(",").map((v) => v.trim()).filter(Boolean);
    if (values.length < 2 || values.some((v) => !/^-?\d+(?:\.\d+)?$/.test(v))) return null;
    tuples.push(values.map((v) => String(parseFloat(v))));
  }
  // The alternatives must agree on size — "a pair" is two, whichever pair you pick.
  if (new Set(tuples.map((t) => t.length)).size !== 1) return null;
  return tuples;
};

// True when the submission is not exactly one of the offered tuples, order within it being free.
// Mirrors FuzzyAnswerMatching::submittedMissesValueTupleChoice.
export const submittedMissesValueTupleChoice = (submitted, correct) => {
  const tuples = correctIsValueTupleChoice(correct);
  if (tuples === null) return false;
  const given = plainAnswerText(submitted).split(/[,\s]+/).map((v) => v.trim()).filter(Boolean);
  // Only value lists are judged here; anything else is left to the other tiers.
  if (!given.length || given.some((v) => !/^-?\d+(?:\.\d+)?$/.test(v))) return false;
  const canon = (a) => a.map((v) => String(parseFloat(v))).sort().join("|");
  const g = canon(given);
  return !tuples.some((t) => canon(t) === g);
};

// Wrong on notation/completeness grounds alone, whatever it means. The lenient tiers (prose
// containment, the backend's AI graders) read straight past exactly these, so the client's
// upgrade-the-backend fallbacks check it before accepting an answer.
// A single-letter mark scheme ("G", "A or B") — every alternative is exactly one letter.
// Mirrors FuzzyAnswerMatching::correctIsSingleLetterAnswer.
export const correctIsSingleLetterAnswer = (correct) => {
  const plain = decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (!plain) return null;
  const alts = extractCorrectAlternatives(plain);
  if (!alts.length) return null;
  const letters = [];
  for (const alt of alts) {
    const t = String(alt ?? "").replace(/\s+/g, "").trim();
    if (!t || !/^\p{L}$/u.test(t)) return null;
    letters.push(t.toUpperCase());
  }
  return [...new Set(letters)];
};

// Extra characters alongside the right letter ("gm" for "G") are wrong — AI Vision reads the letter
// inside and calls it correct. Mirrors FuzzyAnswerMatching::submittedMissesSingleLetterAnswer.
export const submittedMissesSingleLetterAnswer = (submitted, correct) => {
  const letters = correctIsSingleLetterAnswer(correct);
  if (!letters) return false;
  // "spinner A" / "shape B" name the label and say what kind of thing it is — the letter still IS the
  // answer. Only a plain noun may sit beside it: "gm" (no separate label token) and "not A" stay wrong.
  const tokens = decodeHtmlSpaces(String(submitted ?? ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/[.,;:!?]/g, " ")
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t && t !== "the");
  if (tokens.length > 1 && tokens.length <= 3) {
    const labelTokens = tokens.filter((t) => /^\p{L}$/u.test(t));
    const nouns = tokens.filter((t) => /^\p{L}{2,}$/u.test(t));
    const negated = nouns.some((n) => ["not", "no", "never", "except", "without", "or", "and"].includes(n));
    if (labelTokens.length === 1 && nouns.length === tokens.length - 1 && !negated) {
      return !letters.includes(labelTokens[0].toUpperCase());
    }
  }
  const s = decodeHtmlSpaces(String(submitted ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, "").trim();
  if (!s) return false;
  if (!/^\p{L}$/u.test(s)) return true;
  return !letters.includes(s.toUpperCase());
};

// Prose mark scheme built around a number ("…must be a power of 4") answered with a different,
// non-overlapping number ("power of 5"), or with filler that omits every claim-number
// ("number must be"). Mirrors FuzzyAnswerMatching::narrativeNumericContradiction.
// The student wrote the WORKING, not the answer - "3 1/4 - 2" for a mark scheme of "1 1/4". Vision
// and text AI both evaluate the expression and call it correct; the question asked for the value.
// Mirrors FuzzyAnswerMatching::submittedIsUnevaluatedExpressionForValueScheme.
export const submittedIsUnevaluatedExpression = (submitted, correct, questionText) => {
  // "2 × 2 × 3 × 3" for a "2² × 3²" scheme is the factorisation asked for, not a sum left undone.
  const sameFactors = expandedProductFactors(submitted);
  const schemeFactors = expandedProductFactors(correct);
  if (sameFactors && schemeFactors && sameFactors.join(",") === schemeFactors.join(",")) return false;
  const flatten = (v) => decodeHtmlSpaces(String(v ?? ""))
    .replace(/<[^>]*>/g, " ")
    // A LaTeX scheme writes its operators as commands ("2 \times 2 \times 3 \times 5"), which left
    // the scheme reading as operator-free.
    .replace(/\\(?:times|cdot|ast)\b/g, "*")
    .replace(/\\div\b/g, "/")
    .replace(/[−–—]/g, "-")
    .replace(/[⋅·]/g, "*")
    // "16x25" / "16 x 25" is multiplication written with a letter-x, not a variable.
    .replace(/(?<=\d)\s*[xX×]\s*(?=\d|\()/g, "*")
    // A mixed number ("3 1/4") is one value. A bare "456/19" is still a division — do not hide it.
    .replace(/(?<=\d)\s+(\d+)\s*\/\s*(?=\d)/g, " $1\x00")
    .replace(/\s+/g, " ")
    .trim();
  // An operator BETWEEN two operands. A leading sign ("-8") is not working. Allow a unary minus
  // after an opening paren so "9-(-12)" is seen as subtraction, not a bare signed number.
  const hasOperator = (v) => /\d\s*[+*×÷/-]\s*\(?\s*-?\s*\d/.test(v);

  const s = flatten(submitted);
  const c = flatten(correct);
  if (!s || !c || !hasOperator(s) || hasOperator(c)) return false;

  // The unit chip beside the box is appended to what the student typed, so the answer arrives as
  // "1/2 km" against a "0.5 km" scheme. Every numeric test below is anchored ($), so the unit made
  // the lone-fraction exemption miss and a correct fraction was reported as unevaluated working.
  // Units never introduce an operator, so the operator test above still reads the full string.
  const sBare = stripCommonUnit(s);
  const cBare = stripCommonUnit(c);

  const q = flatten(questionText).toLowerCase();
  if (/\b(calculation|number sentence|expression|working|method|formula|equation|show how|write down the sum)\b/.test(q)) return false;

  // A lone a/b is a fraction value ("3/4" for "0.75"), not working — unless the scheme is a
  // whole number the fraction equals ("456/19" for "24"), which is the division left unevaluated.
  const sAsFrac = sBare.replace(/\x00/g, "/");
  if (/^-?\d+\s*\/\s*\d+$/.test(sAsFrac) && !/^-?[\d,]+$/.test(cBare.trim())) return false;

  // Working that ENDS in the answer ("20 x 3 = 60" for a "60" scheme) has been answered. Only an
  // expression left standing with the value nowhere in it is the question restated ("3456 + 789").
  if (!/^-?[\d,]+(?:\.\d+)?$/.test(cBare.trim())) return true;
  const target = parseFloat(cBare.replace(/,/g, "").trim());
  const tokens = sBare.match(/-?[\d,]+(?:\.\d+)?/g) || [];
  return !tokens.some((t) => Math.abs(parseFloat(t.replace(/,/g, "")) - target) < 1e-9);
};

export const narrativeNumericContradiction = (submitted, correct) => {
  const plain = (v) => decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const c = plain(correct);
  const s = plain(submitted);
  if (c.length < 20) return false;
  if (!/\b(needs?|requires?|more to|answer is|therefore|always|same|because|must be)\b/i.test(c)
    && !/^[A-Z][^.!?]{10,}[.!?]?$/.test(c)) {
    return false;
  }
  if (!/\p{L}/u.test(s) || !/\p{L}/u.test(c)) return false;
  const nums = (t) => [...new Set((t.match(/-?\d+(?:\.\d+)?/g) || []))];
  const cSet = nums(c);
  const sSet = nums(s);
  if (!cSet.length) return false;
  if (sSet.length && cSet.every((n) => !sSet.includes(n))) return true;
  if (sSet.length) return false;
  const lower = c.toLowerCase();
  return /\b(?:power|powers)\s+of\b/u.test(lower)
    || /\bdivisible\s+by\b/u.test(lower)
    || /\b(?:always|exactly|only|numerator|denominator|multiple\s+of|factor\s+of)\b/u.test(lower)
    || /\b(?:because|therefore|hence|must be)\b/u.test(lower);
};

// A tally-and-bracket code ("I I(I) I(I)", typed with pipes) folded to a comparable structure — tally
// glyphs unified, spacing dropped, brackets kept. Null when the text is not such a code.
// Mirrors FuzzyAnswerMatching::bracketCodeCanonical.
const bracketCodeCanonical = (value) => {
  const t = decodeHtmlSpaces(String(value ?? ""))
    .replace(/<[^>]*>/g, "")
    .replace(/[（]/g, "(")
    .replace(/[）]/g, ")")
    .replace(/\s+/g, "")
    .replace(/[Iil|1ǀ¦丨]/gu, "I");
  if (!t) return null;
  return /^[I()]+$/.test(t) ? t : null;
};

export const correctIsBracketCode = (correct) => {
  const c = bracketCodeCanonical(correct);
  if (!c) return false;
  // "(ii)" / "(iii)" is a roman-numeral part label, not a tally code — see correctIsBracketCode in
  // FuzzyAnswerMatching.php.
  if (/^\(I+\)$/.test(c)) return false;
  return c.includes("(") && (c.match(/I/g) || []).length >= 2;
};

export const bracketCodeMatches = (submitted, correct) => {
  const s = bracketCodeCanonical(submitted);
  const c = bracketCodeCanonical(correct);
  return !!s && !!c && s === c;
};

// The question demands a particular WRITTEN form for a fraction answer — "in its simplest form", "as a
// mixed number". Only then is a value-equal alternative form a wrong answer.
// Mirrors FuzzyAnswerMatching::questionDemandsMixedNumberForm.
export const questionDemandsMixedNumberForm = (questionText) => {
  const t = plainAnswerText(questionText).toLowerCase();
  return t !== "" && /\bsimplest\s+form\b|\blowest\s+terms\b|\bsimplif(?:y|ied|ying)\b|\bfully\s+reduced\b|\bmixed\s+(?:numbers?|fractions?)\b|\bwhole\s+number\s+and\s+a\s+fraction\b/.test(t);
};

// A mixed-number mark scheme ("7 1/2") answered with a top-heavy fraction ("15/2").
// Two cases (mirrors FuzzyAnswerMatching::submittedIsImproperFractionForMixedScheme):
//   1. The question demands the form — any improper fraction is wrong.
//   2. The values DON'T agree ("42/5" for "4 2/5") — wrong regardless; the AI grader has been seen
//      to misread a glued "42/5" as the mixed form and upgrade a rule "incorrect".
// A value-equal improper fraction stays acceptable when the stem does not demand the form
// (HABS 2013 Q6). Directional either way: a mixed number against an improper mark scheme stays ok.
export const submittedIsImproperFractionForMixedScheme = (submitted, correct, questionText = "") => {
  const plain = (v) => {
    let t = normalizeVulgarFractions(normalizeLatexFraction(plainAnswerText(v))).replace(/\s+/g, " ").trim();
    t = stripCommonUnit(t);
    return t.trim();
  };
  const cm = plain(correct).match(/^(-?)(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (!cm) return false;
  const sm = plain(submitted).match(/^(-?)(\d+)\s*\/\s*(\d+)$/);
  if (!sm) return false;
  const den = parseInt(sm[3], 10);
  if (!(den > 0) || parseInt(sm[2], 10) < den) return false;
  if (questionDemandsMixedNumberForm(questionText)) return true;
  const cSign = cm[1] === "-" ? -1 : 1;
  const sSign = sm[1] === "-" ? -1 : 1;
  const cVal = cSign * (parseInt(cm[2], 10) + parseInt(cm[3], 10) / parseInt(cm[4], 10));
  const sVal = sSign * (parseInt(sm[2], 10) / den);
  return Math.abs(cVal - sVal) > 1e-9;
};

/** An equation / labelled assignment ("C=169") rather than a bare value. */
export const submittedLooksLikeEquation = (submitted) => {
  const s = plainAnswerText(submitted).trim();
  if (!s || !s.includes("=")) return false;
  return /\S.*=.*\S/u.test(s);
};

/** Bare number or number+unit mark scheme ("169", "169 cm²", "1/6"). */
export const answerIsBareNumericOrMeasurement = (value) => {
  const t = plainAnswerText(value).trim();
  if (!t) return false;
  if (isPlainNumberVal(t)) return true;
  const bare = stripCommonUnit(t);
  return bare !== t && isPlainNumberVal(bare);
};

// A bare month mark scheme ("November") — the question asked WHICH MONTH, not which date, so a day
// number the answer never claimed ("November 1") is a different answer. AI Vision reads the right
// month and marks it correct. Mirrors FuzzyAnswerMatching::submittedAddsDayNumberToMonthScheme.
const CALENDAR_MONTH_NAMES = [
  "january", "february", "march", "april", "may", "june", "july",
  "august", "september", "october", "november", "december",
  "jan", "feb", "mar", "apr", "jun", "jul", "aug", "sep", "sept", "oct", "nov", "dec",
];

export const submittedAddsDayNumberToMonthScheme = (submitted, correct) => {
  const c = plainAnswerText(correct).toLowerCase().replace(/[.\s]+$/, "");
  if (!CALENDAR_MONTH_NAMES.includes(c)) return false;
  const s = plainAnswerText(submitted).toLowerCase();
  return new RegExp(`\\b${c}`).test(s) && /\d/.test(s);
};

// "Can Bella afford any of the deals? If so, which one?" is marked "Yes. DEAL 3" — the verdict alone is
// half the answer. Every containment / prose tier (and the AI graders) read the matching "yes" as
// agreement. Only a SHORT identifying tail counts as required (it names a deal / option / value); a
// prose reason is graded by the marking-point path instead.
// Mirrors FuzzyAnswerMatching::verdictSchemeMissingIdentifier.
const verdictTailTokens = (text) => {
  const t = plainAnswerText(text);
  if (!t || extractYesNoVerdict(t) === null) return null;
  const m = t.match(/^\s*(?:yes|yeah|yep|true|no|nope|false)\b[\s.,;:!\u2014\u2013-]*(.*)$/i);
  if (!m) return null;
  const tail = m[1].replace(/^[\s.,;:!\u2014\u2013-]+|[\s.,;:!\u2014\u2013-]+$/g, "");
  if (!tail || tail.length > 40 || /\b(?:because|since|as|so|therefore|due to)\b/i.test(tail)) return null;
  const tokens = tail.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (!tokens.length || tokens.length > 5 || !tokens.some((tk) => /\d/.test(tk))) return null;
  return tokens;
};

export const verdictSchemeMissingIdentifier = (submitted, correct) => {
  const required = verdictTailTokens(correct);
  if (!required) return false;
  const s = plainAnswerText(submitted);
  if (!s || extractYesNoVerdict(s) === null) return false;
  const got = s.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  // Only the digit-bearing tokens identify the thing ("3" of "DEAL 3") — the label word beside them is
  // how the paper prints it, so "Yes, 3" names the same deal.
  return required.some((tk) => /\d/.test(tk) && !got.includes(tk));
};

// The mirror: the identifier given, the verdict left out ("3" for "yes. DEAL 3"). Only for a stem that
// asks BOTH halves outright — the "If so" is what makes the yes/no an answer in its own right rather
// than something naming the deal already implies. Mirrors
// FuzzyAnswerMatching::submissionMissesSchemeVerdict.
export const submissionMissesSchemeVerdict = (submitted, correct, questionText) => {
  if (!verdictTailTokens(correct)) return false;
  const q = plainAnswerText(questionText).toLowerCase();
  if (!q || !/\bif\s+(?:so|yes|it\s+(?:is|does|can))\b/.test(q)) return false;
  return extractYesNoVerdict(plainAnswerText(submitted)) === null;
};

// Either half of a "yes. DEAL 3" answer on its own. Both halves are worth something, so this never
// means "wrong" — it means the answer is incomplete, and the callers score it as partial.
export const verdictSchemeHalfAnswered = (submitted, correct, questionText) =>
  // Only a matching verdict earns the half mark — a bare "no" against a "yes …" scheme is simply the
  // wrong answer, not an incomplete right one.
  (verdictSchemeMissingIdentifier(submitted, correct)
    && extractYesNoVerdict(plainAnswerText(submitted)) === extractYesNoVerdict(plainAnswerText(correct)))
  || submissionMissesSchemeVerdict(submitted, correct, questionText);

// Mirrors FuzzyAnswerMatching::extractCoordinatePairs.
// A coordinate may be written as a decimal, a fraction or a mixed number: (11/2, 5 1/2) ≡ (5.5, 5.5).
// Mirrors FuzzyAnswerMatching::extractCoordinatePairs / coordinateNumber.
const COORD_NUM = String.raw`-?(?:\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)`;
const COORD_PAIR_RE = new RegExp(String.raw`\(\s*(${COORD_NUM})\s*,\s*(${COORD_NUM})\s*\)`, "g");

const coordinateNumber = (raw) => {
  const r = String(raw).trim();
  const neg = r.startsWith("-");
  const body = r.replace(/^-/, "");
  let v;
  let m;
  if ((m = body.match(/^(\d+)\s+(\d+)\/(\d+)$/))) v = Number(m[1]) + (Number(m[3]) ? Number(m[2]) / Number(m[3]) : 0);
  else if ((m = body.match(/^(\d+)\/(\d+)$/))) v = Number(m[2]) ? Number(m[1]) / Number(m[2]) : 0;
  else v = parseFloat(body);
  return neg ? -v : v;
};

export const extractCoordinatePairs = (text) => {
  const t = decodeHtmlEntities(decodeHtmlSpaces(String(text ?? "")).replace(/<[^>]*>/g, ""));
  const pairs = [];
  for (const m of t.matchAll(COORD_PAIR_RE)) pairs.push([coordinateNumber(m[1]), coordinateNumber(m[2])]);
  if (pairs.length === 0) {
    const bare = t.trim().match(new RegExp(String.raw`^(${COORD_NUM})\s*,\s*(${COORD_NUM})$`));
    if (bare) pairs.push([coordinateNumber(bare[1]), coordinateNumber(bare[2])]);
  }
  return pairs;
};

// The mark scheme is nothing but coordinate pairs — "(3, 3)", "(3, 3), (4, 4)". The brackets and the
// space after the comma are notation, so "3,3" is the same point written plainly, but the ORDER of the
// two numbers is the answer. Mirrors FuzzyAnswerMatching::correctIsCoordinatePair.
export const correctIsCoordinatePair = (correct) => {
  const plain = decodeHtmlEntities(decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, "")).trim();
  if (!plain || extractCoordinatePairs(plain).length === 0) return false;
  let rest = plain.replace(new RegExp(COORD_PAIR_RE.source, "g"), "");
  if (rest.trim() === plain) {
    rest = plain.replace(new RegExp(String.raw`^\s*${COORD_NUM}\s*,\s*${COORD_NUM}\s*$`), "");
  }
  return rest.replace(/\band\b|[,;]/gi, " ").trim() === "";
};

// Mirrors FuzzyAnswerMatching::coordinateAnswerMatches — positional first, then as a set.
const coordinatePairsEqual = (a, b) => Math.abs(a[0] - b[0]) < 1e-4 && Math.abs(a[1] - b[1]) < 1e-4;

export const coordinateAnswerMatches = (submitted, correct) => {
  const subPairs = extractCoordinatePairs(submitted);
  const corPairs = extractCoordinatePairs(correct);
  if (subPairs.length === 0 || corPairs.length === 0) return false;
  if (subPairs.length !== corPairs.length) return false;
  if (corPairs.every((cp, i) => coordinatePairsEqual(subPairs[i], cp))) return true;
  // Same set of points, different typing order — a coordinate LIST is a set ("find all the pairs of
  // whole numbers that p and q could be"), unless the question asks for a route/sequence, which is
  // graded by the route/label-sequence tier and never reaches here. Match each submitted pair against
  // an unused correct pair so a student who typed the same pairs in another order isn't marked wrong.
  // The order of the two numbers WITHIN a pair still decides it — "(21, 22)" is not "(22, 21)".
  const remaining = [...corPairs];
  return subPairs.every((sp) => {
    const i = remaining.findIndex((cp) => coordinatePairsEqual(sp, cp));
    if (i < 0) return false;
    remaining.splice(i, 1);
    return true;
  });
};

// Name -> numeric values asserted about that name. Mirrors ExplanatoryAnswerMatcher::namedEntityNumberPairs:
// only capitalised entities in short local patterns around the number are harvested, so ordinary prose
// does not become a fake key/value record.
const namedEntityNumberPairs = (value) => {
  const plain = plainAnswerText(value);
  if (plain === "") return {};
  const pairs = {};
  const name = String.raw`\p{Lu}[\p{L}'’\-]*(?:\s+\p{Lu}[\p{L}'’\-]*){0,2}`;
  const unit = "kg|g|cm|mm|m|km|l|ml|%|degrees?";
  const add = (rawEntity, number) => {
    let entity = String(rawEntity).replace(/['’]s$/i, "").replace(/\s+/g, " ").trim();
    if (entity === "" || !number) return;
    if (!/^\p{Lu}[\p{L}'’-]*(?:\s+\p{Lu}[\p{L}'’-]*)*$/u.test(entity)) return;
    const letters = entity.replace(/['’-]/g, "");
    if (letters.length < 3 || entity.split(" ").length > 3) return;
    const skip = new Set(["total", "amount", "mass", "percentage", "percent", "sector", "chart", "slice"]);
    if (skip.has(entity.toLowerCase())) return;
    const key = entity.toLowerCase();
    pairs[key] = pairs[key] || [];
    if (!pairs[key].includes(number)) pairs[key].push(number);
  };
  const patterns = [
    [new RegExp(`(-?\\d+(?:\\.\\d+)?)\\s*(?:${unit})?\\s+(?:for|at)\\s+(${name})`, "gu"), 2, 1],
    [new RegExp(`(${name})['’]s\\s+(?:[A-Za-z]+\\s+){0,3}?(-?\\d+(?:\\.\\d+)?)`, "gu"), 1, 2],
    [new RegExp(`(${name})(?:['’]s)?\\s+(?:total|amount|mass)\\s*(?:[:\\uFF1A\\-–—=]|is|was)?\\s*(-?\\d+(?:\\.\\d+)?)`, "giu"), 1, 2],
    [new RegExp(`(${name})(?:['’]s)?\\s*(?:[:\\uFF1A\\-–—=]|\\()\\s*(-?\\d+(?:\\.\\d+)?)`, "gu"), 1, 2],
    [new RegExp(`(${name})(?:['’]s)?\\s+(?:sold\\s+)?(-?\\d+(?:\\.\\d+)?)\\s*(?:${unit})\\b`, "gu"), 1, 2],
  ];
  for (const [re, entityIdx, numberIdx] of patterns) {
    for (const m of plain.matchAll(re)) add(m[entityIdx], m[numberIdx]);
  }
  return pairs;
};

const lookupNamedEntityNumbers = (pairs, entity) => {
  if (pairs[entity]) return pairs[entity];
  const stem = entity.replace(/s$/, "");
  if (!stem) return [];
  for (const [key, nums] of Object.entries(pairs)) {
    if (key === entity || key.replace(/s$/, "") === stem) return nums;
  }
  return [];
};

// A prose answer naming the same entities and the same numbers as the mark scheme, but attaching the
// numbers to the WRONG entities. The mapping is the whole claim, so a contradicted pairing stays wrong.
const GROUP_PERCENT_SKIP = new Set([
  "because", "liked", "where", "whereas", "only", "which", "that", "this", "their", "there",
  "about", "from", "with", "were", "was", "have", "has", "had", "than", "then", "equally",
  "popular", "survey", "wrong", "correct", "percent", "percentage", "out", "the", "and",
  "for", "but", "not", "they", "them", "both", "same", "more", "less", "said", "explain",
  "why", "when", "what", "who", "how", "into", "also", "just", "very", "much", "many",
  "some", "each", "every", "other", "total", "number", "numbers", "answer", "question",
]);

const percentValuesClose = (a, b) => {
  const left = Number(a);
  const right = Number(b);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return String(a) === String(b);
  return Math.abs(left - right) <= 0.51;
};

const falseOutOfPercentClaim = (text) => {
  const t = plainAnswerText(text).toLowerCase().replace(/\s+/g, " ").trim();
  if (!t) return false;
  const patterns = [
    /(\d+(?:\.\d+)?)\s+out\s+of\s+(\d+(?:\.\d+)?)(?:\s+[a-z]+){0,6}?\s*\(\s*(\d+(?:\.\d+)?)\s*%\s*\)/gi,
    /(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)(?:\s+[a-z]+){0,4}?\s*\(\s*(\d+(?:\.\d+)?)\s*%\s*\)/gi,
    /(\d+(?:\.\d+)?)\s+out\s+of\s+(\d+(?:\.\d+)?)(?:\s+[a-z]+){0,6}?\s*(?:=|is|are|equals)\s*(\d+(?:\.\d+)?)\s*%/gi,
  ];
  for (const re of patterns) {
    for (const m of t.matchAll(re)) {
      const den = Number(m[2]);
      if (!(den > 0)) continue;
      const exact = (Number(m[1]) / den) * 100;
      const claimed = Number(m[3]);
      if (Math.abs(exact - claimed) > 1 && Math.abs(Math.round(exact) - claimed) > 0.51) return true;
    }
  }
  return false;
};

const groupPercentBindings = (value) => {
  const text = plainAnswerText(value).toLowerCase().replace(/\s+/g, " ").trim();
  if (!text) return {};
  const pairs = {};
  const add = (group, number) => {
    const key = String(group || "").trim();
    const n = String(number || "").trim();
    if (!key || !n || key.length < 3 || GROUP_PERCENT_SKIP.has(key)) return;
    if (!pairs[key]) pairs[key] = [];
    if (!pairs[key].includes(n)) pairs[key].push(n);
  };
  for (const m of text.matchAll(/\b([a-z]{3,})\s*\(\s*(\d+(?:\.\d+)?)\s*%\s*\)/gi)) add(m[1], m[2]);
  for (const m of text.matchAll(/\b(\d+(?:\.\d+)?)\s*%\s+of\s+(?:the\s+)?([a-z]{3,})\b/gi)) add(m[2], m[1]);
  return pairs;
};

/** "boys (15%) … girls (30%)" for "boys (30%) … girls (15%)" — same words, swapped percentages. */
export const percentageGroupClaimConflict = (submitted, correct) => {
  if (falseOutOfPercentClaim(submitted)) return true;
  const correctPairs = groupPercentBindings(correct);
  const submittedPairs = groupPercentBindings(submitted);
  const groups = Object.keys(correctPairs);
  if (groups.length === 0 || Object.keys(submittedPairs).length === 0) return false;
  return groups.some((group) => {
    const got = submittedPairs[group] || submittedPairs[group.replace(/s$/, "")] || [];
    const expected = correctPairs[group] || [];
    return got.length > 0 && got.some((n) => !expected.some((e) => percentValuesClose(n, e)));
  });
};

export const namedEntityNumberAssociationConflict = (submitted, correct) => {
  const correctPairs = namedEntityNumberPairs(correct);
  if (Object.keys(correctPairs).length < 2) return false;
  const submittedPairs = namedEntityNumberPairs(submitted);
  if (Object.keys(submittedPairs).length === 0) return false;
  return Object.entries(correctPairs).some(([entity, expected]) => {
    const got = lookupNamedEntityNumbers(submittedPairs, entity);
    return got.length > 0 && got.some((n) => !expected.includes(n));
  });
};

// "divisible by 3" / "power of 2" -> { "divisible by": ["3"], "power of": ["2"] }. Wording is folded
// so "divided by 3" and "divisible by 3" are one condition.
// Mirrors ExplanatoryAnswerMatcher::numericRelationPairs (PHP) — keep both in sync.
const numericRelationPairs = (value) => {
  const text = plainAnswerText(value).toLowerCase();
  const pairs = {};
  if (!text) return pairs;
  const re = /\b(divisible\s+by|divided\s+by|divide\s+by|multiples?\s+of|factors?\s+of|powers?\s+of|multiplied\s+by|square\s+of|cube\s+of|remainder\s+of)\s+(-?\d+(?:\.\d+)?)/g;
  const fold = {
    "divided by": "divisible by",
    "divide by": "divisible by",
    "multiples of": "multiple of",
    "factors of": "factor of",
    "powers of": "power of",
  };
  let m;
  while ((m = re.exec(text)) !== null) {
    const relation = fold[m[1].replace(/\s+/g, " ")] ?? m[1].replace(/\s+/g, " ");
    if (!pairs[relation]) pairs[relation] = [];
    if (!pairs[relation].includes(m[2])) pairs[relation].push(m[2]);
  }
  return pairs;
};

// The mark scheme states a numeric relation ("divisible by 3", "a power of 2") and the submission
// names it with a different number, or answers a multi-condition scheme with only one condition.
// Mirrors ExplanatoryAnswerMatcher::numericRelationConflict (PHP).
const NUMBER_WORDS = {
  zero: "0", one: "1", two: "2", three: "3", four: "4", five: "5", six: "6", seven: "7",
  eight: "8", nine: "9", ten: "10", eleven: "11", twelve: "12", twenty: "20", half: "2", double: "2",
};

// Every number the text names, as digits — "3" and "three" both count.
const numbersMentioned = (value) => {
  const text = plainAnswerText(value).toLowerCase();
  const found = new Set(text.match(/-?\d+(?:\.\d+)?/g) || []);
  Object.entries(NUMBER_WORDS).forEach(([word, digit]) => {
    if (new RegExp(`\\b${word}\\b`).test(text)) found.add(digit);
  });
  return [...found];
};

export const numericRelationConflict = (submitted, correct) => {
  const expected = numericRelationPairs(correct);
  const relations = Object.keys(expected);
  if (!relations.length) return false;
  const got = numericRelationPairs(submitted);
  // A condition can be stated without the scheme's phrasing ("One side is x2" for "multiplied by 2"),
  // so only a condition whose number is nowhere in the submission counts as missing.
  const submittedNumbers = numbersMentioned(submitted);
  let missing = 0;
  for (const relation of relations) {
    const gotNumbers = got[relation] || [];
    if (!gotNumbers.length) {
      if (expected[relation].some((n) => !submittedNumbers.includes(n))) missing++;
      continue;
    }
    if (expected[relation].some((n) => !gotNumbers.includes(n))) return true;
  }
  const powerGot = got["power of"] || [];
  const powerExp = expected["power of"] || [];
  const multGot = [...new Set([...(got["multiple of"] || []), ...(got["divisible by"] || [])])];
  const multExp = [...new Set([...(expected["multiple of"] || []), ...(expected["divisible by"] || [])])];
  if (powerExp.length && powerExp.some((n) => multGot.includes(n)) && powerExp.some((n) => !powerGot.includes(n))) {
    return true;
  }
  if (multExp.length && multExp.some((n) => powerGot.includes(n)) && multExp.some((n) => !multGot.includes(n))) {
    return true;
  }
  return relations.length > 1 && missing > 0;
};

const claimSkeleton = (value) =>
  plainAnswerText(value)
    .toLowerCase()
    .replace(/-?\d+(?:\.\d+)?/g, " # ")
    .replace(/[^\p{L}\p{N}#]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

// The mark scheme's own sentence copied back with its figures changed ("the pair 4x7 gives a total
// grid of 6x9" for "…4x6 … 6x8 …"). Every word tier reads the shared wording as agreement.
// Mirrors ExplanatoryAnswerMatcher::numberedClaimSkeletonConflict (PHP).
export const numberedClaimSkeletonConflict = (submitted, correct) => {
  const nums = (v) => plainAnswerText(v).match(/-?\d+(?:\.\d+)?/g) || [];
  const sNumbers = nums(submitted);
  const cNumbers = nums(correct);
  if (cNumbers.length < 2 || sNumbers.length < 2) return false;
  if (sNumbers.join(",") === cNumbers.join(",")) return false;
  const sSkeleton = claimSkeleton(submitted);
  if (!sSkeleton || sSkeleton !== claimSkeleton(correct)) return false;
  return sSkeleton.split(" ").filter((w) => w !== "#" && w.length > 2).length >= 3;
};

/** @returns {{ addends: string[], total: string } | null} */
export const parseLabelledSumEquation = (value) => {
  let t = plainAnswerText(value).toLowerCase().replace(/[×⋅·]/g, "+").replace(/\s+/g, " ").trim();
  if (!t) return null;
  let m = t.match(
    /^(?:(?:area|areas?)\s*(?:of\s*)?)?([a-z])\s*\+\s*(?:(?:area|areas?)\s*(?:of\s*)?)?([a-z])\s*=\s*(?:(?:area|areas?)\s*(?:of\s*)?)?([a-z])\.?$/i
  );
  if (m) return { addends: [m[1].toUpperCase(), m[2].toUpperCase()], total: m[3].toUpperCase() };
  m = t.match(
    /\b(?:areas?|squares?)\s+(?:of\s+)?(?:squares?\s+)?([a-z])\s*(?:and|,)\s*([a-z])\b.{0,40}?\b(?:together\s+)?(?:equal|equals?|make|makes?|give|gives?)\b.{0,40}?\b(?:area\s+(?:of\s+)?)?(?:square\s+)?([a-z])\b/i
  );
  if (m) return { addends: [m[1].toUpperCase(), m[2].toUpperCase()], total: m[3].toUpperCase() };
  m = t.match(/\b([a-z])\s*(?:and|,)\s*([a-z])\b.{0,20}?\b(?:together\s+)?(?:equal|equals?)\b.{0,20}?\b([a-z])\b/i);
  if (m) return { addends: [m[1].toUpperCase(), m[2].toUpperCase()], total: m[3].toUpperCase() };
  return null;
};

// Mirrors ExplanatoryAnswerMatcher::labelledSumEquationsAgree.
export const labelledSumEquationsAgree = (submitted, correct) => {
  const s = parseLabelledSumEquation(submitted);
  const c = parseLabelledSumEquation(correct);
  if (!s || !c) return false;
  const sAdd = [...s.addends].sort().join(",");
  const cAdd = [...c.addends].sort().join(",");
  return sAdd === cAdd && s.total === c.total;
};

// Same labels as the mark scheme's additive equality, but a different addends/total partition
// ("A + C = B" for "A and B together equal C"). Mirrors ExplanatoryAnswerMatcher::labelledSumEquationConflict.
export const labelledSumEquationConflict = (submitted, correct) => {
  const s = parseLabelledSumEquation(submitted);
  const c = parseLabelledSumEquation(correct);
  if (!s || !c) return false;
  if (labelledSumEquationsAgree(submitted, correct)) return false;
  const sAll = [...s.addends, s.total].sort().join(",");
  const cAll = [...c.addends, c.total].sort().join(",");
  return sAll === cAll;
};

// Mirrors FuzzyAnswerMatching::arithmeticOperationSequence / operationInstructionMismatch.
const arithmeticOperationSequence = (text) => {
  const t = plainAnswerText(text).toLowerCase().replace(/\u00a0/g, " ").trim();
  if (!t) return [];
  // "3x/5 days" is an algebraic value — "/" is not "divide".
  if (isAlgebraicExpression(text)) return [];
  if (/^-?\d+(?:\s+\d+)?\s*\/\s*\d+$/.test(t) || /^-?\d+(?:\.\d+)?$/.test(t)
    || /^-?\d+(?:\.\d+)?\s+times$/.test(t) || isSumExpression(t)
    || /^-\s*\d+(?:\.\d+)?\s*(?:°\s*[cf]?|degrees?\s*(?:c|f|celsius|fahrenheit)?|%|[a-z]{1,12}[²³]?)$/u.test(t)) {
    return [];
  }
  const re = /\b(multiply|multiplied|product|divide|divided|shared?|split|subtract|subtracted|minus|take\s*away|less|deduct|add|added|plus|sum|total|increase)\b|[×*]\s*\d|(?:^|\s)x\s*\d|\d\s*x(?:\s|$)|[÷]\s*\d|(?<!\d)\/\s*\d|(?:^|\s)-\s*\d|(?:^|\s)\+\s*\d|\btimes\b(?![^.]{0,40}?\b(?:than|as)\b)/gu;
  const ops = [];
  for (const m of t.matchAll(re)) {
    const tok = String(m[0] || "").toLowerCase().trim();
    if (/^(?:multiply|multiplied|product|×|\*|x|times)/.test(tok) || /[×*x]/.test(tok)) ops.push("multiply");
    else if (/^(?:divide|divided|share|shared|split|÷)/.test(tok) || tok.includes("/")) ops.push("divide");
    else if (/^(?:subtract|subtracted|minus|take|less|deduct|-)/.test(tok)) ops.push("subtract");
    else if (/^(?:add|added|plus|sum|total|increase|\+)/.test(tok)) ops.push("add");
  }
  return ops;
};

// An operation INSTRUCTION ("Subtract 3 from the input", "Multiply by 4 and divide by 9"), not prose
// that merely contains an operation word. The length/sentence gate mirrors
// FuzzyAnswerMatching::looksLikeOperationInstruction: without it a multi-sentence rule scheme ("…then
// build 1 square on the left and 1 square on top. …add 3 matches per square.") read as "add 3", so a
// correct rule written in the student's own words came back deterministically wrong and the take pages
// overturned the server's Correct.
const looksLikeOperationInstruction = (text) => {
  const plain = plainAnswerText(text).trim();
  if (!plain) return false;
  if (/[.!?;]\s+\S/u.test(plain)) return false;
  if (plain.split(/\s+/u).length > 10) return false;
  const ops = arithmeticOperationSequence(text);
  return ops.length > 0 && /\b(?:multiply|divide|subtract|add|plus|minus|times)\b/i.test(plain);
};

const numbersInOrder = (text) => {
  const t = plainAnswerText(text);
  return [...t.matchAll(/-?\d+(?:\.\d+)?/g)].map((m) => m[0]);
};

// LaTeX operator commands as the symbols they render as. "\times" is the × SYMBOL, not the English
// word — but \b matches between the backslash and the "t", so every LaTeX mark scheme
// ("8 \times 18 = 144") read as an instruction to multiply.
export const canonicalizeLatexOperators = (value) => String(value ?? "")
  .replace(/\\times\b/g, "×")
  .replace(/\\div\b/g, "÷")
  .replace(/\\cdot\b/g, "×");

export const operationInstructionMismatch = (submitted, correct) => {
  submitted = canonicalizeLatexOperators(submitted);
  correct = canonicalizeLatexOperators(correct);
  if (isAlgebraicExpression(correct)) return false;
  if (!looksLikeOperationInstruction(correct)) return false;
  const expected = arithmeticOperationSequence(correct);
  if (!expected.length) return false;
  let submittedForOps = submitted;
  let submittedOps = arithmeticOperationSequence(submittedForOps);
  if (!submittedOps.length) {
    const t = plainAnswerText(submittedForOps).toLowerCase().trim();
    const sign = t.match(/^([-+−])\s*(\d+(?:\.\d+)?)$/u);
    if (sign) {
      submittedOps = [sign[1] === "+" ? "add" : "subtract"];
      submittedForOps = sign[2];
    }
  }
  const expNums = numbersInOrder(correct);
  const subNums = numbersInOrder(submittedForOps);
  if (submittedOps.length && submittedOps.join(",") !== expected.join(",")) {
    const expPairs = expected.map((op, i) => `${op}:${expNums[i] ?? ""}`).sort();
    const subPairs = submittedOps.map((op, i) => `${op}:${subNums[i] ?? ""}`).sort();
    const families = [...new Set(expected)];
    const commutative = (families.length <= 2
      && families.every((f) => f === "multiply" || f === "divide"))
      || (families.length <= 2 && families.every((f) => f === "add" || f === "subtract"));
    if (!(commutative && expPairs.join(",") === subPairs.join(",") && expPairs.every((p) => p.split(":")[1]))) {
      return true;
    }
    return false;
  }
  if (!submittedOps.length && expected.length
    && (isPlainNumberVal(plainAnswerText(submitted)) || parseSimpleNumericValue(submitted) !== null)) {
    return true;
  }
  return expNums.length > 0 && subNums.length > 0 && subNums.join(",") !== expNums.join(",");
};

export const operationInstructionEquivalent = (submitted, correct) => {
  submitted = canonicalizeLatexOperators(submitted);
  correct = canonicalizeLatexOperators(correct);
  if (isAlgebraicExpression(correct)) return false;
  if (!looksLikeOperationInstruction(correct) || !looksLikeOperationInstruction(submitted)) return false;
  const expected = arithmeticOperationSequence(correct);
  const submittedOps = arithmeticOperationSequence(submitted);
  if (!expected.length || !submittedOps.length) return false;
  const expNums = numbersInOrder(correct);
  const subNums = numbersInOrder(submitted);
  const expPairs = expected.map((op, i) => `${op}:${expNums[i] ?? ""}`).sort().join(",");
  const subPairs = submittedOps.map((op, i) => `${op}:${subNums[i] ?? ""}`).sort().join(",");
  if (!expPairs || expPairs !== subPairs) return false;
  const families = [...new Set(expected)];
  return (families.length <= 2 && families.every((f) => f === "multiply" || f === "divide"))
    || (families.length <= 2 && families.every((f) => f === "add" || f === "subtract"));
};

const COLOUR_WORDS = new Set(["red", "blue", "green", "yellow", "orange", "purple", "pink", "black", "white", "brown", "grey", "gray"]);
const colourAlias = { grey: "gray", gray: "grey" };
const contentTokens = (t) => plainAnswerText(t).toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);

export const submissionFlipsColourWord = (submitted, correct) => {
  const s = contentTokens(submitted);
  const c = contentTokens(correct);
  const sColours = s.filter((w) => COLOUR_WORDS.has(w));
  const cColours = c.filter((w) => COLOUR_WORDS.has(w));
  if (sColours.length !== 1 || cColours.length !== 1) return false;
  if (sColours[0] === cColours[0] || colourAlias[sColours[0]] === cColours[0]) return false;
  const sRest = s.filter((w) => !COLOUR_WORDS.has(w));
  const cRest = c.filter((w) => !COLOUR_WORDS.has(w));
  const smaller = Math.min(sRest.length, cRest.length);
  if (smaller < 2) return false;
  return sRest.filter((w) => cRest.includes(w)).length / smaller >= 0.6;
};

// Mirrors ExplanatoryAnswerMatcher::submissionFlipsNumberWord — "two less than the number on the red
// dice" for "one less than …" (St Anselms 2025 Q29c). A digit and its word are the same number.
const SWAP_NUMBER_WORDS = {
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17,
  eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70,
  eighty: 80, ninety: 90, hundred: 100,
};
const numberWordValue = (w) => (w in SWAP_NUMBER_WORDS ? SWAP_NUMBER_WORDS[w] : /^\d{1,3}$/.test(w) ? Number(w) : null);

export const submissionFlipsNumberWord = (submitted, correct) => {
  const s = contentTokens(submitted);
  const c = contentTokens(correct);
  const sNums = [...new Set(s.map(numberWordValue).filter((v) => v !== null))];
  const cNums = [...new Set(c.map(numberWordValue).filter((v) => v !== null))];
  if (sNums.length !== 1 || cNums.length !== 1 || sNums[0] === cNums[0]) return false;
  const sRest = s.filter((w) => numberWordValue(w) === null);
  const cRest = c.filter((w) => numberWordValue(w) === null);
  const smaller = Math.min(sRest.length, cRest.length);
  if (smaller < 2) return false;
  return sRest.filter((w) => cRest.includes(w)).length / smaller >= 0.6;
};

const COMPARATIVE_PAIRS = [["more", "less"], ["faster", "slower"], ["greater", "less"], ["higher", "lower"], ["bigger", "smaller"], ["larger", "smaller"]];

export const inverseComparativeEquivalent = (submitted, correct) => {
  const s = contentTokens(submitted);
  const c = contentTokens(correct);
  const flipped = [];
  for (const [one, other] of COMPARATIVE_PAIRS) {
    const sHasOne = s.includes(one);
    const sHasOther = s.includes(other);
    const cHasOne = c.includes(one);
    const cHasOther = c.includes(other);
    if (sHasOne === sHasOther || cHasOne === cHasOther) continue;
    if (sHasOne === cHasOne) continue;
    flipped.push([one, other]);
  }
  if (flipped.length < 2) return false;
  const drop = new Set(flipped.flat());
  const sRest = s.filter((w) => !drop.has(w));
  const cRest = c.filter((w) => !drop.has(w));
  const smaller = Math.min(sRest.length, cRest.length);
  if (smaller < 2) return false;
  return sRest.filter((w) => cRest.includes(w)).length / smaller >= 0.6;
};

// Mirrors FuzzyAnswerMatching::submissionIsDeterministicallyWrong.
// A duration written with two units where the smaller one was never carried into the larger:
// "3 hours 190 minutes", "2 hours 70 minutes", "5 minutes 90 seconds". A bare "190 minutes" is a fine
// answer — only the composite form makes the overflow an error. Mirrors
// FuzzyAnswerMatching::submittedOverflowsCompositeTimeUnits.
export const submittedOverflowsCompositeTimeUnits = (submitted) => {
  const s = decodeHtmlSpaces(String(submitted ?? "")).replace(/<[^>]*>/g, " ").toLowerCase();
  if (!s.trim()) return false;
  const valueFor = (pattern) => {
    const m = s.match(new RegExp(`(-?\\d+(?:\\.\\d+)?)\\s*(?:${pattern})\\b`));
    return m ? parseFloat(m[1]) : null;
  };
  const hours = valueFor("hours?|hrs?");
  const minutes = valueFor("minutes?|mins?");
  const seconds = valueFor("seconds?|secs?");
  if (hours !== null && minutes !== null && minutes >= 60) return true;
  return minutes !== null && seconds !== null && seconds >= 60;
};

// "485 minutes" for "8 hours and 5 minutes": the scheme gives the duration in two units and the take
// page shows one box per unit, so the same total in one unit leaves a box empty (HBS Round2 Set3 Q6b).
// Mirrors FuzzyAnswerMatching::submittedDropsCompositeDurationUnit.
export const submittedDropsCompositeDurationUnit = (submitted, correct) => {
  const s = plainAnswerText(submitted).toLowerCase();
  const c = plainAnswerText(correct).toLowerCase();
  if (!s || !c) return false;
  const has = (t, unit) => new RegExp(`\\d+(?:\\.\\d+)?\\s*(?:${unit})\\b`).test(t);
  return [["hours?|hrs?|h", "minutes?|mins?"], ["minutes?|mins?", "seconds?|secs?"]].some(
    ([big, small]) => has(c, big) && has(c, small) && has(s, big) !== has(s, small),
  );
};

// A decimal typed without the zero before the point (".686" for "0.686"). The value is right but the
// form is not how a decimal is written, and every numeric tier parses ".686" to 0.686 and accepts it —
// as does AI Vision ("0.686 is equivalent to .686"). Only fires when the mark scheme itself writes the
// leading zero, so a scheme that is genuinely bare is unaffected.
// Mirrors FuzzyAnswerMatching::submittedDropsLeadingZero.
export const submittedDropsLeadingZero = (submitted, correct) => {
  const s = plainAnswerText(submitted);
  const c = plainAnswerText(correct);
  if (!s || !c) return false;
  // Strip a leading currency/sign so "£.50" is caught too, and any trailing unit chip.
  const bare = (v) => v.replace(/^[£$€]\s*/, "").replace(/^[+-]\s*/, "").trim();
  const sb = bare(s);
  const cb = bare(c);
  // A unit on either side ("0.77 m" scheme, ".77" beside the "m" chip) does not change how the number
  // is written.
  const unit = "(?:\\s*[\\p{L}%°²³]+\\.?)?";
  const sm = sb.match(new RegExp(`^\\.(\\d+)${unit}$`, "u"));
  if (!sm) return false;
  const cm = cb.match(new RegExp(`^0\\.(\\d+)${unit}$`, "u"));
  return !!cm && cm[1] === sm[1];
};

// A 12-hour mark scheme states the half of the day ("8:19 pm"); the same clock face without it
// ("8:19") does not name a time of day, and both the prose tiers and AI Vision read the matching
// digits as agreement. Distinct from timesMatch's period check, which only fires once a tier has
// already decided to compare the two as times.
// Mirrors FuzzyAnswerMatching::submittedMissesMeridiem.
export const submittedMissesMeridiem = (submitted, correct) => {
  const c = plainAnswerText(correct);
  const s = plainAnswerText(submitted);
  if (!s || !c) return false;
  if (extractWrittenAmPm(c) === null) return false;
  if (extractWrittenAmPm(s) !== null) return false;
  // Only a bare clock face counts — prose that happens to omit "pm" is another tier's business.
  if (!/^\d{1,2}\s*[:.]\s*\d{2}$/.test(s)) return false;
  const sClock = parseClockTime(s);
  const cClock = parseClockTime(c);
  // Wrong digits as well as a missing meridiem is already wrong by value; this guard is about the
  // right clock face handed in without the half of the day that completes it.
  return !!sClock && !!cClock && sClock.hour === cClock.hour && sClock.minute === cClock.minute;
};

// Two plain numbers that are simply not the same number ("4.3" for "4.25", "30" for "-30"). Every
// containment / prose tier can find the shorter inside the longer, and AI Vision has reported
// "'4.3' is equivalent to the correct answer '4.25'". A value mismatch is the least ambiguous
// wrongness there is, so it must never be upgraded.
// Mirrors FuzzyAnswerMatching::plainNumberValueConflict.
export const plainNumberValueConflict = (submitted, correct) => {
  const s = plainAnswerText(submitted);
  const c = plainAnswerText(correct);
  if (!s || !c) return false;
  if (!isPlainNumberVal(s) || !isPlainNumberVal(c)) return false;
  const sNum = parseSimpleNumericValue(s);
  const cNum = parseSimpleNumericValue(c);
  if (sNum === null || cNum === null) return false;
  return Math.abs(sNum - cNum) > 1e-9;
};

export const submissionIsDeterministicallyWrong = (submitted, correct, questionText = "") => {
  if (String(submitted ?? "").trim() === "") return false;
  submitted = canonicalizeDegreeGlyphs(submitted);
  correct = canonicalizeDegreeGlyphs(correct);
  // The same prime factorisation in another notation ("2 × 2 × 3 × 3" / "2² × 3²") is not wrong —
  // several tiers below (power form, product form) read the two notations as different answers.
  const factorsSub = expandedProductFactors(submitted);
  const factorsCorrect = expandedProductFactors(correct);
  if (factorsSub && factorsCorrect && factorsSub.join(",") === factorsCorrect.join(",")) return false;
  // Algebraic schemes first — "/" in "3x/5 days" is not a divide instruction, and equivalent forms
  // ("0.6x", "6/10x") must not be marked deterministically wrong before the evaluator runs.
  if (isAlgebraicExpression(correct)) {
    return !algebraicExpressionMatches(submitted, correct);
  }
  // A coordinate scheme is decided by the coordinate matcher before the operator/number tiers — the
  // "/" of a fraction coordinate ("(11/2, 11/2)") otherwise reads as a division instruction.
  if (correctIsCoordinatePair(correct) && extractCoordinatePairs(submitted).length > 0) {
    return !coordinateAnswerMatches(submitted, correct);
  }
  if (timesGlyphCalculationMatches(submitted, correct)) return false;
  if (submittedMatchesWorkingEquationResult(submitted, correct)) return false;
  if (operationInstructionMismatch(submitted, correct)) return true;
  if (submittedMissesPiFactor(submitted, correct, questionText)) return true;
  if (submittedMissesRatioSeparator(submitted, correct, questionText)) return true;
  if (questionAsksForFractionForm(questionText)
      && valueIsFraction(correct)
      && !valueIsFraction(submitted)) return true;
  if (submittedDropsLeadingZero(submitted, correct)) return true;
  if (submittedMissesMeridiem(submitted, correct)) return true;
  if (plainNumberValueConflict(submitted, correct)) return true;
  // A missing or wrong-dimension unit ("124" / "124 cm²" for "124 m²") is wrong on the quantity, so the
  // AI must not re-accept it on the digits — same rule isAnswerCorrect applies.
  if (measuredUnitVerdict(submitted, correct, questionText) === false) return true;
  if (unitsConflict(submitted, correct)) return true;
  if (isPlainNumberVal(plainAnswerText(correct))
      && !isPlainNumberVal(plainAnswerText(submitted))
      && valueHasMeasurementUnit(submitted)
      && parseSimpleNumericValue(submitted) !== null) return true;
  if (isPlainNumberVal(plainAnswerText(correct)) && isPlainNumberVal(plainAnswerText(submitted))) {
    const sNum = parseSimpleNumericValue(submitted);
    const cNum = parseSimpleNumericValue(correct);
    if (sNum !== null && cNum !== null
        && ((cNum < 0 && sNum > 0) || (cNum > 0 && sNum < 0))
        && Math.abs(Math.abs(sNum) - Math.abs(cNum)) < 0.0001) return true;
  }
  if (submissionFlipsColourWord(submitted, correct) || submissionFlipsNumberWord(submitted, correct)) return true;
  // A coordinate mark scheme is positional — "(22, 21)" is not "(21, 22)". Every value-set tier below
  // (and the AI) compares the two as the same pair of numbers.
  if (correctIsCoordinatePair(correct) && extractCoordinatePairs(submitted).length > 0
    && !coordinateAnswerMatches(submitted, correct)) return true;
  const namedPair = correctIsNamedPair(correct);
  if (namedPair && !namedPairMatches(submitted, namedPair, questionText)
    && !namedPairSingleNameAnswer(submitted, namedPair, questionText)) return true;
  // "=/" is a ≠, not an "=" with a stray slash — but that is exactly how the AI reads it.
  const expectedOperator = canonicalComparisonOperator(correct);
  if (expectedOperator && canonicalComparisonOperator(submitted) !== expectedOperator) return true;
  // A power mark scheme with a dropped/wrong power (e.g. "1+2³+…" for "1³+2³+…") must not be
  // upgraded by the prose / value-equivalence client fallbacks.
  if (isNumericPowerExpression(correct) && !matchesNumericPower(submitted, correct)) return true;
  // Brackets dropped from a bracketed mark scheme — same symbols, different sum.
  if (bracketedCalculationMismatch(submitted, correct)) return true;
  // A sum with a dropped "+" — the AI / prose tiers read the same digits and call it correct.
  if (isSumExpression(correct) && !(isSumExpression(submitted) && sumExpressionCanonical(submitted) === sumExpressionCanonical(correct))) return true;
  // A product with a factor dropped or repeated — the digit tiers read the same factors and accept it.
  if (isProductExpression(correct) && isProductExpression(submitted)
    && productExpressionCanonical(submitted) !== productExpressionCanonical(correct)) return true;
  // Truncated edge route / wrong coin list (£ vs p).
  if (correctIsEdgeRoute(correct) && !edgeRouteMatches(submitted, correct)) return true;
  if (isMoneyDenominationList(correct) && !moneyDenominationListMatches(submitted, correct)) return true;
  // Unfinished equation ending at "=".
  if (/=\s*$/u.test(String(submitted ?? "").trim()) && !/=\s*$/u.test(String(correct ?? "").trim())) return true;
  // "Write down A PAIR of numbers" answered with three ("12,4,8") — every prose/AI tier finds the required
  // pair among the values and reads straight past the extra one.
  if (submittedMissesValueTupleChoice(submitted, correct)) return true;
  // Single-letter answers ("G") — "gm" contains the letter but is not the answer.
  if (submittedMissesSingleLetterAnswer(submitted, correct)) return true;
  // Prose mark scheme built around a number ("…must be a power of 4") answered with a different,
  // non-overlapping number ("power of 5") — every keyword-overlap / short-label tier shares "power".
  if (narrativeNumericContradiction(submitted, correct)) return true;
  // A tally/bracket code ("I I(I) I(I)") — the bracket structure is the answer, so a code with one
  // dropped ("||||(|)") is wrong however many marks it shares.
  if (correctIsBracketCode(correct) && !bracketCodeMatches(submitted, correct)) return true;
  // "15/2" for "7 1/2" and "November 1" for "November" — the right value / the right month written in
  // a form the mark scheme rules out. Every value-equality tier above (and AI Vision) accepts both.
  if (submittedIsImproperFractionForMixedScheme(submitted, correct, questionText)) return true;
  if (submittedAddsDayNumberToMonthScheme(submitted, correct)) return true;
  if (correctAnswerIsDate(correct) && !dateAnswersEquivalent(submitted, correct)) return true;
  // Either half of a "yes. DEAL 3" answer alone. Not "wrong" — incomplete — but it must never be
  // upgraded to Correct by the prose-containment tier, which finds the bare "3" inside the scheme.
  if (verdictSchemeHalfAnswered(submitted, correct, questionText)) return true;
  // A bare measurement ("169 cm²") answered with an equation that contains that number ("C=169").
  if (answerIsBareNumericOrMeasurement(correct) && submittedLooksLikeEquation(submitted)) return true;
  // "1 pig" answered "1" — the trailing count noun is part of the answer.
  if (correctRequiresTrailingCountNoun(correct)) {
    if (submittedIsPlainNumber(submitted)) {
      if (!stemCountsSchemeNoun(correct, questionText)) return true;
    } else if (!submittedCarriesSchemeCountNoun(submitted, correct)) return true;
  }
  // "800" off a timetable printing "0800" — the four digits are how the answer is written, but the AI
  // graders read the value ("800 is equivalent to 0800") and mark it correct.
  if (timetableDigitsMissing(submitted, correct, questionAsksForClockTime(questionText))) return true;
  if (yesNoVerdictConflict(submitted, correct)) return true;
  // "Tosco 540 kg, Sunbury 300 kg" for "300 kg for Tosco vs 540 kg for Sunbury" — the same names and the
  // same digits attached the other way round, which every prose tier reads as agreement.
  if (namedEntityNumberAssociationConflict(submitted, correct)) return true;
  if (percentageGroupClaimConflict(submitted, correct)) return true;
  // "Area of A + Area of C = Area of B" vs "A and B together equal C"
  if (labelledSumEquationConflict(submitted, correct)) return true;
  // "must be a power of 2" for a scheme that ALSO requires divisibility by 3, and the scheme's own
  // sentence handed back with different figures — both share every keyword with the mark scheme.
  if (numericRelationConflict(submitted, correct)) return true;
  if (numberedClaimSkeletonConflict(submitted, correct)) return true;
  // "3 hours 190 minutes" / "2 hours 70 minutes" — a composite duration whose smaller unit spills
  // past its base is not a valid time whatever it totals, and the AI grader marks it correct.
  if (submittedOverflowsCompositeTimeUnits(submitted)) return true;
  if (submittedDropsCompositeDurationUnit(submitted, correct)) return true;
  return hasStrayBackslash(submitted)
    // "5 04" for "£5.04" — a space typed where the decimal point belongs.
    || submittedSpacesDecimalPoint(submitted, correct)
    // "0,918" for 0.918 — nor is the comma. Also "10,274" for "10.274".
    || submittedCommaDecimalsNumber(submitted, correct)
    // A discrete value set ("1, 2, 7, 14", "the primes to 20") answered with a missing value is a
    // different, wrong claim — the prose/AI tiers read the present values and agree. All required
    // values plus an extra/duplicate ("…,19,2") is partial credit, not flat Incorrect: leave that to
    // numericListPartialCredit so Review/Result can show Partial instead of Incorrect.
    || (correctIsShortValueSet(correct) && !correctIsNamedPair(correct)
      && !answerSetMatchesExactly(submitted, correct)
      && !(numericListPartialCredit(submitted, correct)?.matched
        === splitMultiValueAnswer(String(correct)).length)
      && !(multiPartListPartialCredit(submitted, correct)?.matched > 0))
    || rotationDirectionConflict(submitted, correct)
    || likelihoodLabelConflict(submitted, correct)
    || submittedMissesRangeEndpoints(submitted, correct)
    // "8 and 4" for "between 4 and 8" — both endpoints, no relation between them.
    || submittedMissesRangeRelation(submitted, correct)
    || submittedIsDanglingFragment(submitted, correct)
    // "Ninety-two thousand" for "ninety-two thousand five hundred", "Dunton" for "Dunton Green" — the
    // right answer with a piece missing, which every containment tier (and the AI) reads as agreeing.
    || submittedMissesNumberWordsScheme(submitted, correct)
    || submittedDropsProperNounWord(submitted, correct)
    // An incomplete "list all the ways" answer is a run of lines lifted verbatim from the mark scheme,
    // so isShortValueInProseAnswer finds every one of them and upgrades the backend's correct "partial".
    || (correctIsSumEnumeration(correct) && !sumEnumerationMatches(submitted, correct))
    || submittedIsUnevaluatedExpression(submitted, correct, questionText)
    || submittedUnsimplifiedIntegerFraction(submitted, correct, questionText)
    // "angle" for "Obtuse angle" / "numbers" for "square numbers" — head noun without the qualifier.
    || submissionDropsPhraseQualifier(submitted, correct)
    || isGeneralShapeForSpecializedScheme(submitted, correct)
    || submissionAddsWrongShapeHead(submitted, correct);
};

const gcdOf = (a, b) => (b === 0 ? a : gcdOf(b, a % b));

// A fraction (or the fractional part of a mixed number) already in lowest terms. A value that isn't a
// fraction at all (whole number, decimal, prose) isn't "unsimplified" — returns true so other tiers own
// it. Mirrors FuzzyAnswerMatching::fractionIsInSimplestForm on the backend.
export const fractionIsInSimplestForm = (value) => {
  const v = normalizeLatexFraction(decodeHtmlSpaces(String(value ?? "")).replace(/<[^>]*>/g, ""))
    .trim()
    .toLowerCase();
  let n;
  let d;
  let m = v.match(/^-?\d+\s+(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    n = parseInt(m[1], 10);
    d = parseInt(m[2], 10);
  } else if ((m = v.match(/^(-?\d+)\s*\/\s*(\d+)$/))) {
    n = Math.abs(parseInt(m[1], 10));
    d = parseInt(m[2], 10);
  } else {
    return true;
  }
  if (!d) return true;
  // 0/4 is not simplest (write 0). 0/1 is already the integer form of zero.
  if (n === 0) return d === 1;
  // 1/1, 2/1, 4/2 — a fraction that is a whole number is not in simplest written form.
  if (d === 1 || n % d === 0) return false;
  return gcdOf(n, d) === 1;
};

// The mark scheme is a whole number ("1", "24") and the student wrote an equal-valued fraction
// ("1/1", "24/1", "48/2"). When the stem asks for simplest / simplified form, that fraction is
// working, not the answer. Mirrors FuzzyAnswerMatching::submittedUnsimplifiedIntegerFraction.
export const submittedUnsimplifiedIntegerFraction = (submitted, correct, questionText = "") => {
  if (!questionAsksForSimplestForm({ question_text: questionText })) return false;
  const c = plainAnswerText(correct).replace(/<[^>]*>/g, "").trim();
  if (!/^-?\d+$/.test(c)) return false;
  const target = parseInt(c, 10);
  const s = normalizeLatexFraction(plainAnswerText(submitted)).trim();
  const m = s.match(/^(-?\d+)\s*\/\s*(\d+)$/);
  if (!m) return false;
  const n = parseInt(m[1], 10);
  const d = parseInt(m[2], 10);
  if (!d) return false;
  return n / d === target;
};

// "Option B is cheaper by £72" answered with working that ends "Option B is cheaper from £72".
// The conclusion is the same claim — only the preposition (from/by) and the working above it differ.
// Mirrors FuzzyAnswerMatching::proseComparisonConclusionMatches.
export const proseComparisonConclusionMatches = (submitted, correct) => {
  const plain = (v) => decodeHtmlEntities(decodeHtmlSpaces(String(v ?? "").replace(/<[^>]*>/g, " ")))
    .replace(/\s+/g, " ").trim().toLowerCase();
  const s = plain(submitted);
  const c = plain(correct);
  if (!s || !c || c.length < 8) return false;
  const normalizePrep = (t) => t
    .replace(/\bcheaper\s+from\b/g, "cheaper by")
    .replace(/\bmore\s+expensive\s+from\b/g, "more expensive by")
    .replace(/\bsaved\s+from\b/g, "saved by")
    .replace(/[£$€]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  const ns = normalizePrep(s);
  const nc = normalizePrep(c);
  if (!nc) return false;
  // The SCHEME has to be a comparison conclusion — "Option B is cheaper by £72" — before any of this
  // applies. Ungated, the containment test below awarded full marks to every answer that merely
  // quoted the mark scheme somewhere inside it: a value list with an extra value ("2, 3, 5 … 19, 2"
  // against "2, 3, 5 … 19") and a label sequence with a printed prefix ("A,B,D, E, B …" against
  // "D, E, B …") both contain their scheme verbatim. Mirrors the backend gate.
  const option = nc.match(/\boption\s+([a-d])\b/i);
  const amount = nc.match(/\b(\d+(?:\.\d+)?)\b/);
  const cmp = nc.match(new RegExp(`\\b(${COMPARISON_WORDS})\\b`, "i"));
  if (!option || !amount || !cmp) return false;
  if (ns.includes(nc)) return true;
  const opt = option[1].toLowerCase();
  const amt = amount[1];
  const word = cmp[1].toLowerCase();
  return new RegExp(`\\boption\\s+${opt}\\b`, "i").test(ns)
    && new RegExp(`\\b${amt}\\b`).test(ns)
    && new RegExp(`\\b${word}\\b`, "i").test(ns);
};

// A question whose answer is an ordered sequence — order matters, so a correct set of values in
// the wrong order must not be accepted (e.g. "list these words in order, starting with the lowest").
export const requiresOrderedAnswer = (question) => {
  if (!question) return false;
  const type = String(question.type || question.question_type || "").toLowerCase();
  if (type === "ordering") return true;
  // The author's explicit answer order (question_bank_answers.order_mode) beats the wording guess —
  // mirrors FuzzyAnswerMatching::questionRequiresOrderedAnswer.
  const authoredOrder = question.answer_data?.order_mode ?? question.order_mode;
  if (authoredOrder === "ordered") return true;
  if (authoredOrder === "any") return false;
  const text = decodeHtmlSpaces(String(question.question_text || question.text || question.question || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (!text) return false;
  // Continuing a sequence is positional: the boxes are the (n+1)th and (n+2)th terms, so the same
  // two numbers swapped are a different answer. Mirrors FuzzyAnswerMatching::questionRequiresOrderedAnswer.
  if (/\bnext\s+(?:\w+\s+){0,2}(?:numbers?|terms?|values?|digits?|letters?|shapes?)\b/.test(text)
    || /\b(?:sequence|pattern|series)\b[^.]{0,60}\bnext\b/.test(text)
    || /\bnext\b[^.]{0,60}\b(?:sequence|pattern|series)\b/.test(text)
    || /\bcontinue\s+(?:the\s+)?(?:sequence|pattern|series)\b/.test(text)
    || looksLikeSequenceContinuationStem(text)) return true;
  return /\bin\s+(?:ascending|descending|increasing|decreasing|numerical|alphabetical|reverse)?\s*order\b/.test(text)
    || /\border\s+of\b/.test(text)
    || /\b(?:arrange|rearrange|list|order|put|rank|sort|reorder)\b[^.]*\border\b/.test(text)
    || /\bstarting\s+with\s+the\s+(?:lowest|highest|smallest|largest|biggest|first)\b/.test(text)
    || /\bfrom\s+(?:lowest|highest|smallest|largest|biggest)\s+to\b/.test(text)
    || /\badult\b.{0,60}\bchild(?:ren)?\b/.test(text)
    || /\bchild(?:ren)?\b.{0,60}\badult\b/.test(text)
    || /\bnumber\s+in\b.{0,40}\bnumber\s+out\b/.test(text)
    || /\bnumber\s+out\b.{0,40}\bnumber\s+in\b/.test(text)
    || /\blargest\b.{0,40}\bsmallest\b/.test(text)
    || /\bsmallest\b.{0,40}\blargest\b/.test(text)
    || /\bfill\s+in\s+the\s+table\b/.test(text)
    || /\bcomplete\s+(?:the\s+)?(?:table|grid)\b/.test(text);
};

// A mark scheme that is a SEQUENCE rather than an unordered set: every part is numeric and some value
// repeats (e.g. the digital-root pattern "1, 4, 9, 7, 7, 9, 4, 1"). Order carries meaning, so the same
// multiset in a different order is a different answer. A set of DISTINCT values (factors "1, 2, 7, 14")
// stays order-insensitive. Mirrors FuzzyAnswerMatching::correctIsNumericSequence on the backend.
export const correctIsNumericSequence = (correct) => {
  const parts = splitMultiValueAnswer(String(correct ?? ""));
  if (parts.length < 3) return false;
  if (!parts.every((p) => /^-?\d+(?:\.\d+)?$/.test(String(p).trim()))) return false;
  const normalized = parts.map((p) => String(parseFloat(String(p).trim())));
  return new Set(normalized).size < normalized.length;
};

// True when the submitted values ARE the expected values, just sequenced differently — the only case
// that may be reported as "wrong order". A list with a wrong/missing value is plainly incorrect, and
// saying "wrong order" about it (as the greedy in-order scan used to) misleads the student.
export const isSameValueMultiset = (submittedParts, correctParts, questionText = "") => {
  if (submittedParts.length !== correctParts.length) return false;
  const used = new Set();
  for (const cp of correctParts) {
    let hit = false;
    for (let si = 0; si < submittedParts.length; si++) {
      if (used.has(si)) continue;
      if (singleValueMatches(submittedParts[si], cp, questionText)) {
        used.add(si);
        hit = true;
        break;
      }
    }
    if (!hit) return false;
  }
  return true;
};

// A discrete value set — "1, 2, 7, 14" (all the factors), "2, 3, 5, 7, 11, 13, 17, 19" (the primes to
// 20). Every value is required and each is required ONCE, so the answer is decided by exact set
// equality. A repeating numeric sequence ("1, 4, 9, 7, 7, 9, 4, 1") is ordered data, not a set, and a
// scheme whose parts are phrases is prose. Mirrors FuzzyAnswerMatching::blankAcceptIsValueSet.
export const correctIsShortValueSet = (correct) => {
  const raw = String(correct ?? "");
  if (/<li\b/i.test(raw)) return false;
  const parts = splitMultiValueAnswer(raw);
  if (parts.length < 2) return false;
  if (correctIsNumericSequence(correct)) return false;
  // Letter-route sequences are ordered answers, not unordered sets.
  if (correctIsLabelSequence(correct)) return false;
  // "Yes, 0.5 and 0.125" is a decision + values, not a bare value set.
  if (parts.some((p) => /^(?:yes|no)$/i.test(String(p).trim()))) return false;
  if (correctIsCoordinatePair(correct)) return false;
  // "between 4 and 8" is a range STATEMENT, not the set {4, 8} — read as a set, every correct way of
  // writing the range failed set equality and came out deterministically wrong.
  if (/\b(?:between|from)\s+-?\d+(?:\.\d+)?\s*(?:and|to|–|-)\s*-?\d/.test(plainAnswerText(correct).toLowerCase())) return false;
  return parts.every((p) => String(p).trim().split(/\s+/).filter(Boolean).length <= 2);
};

// Set equality for the above: the student must give as many values as the scheme lists, each matching a
// distinct one. Surplus values — a repeat ("1,2,4,7,28,1,2,14") or a wrong extra ("…,14,28,3") — are
// what the old "every expected value was found somewhere" fallback let through as Correct while the
// backend scored 0. Mirrors FuzzyAnswerMatching::answerSetMatches, including its two readings of the
// submission (whitespace+comma, then comma only, so "66 °, 66 °" keeps its unit chips).
export const answerSetMatchesExactly = (submitted, correct) => {
  const tokenize = (v, strict) => {
    let s = expandMultiValueAndJoiners(decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, " "));
    // "or"/"and" are separators on the submitted side: a two-value set ("Birmingham, London") is
    // naturally answered "Birmingham and London", and the whitespace tokenizer would otherwise read the
    // joiner as a third value and fail the right answer.
    if (strict) s = s.replace(/\s+(?:or|and)\s+/gi, ", ");
    return s.trim().split(strict ? /[\s,]+/ : /[\s,;|]+/).map((p) => p.trim()).filter(Boolean);
  };
  const expectedParts = tokenize(correct, false);
  if (expectedParts.length < 2) return false;

  const readings = [tokenize(submitted, true)];
  if (String(submitted ?? "").includes(",")) {
    readings.push(String(submitted).split(",").map((p) => p.trim()).filter(Boolean));
  }

  return readings.some((submittedParts) => {
    if (submittedParts.length !== expectedParts.length) return false;
    const used = new Set();
    return expectedParts.every((exp) => {
      for (let i = 0; i < submittedParts.length; i++) {
        if (used.has(i)) continue;
        if (singleValueMatches(submittedParts[i], exp)) {
          used.add(i);
          return true;
        }
      }
      return false;
    });
  });
};

// "List the prime numbers from 0 to 20" is all-or-nothing on the server: one duplicate or one missing
// value hides the seven the student got right. Returns { matched, total, extra, ratio } when the answer
// earns partial credit, otherwise null — never a verdict on its own, callers only soften Incorrect →
// Partial with it. Only bare-number schemes qualify; prose ("3, because it is odd") splits on commas
// too and scoring that by value overlap is meaningless. Both readings of the submission are tried so a
// dropped separator ("17 19" for "17, 19") is still read as two values.
export const numericListPartialCredit = (submitted, correct) => {
  const text = decodeHtmlEntities(String(submitted ?? "").replace(/&nbsp;/gi, " ").replace(/<[^>]*>/g, " "))
    .replace(/÷/g, "/").replace(/×/g, "*");
  if (!text.trim() || !String(correct ?? "").trim()) return null;
  const isNumericToken = (p) => {
    const t = String(p ?? "").trim();
    if (!t || t.split(/\s+/).filter(Boolean).length > 3) return false;
    return parseSimpleNumericValue(t) !== null;
  };
  const correctParts = splitMultiValueAnswer(String(correct));
  if (correctParts.length < 2 || !correctParts.every(isNumericToken)) return null;

  const readings = [
    splitMultiValueAnswer(text),
    text.trim().split(/[\s,]+/).map((p) => p.trim()).filter(Boolean),
  ].filter((r) => r.length > 0 && r.every(isNumericToken));
  if (!readings.length) return null;

  let best = null;
  readings.forEach((subParts) => {
    const remaining = [...subParts];
    let matched = 0;
    correctParts.forEach((cp) => {
      const i = remaining.findIndex((sp) => singleValueMatches(sp, cp));
      if (i >= 0) {
        remaining.splice(i, 1);
        matched++;
      }
    });
    const score = matched - remaining.length;
    if (!best || score > best.score) best = { matched, extra: remaining.length, score };
  });

  if (!best || best.matched === 0) return null;
  if (best.matched === correctParts.length && best.extra === 0) return null;
  return {
    matched: best.matched,
    total: correctParts.length,
    extra: best.extra,
    ratio: Math.max(0, best.score) / correctParts.length,
  };
};

// An arrow route ("200m -> 100m -> 430m -> 60m -> 25m") is ordered. The same stops
// with several already in the right place are partly correct. One stop that happens
// to land in the right slot is still wrong. Null when this is not that kind of scheme,
// or when the route is complete (callers treat a full match as correct, not partial).
export const arrowSequencePositionalCredit = (submitted, correct) => {
  const plain = (value) => decodeHtmlEntities(String(value ?? "").replace(/<[^>]*>/g, " "))
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const arrow = /(?:-+>|→|⟶|=>|⇒|➝|➞)/;
  const schemeText = plain(correct);
  if ((schemeText.match(new RegExp(arrow.source, "g")) || []).length < 2) return null;
  const stopToken = (raw) => {
    let token = String(raw ?? "").trim().toLowerCase().replace(/\s+/g, "");
    token = token.replace(/(\d)(?:metres?|meters?|miles?|km|cm|mm|m)\.?$/i, "$1");
    return token;
  };
  const scheme = schemeText.split(arrow).map(stopToken).filter(Boolean);
  if (scheme.length < 3 || scheme.some((token) => token.length > 12 || !/^\d+(?:\.\d+)?$/.test(token))) {
    return null;
  }
  let parts = plain(submitted).split(new RegExp(`\\s*(?:${arrow.source}|,|;|\\bthen\\b)\\s*`, "i"));
  if (parts.length < 2) parts = plain(submitted).split(/\s+/);
  const got = parts.map(stopToken).filter((token) => token && !/^(?:m|km|cm|mm|metres?|meters?|miles?)$/i.test(token));
  if (got.length !== scheme.length) return null;
  let matched = 0;
  got.forEach((token, index) => {
    if (token === scheme[index]) matched += 1;
  });
  if (matched < 2 || matched === scheme.length) return null;
  return {
    matched,
    total: scheme.length,
    extra: 0,
    ratio: matched / scheme.length,
  };
};

// Multi-line list mark schemes ("Red, green" / "Red, Orange" …) where each row is one answer part.
// Mirrors FuzzyAnswerMatching::multiValueSetMatchRatio + matchedCorrectPartsFromSubmission on the backend.
export const multiPartListPartialCredit = (submitted, correct) => {
  const correctParts = splitMultiValueAnswer(String(correct ?? ""));
  if (correctParts.length < 2) return null;
  const isNumericToken = (p) => {
    const t = String(p ?? "").trim();
    if (!t || t.split(/\s+/).filter(Boolean).length > 3) return false;
    return parseSimpleNumericValue(t) !== null;
  };
  if (correctParts.every(isNumericToken)) return null;

  // A written explanation is not a list of parts. splitMultiValueAnswer happily cuts a sentence at
  // its commas, so two near-identical explanations scored "1 of 2 parts" and a fully correct essay
  // came back Partially correct — 0.5. A list item is short; a clause is not.
  const wordCount = (p) => String(p ?? "").trim().split(/\s+/).filter(Boolean).length;
  if (correctParts.some((p) => wordCount(p) > 6)) return null;

  const submittedParts = splitSubmittedListLines(submitted);
  if (!submittedParts.length) return null;

  const used = new Set();
  let matched = 0;
  correctParts.forEach((cp) => {
    for (let i = 0; i < submittedParts.length; i++) {
      if (used.has(i)) continue;
      if (singleValueMatches(submittedParts[i], cp)) {
        used.add(i);
        matched++;
        break;
      }
    }
  });
  const incorrectlySubmitted = submittedParts.length - matched;
  if (matched === 0) return null;
  if (matched === correctParts.length && incorrectlySubmitted === 0) return null;
  if (incorrectlySubmitted > 0 && submittedParts.length !== correctParts.length) return null;
  return {
    matched,
    total: correctParts.length,
    extra: incorrectlySubmitted,
    ratio: matched / correctParts.length,
  };
};

export const fillBlanksAreInterchangeable = (question) => {
  if (!question) return false;
  const overlay = question.answer_data?.overlay_layout || question.overlay_layout || {};
  if (overlay.blanks_interchangeable || overlay.rows_interchangeable) return true;
  const text = decodeHtmlSpaces(String(question.question_text || question.text || question.question || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (/\bwhat\s+are\s+(?:its|the)\s+dimensions?\b/.test(text)) return true;
  if (/\bwhat\s+are\s+they\b|\bwhich\s+are\s+they\b/.test(text)) return true;
  const labels = question.answer_data?.blank_labels || question.blank_labels || [];
  if (!Array.isArray(labels) || labels.length < 2) return false;
  const normalized = labels
    .map((l) => String(l || "").toLowerCase().replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ")
      .replace(/\s*\d+\s*$/, "").replace(/[^a-z]+/g, " ").trim())
    .filter(Boolean);
  return normalized.length >= 2 && new Set(normalized).size === 1;
};

export const multiFillSetCredit = (submittedArr, correctArr) => {
  const sub = (Array.isArray(submittedArr) ? submittedArr : [submittedArr]).map((v) => String(v ?? "").trim());
  const cor = (Array.isArray(correctArr) ? correctArr : [correctArr]).map((v) => String(v ?? "").trim()).filter((v) => v !== "");
  if (cor.length < 2) return null;
  const used = new Set();
  let matched = 0;
  cor.forEach((c) => {
    for (let i = 0; i < sub.length; i++) {
      if (used.has(i) || sub[i] === "") continue;
      if (singleValueMatches(sub[i], c)) {
        used.add(i);
        matched++;
        break;
      }
    }
  });
  return {
    matched,
    total: cor.length,
    extra: sub.filter((v, i) => v !== "" && !used.has(i)).length,
  };
};

const yesDecisionWithEquivalentValues = (submitted, correct) => {
  const extractDecision = (t) => {
    const s = String(plainAnswerText(t) || "").trim().toLowerCase();
    if (/^yes\b/.test(s)) return "yes";
    if (/^no\b/.test(s)) return "no";
    // The one-letter abbreviations, but never an algebra variable: "n(n-1)" opens with the variable n,
    // not with the word "no". Read as a decision, every expression in n "agreed" with every other and
    // "n×(n+1)" was accepted for a mark scheme of "n(n-1)". The following word must be two letters or
    // more so the "x" of "n x (n-1)" stays a times sign.
    // Mirrors ExplanatoryAnswerMatcher::extractYesNoDecision.
    if (/^y(?:\s*[,.:;!]|\s+(?=[a-z]{2})|\s*$)/.test(s)) return "yes";
    if (/^n(?:\s*[,.:;!]|\s+(?=[a-z]{2})|\s*$)/.test(s)) return "no";
    return null;
  };
  const sDec = extractDecision(submitted);
  const cDec = extractDecision(correct);
  if (!sDec || !cDec || sDec !== cDec) return false;
  const nums = (t) => {
    const raw = String(plainAnswerText(t) || "");
    const vals = [];
    const fracRe = /(?<![A-Za-z])(-?\d+)\s*\/\s*(\d+)(?!\d)/g;
    let m;
    while ((m = fracRe.exec(raw)) !== null) {
      const d = Number(m[2]);
      if (d) vals.push(Number(m[1]) / d);
    }
    const decRe = /(?<![A-Za-z0-9./])(-?\d+(?:\.\d+)?)(?![A-Za-z0-9./])/g;
    while ((m = decRe.exec(raw)) !== null) vals.push(Number(m[1]));
    return vals;
  };
  const sVals = nums(submitted);
  const cVals = nums(correct);
  if (!sVals.length || !cVals.length) return false;
  const used = new Set();
  return cVals.every((cv) => {
    const i = sVals.findIndex((sv, si) => !used.has(si) && Math.abs(sv - cv) < 1e-6 * Math.max(1, Math.abs(cv)));
    if (i < 0) return false;
    used.add(i);
    return true;
  });
};

const mistakeExplanationAgrees = (submitted, correct) => {
  const norm = (t) => String(plainAnswerText(t) || "")
    .replace(/\\times|\\cdot/g, "×")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .trim();
  const s = norm(submitted);
  const c = norm(correct);
  if (s.length < 12 || c.length < 12) return false;
  const claimRe = /\d+(?:\s*[×x*]\s*\d+){1,}|\d+\s*(?:\^|²|³)\s*\d*/g;
  const compact = (claim) => String(claim).replace(/\s+/g, "").replace(/[×*·]/g, "x").replace(/²|\^2/g, "^2").replace(/³|\^3/g, "^3");
  const cClaims = [...new Set((c.match(claimRe) || []).map(compact))];
  const sClaims = [...new Set((s.match(claimRe) || []).map(compact))];
  if (cClaims.length < 2 || sClaims.length < 2) return false;
  const hit = cClaims.filter((claim) => sClaims.includes(claim)).length;
  return hit >= Math.max(2, Math.ceil(cClaims.length * 0.6));
};

/** The asked quantity is the area. A width question that only mentions a given area does not match. */
export const questionAsksForArea = (questionText) => {
  const q = plainAnswerText(questionText).replace(/\s+/g, " ").trim();
  if (!q) return false;
  return /\b(?:what\s+is|find|calculate|work\s+out|state|give)\s+(?:the\s+|its\s+)?areas?\b/i.test(q);
};

/** "the area of the rectangle is 98 cm²" / "Area = 14 × 7 = 98". Last area sentence wins. */
export const extractAreaConclusion = (text) => {
  const plain = plainAnswerText(text).replace(/\s+/g, " ").trim();
  if (!plain) return null;
  const unit = "(?:mm|cm|km|m)(?:²|2)?";
  let lastNum = null;
  let lastUnit = null;
  for (const sentence of plain.split(/(?<=[.!?])\s+/)) {
    if (!/\bareas?\b/i.test(sentence)) continue;
    const eqs = [...sentence.matchAll(new RegExp(`=\\s*(\\d+(?:\\.\\d+)?)(?:\\s*(${unit}))?`, "gi"))];
    if (eqs.length > 0) {
      const hit = eqs[eqs.length - 1];
      lastNum = hit[1];
      lastUnit = hit[2] || null;
      continue;
    }
    const isN = sentence.match(new RegExp(`\\bareas?\\b[^.!?]{0,60}?\\bis\\s+(\\d+(?:\\.\\d+)?)(?:\\s*(${unit}))?`, "i"));
    if (isN?.[1]) {
      lastNum = isN[1];
      lastUnit = isN[2] || null;
    }
  }
  if (!lastNum) return null;
  const unitText = lastUnit ? String(lastUnit).trim() : "";
  return unitText ? `${lastNum} ${unitText}` : lastNum;
};

/**
 * Stored width ("7 cm²") when the question asks for the area and the explanation states
 * a different area. Null when the stored value already is that area.
 */
export const statedAreaWhenQuestionAsksForArea = (questionText, explanation, stored) => {
  if (!questionAsksForArea(questionText)) return null;
  const area = extractAreaConclusion(explanation);
  if (!area) return null;
  const areaNum = parseSimpleNumericValue(area);
  const storedNum = parseSimpleNumericValue(stored);
  if (areaNum == null) return null;
  if (storedNum != null && Math.abs(storedNum - areaNum) < 0.0001) return null;
  // The explanation states the stored number itself, so the area it found is a step on the way (the
  // lawn inside the path, a part of the H shape) — the key stays. Mirrors the server's
  // explanationOverrideContradictsNumericKey: review showed Whitgift Practice Maths Q27b's path area
  // as 216 (the lawn) and St Albans 2025 Q18a's 13 cm² as "1 cm" (its middle bar).
  if (storedNum != null && explanationStatesNumber(explanation, stored)) return null;
  return area;
};

const explanationStatesNumber = (explanation, stored) => {
  const key = String(stored ?? "").replace(/<[^>]*>/g, " ").replace(/&nbsp;| /gi, " ");
  const num = key.match(/-?\d[\d,]*(?:\.\d+)?/);
  if (!num || key.includes("/")) return false;
  const plain = ` ${String(explanation ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;| /gi, " ")
    .replace(/\d+\s*\/\s*\d+|[¼-¾⅐-⅞]/g, " ")} `;
  const escaped = num[0].replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(?<![\\d.,])${escaped}(?![\\d]|[.,]\\d)`).test(plain);
};

/**
 * A stored whole number ("1") that is only the whole part of the mixed number in the
 * working ("1 13/30"). Returns that mixed number, otherwise null.
 * "The correct answer is 18" stays 18 even when the working mentions another mixed step.
 */
export const completeTruncatedMixedCorrectAnswer = (stored, explanation) => {
  const authored = String(stored ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!/^-?\d+$/.test(authored)) return null;
  const plain = String(explanation ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/(\d+)\s*\\frac\{(\d+)\}\{(\d+)\}/g, "$1 $2/$3")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return null;
  if (/\b(?:correct\s+)?answer\s+is\s+-?\d+(?:\.\d+)?\b(?!\s*\/)(?!\s+\d+\s*\/)/i.test(plain)) {
    return null;
  }
  const matches = [...plain.matchAll(/\b(\d+)\s+(\d+)\s*\/\s*(\d+)\b/g)].filter((m) => {
    const num = Number.parseInt(m[2], 10);
    const den = Number.parseInt(m[3], 10);
    return num > 0 && num < den;
  });
  if (matches.length === 0) return null;
  const last = matches[matches.length - 1];
  if (last[1] !== authored.replace(/^-/, "")) return null;
  return `${authored} ${last[2]}/${last[3]}`;
};

const TIMES_SYMBOL_RE = /^(?:×|✕|⨯|⋅|·|\\times|&times;|&#215;|&#x0*d7;)$/i;

const plainTimesCompare = (value) =>
  String(value ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&times;|&#215;|&#x0*d7;|\\times/gi, "×")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * The answer is the multiplication symbol, and the student typed the letter x.
 * Numeric products ("20 x 3", "2x2x3x3") and implicit products ("n x (n-1)") are not this.
 */
export const letterXSubmittedForTimesSymbol = (submitted, correct, correctBlanks) => {
  const pairs = [];
  const addPair = (student, scheme) => {
    const s = plainTimesCompare(student);
    const c = plainTimesCompare(scheme);
    if (s && c) pairs.push([s, c]);
  };
  addPair(submitted, correct);
  const subParts = plainTimesCompare(submitted).split(/\s*\|\s*/);
  const corParts = plainTimesCompare(correct).split(/\s*\|\s*/);
  if (subParts.length > 1 && subParts.length === corParts.length) {
    subParts.forEach((part, index) => addPair(part, corParts[index]));
  }
  if (Array.isArray(correctBlanks)) {
    const studentParts = subParts.length === correctBlanks.length
      ? subParts
      : (correctBlanks.length === 1 ? [plainTimesCompare(submitted)] : []);
    correctBlanks.forEach((slot, index) => {
      const student = studentParts[index] ?? "";
      const alts = Array.isArray(slot) ? slot : [slot];
      alts.forEach((alt) => addPair(student, alt));
    });
  }
  const collapsed = (value) => plainTimesCompare(value).replace(/\s+/g, "");
  if (pairs.some(([student, scheme]) =>
    /^x$/i.test(collapsed(student)) && TIMES_SYMBOL_RE.test(collapsed(scheme)))) {
    return true;
  }
  const betweenFactors = (student, scheme) => {
    if (!/[a-z]\s*[×✕⨯⋅·]\s*[a-z]/i.test(scheme)) return false;
    if (!/[a-z]\s*x\s*[a-z]/i.test(student)) return false;
    const fold = (value) => value.toLowerCase()
      .replace(/[×✕⨯⋅·]/g, "*")
      .replace(/([a-z])\s*x\s*(?=[a-z])/gi, "$1*")
      .replace(/\s+/g, "");
    return fold(student) === fold(scheme);
  };
  return pairs.some(([student, scheme]) => betweenFactors(student, scheme));
};

export const isAnswerCorrect = (submitted, correct, options = {}) => {
  if (submitted == null || submitted === "" || submitted === "—") return false;
  if (correct == null || correct === "" || correct === "—") return false;
  const statedArea = statedAreaWhenQuestionAsksForArea(
    options.questionText,
    options.explanation,
    correct
  );
  if (statedArea) {
    const areaNum = parseSimpleNumericValue(statedArea);
    const submittedNum = parseSimpleNumericValue(submitted);
    if (areaNum != null && submittedNum != null) {
      return Math.abs(areaNum - submittedNum) < 0.0001;
    }
  }
  if (equationLineIsTruncated(submitted, correct)) return false;
  submitted = canonicalizePiToken(canonicalizeDegreeGlyphs(submitted));
  correct = canonicalizePiToken(canonicalizeDegreeGlyphs(correct));
  if (inverseComparativeEquivalent(submitted, correct)) return true;
  if (submissionFlipsColourWord(submitted, correct) || submissionFlipsNumberWord(submitted, correct)) return false;
  if (submittedDropsCompositeDurationUnit(submitted, correct) || submittedOverflowsCompositeTimeUnits(submitted)) return false;
  // Number-in-words answers are judged by value — "eighteen" ≠ "eighty".
  if (submittedMissesNumberWordsScheme(submitted, correct)) return false;
  if (percentageGroupClaimConflict(submitted, correct)) return false;
  if (letterXSubmittedForTimesSymbol(submitted, correct)) return false;
  // "15.2-5.7" for "9.5" — restated working must not match by value (same narrow gate as backend).
  {
    const submittedPlain = plainAnswerText(submitted).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    if (answerIsBareNumericOrMeasurement(correct)
      && !submittedPlain.includes("=")
      && /^[\d\s+\-*\/×÷xX().,]+$/.test(submittedPlain)
      && submittedIsUnevaluatedExpression(submitted, correct, options.questionText)) {
      return false;
    }
  }
  if (submissionMissesSchemeComparisonChoice(submitted, correct, options.questionText)) return false;
  if (submissionMissesWorkedComparisonChoice(submitted, correct)) return false;
  if (submissionDropsPhraseQualifier(submitted, correct)) return false;
  if (isGeneralShapeForSpecializedScheme(submitted, correct)) return false;
  if (submissionAddsWrongShapeHead(submitted, correct)) return false;
  if (labelAnswerRepeatsStemNoun(submitted, correct, options.questionText)) return true;
  if (yesDecisionWithEquivalentValues(submitted, correct)) return true;
  if (verdictWithProvingCalculationAgrees(submitted, correct)) return true;
  if (workedDecisionExplanationAgrees(submitted, correct, options.questionText || "")) return true;
  if (proseComparisonConclusionMatches(submitted, correct)) return true;
  if (mistakeExplanationAgrees(submitted, correct)) return true;
  if (timesGlyphCalculationMatches(submitted, correct)) return true;
  if (submittedMatchesWorkingEquationResult(submitted, correct)) return true;
  if (operationFillMatchesFilledEquation(submitted, correct)
    || operationFillMatchesFilledEquation(correct, submitted)) return true;
  if (operationInstructionEquivalent(submitted, correct)) return true;
  if (operationInstructionMismatch(submitted, correct)) return false;
  if (submittedMissesPiFactor(submitted, correct, options.questionText)) return false;
  if (submittedMissesRatioSeparator(submitted, correct, options.questionText)) return false;
  if (questionAsksForFractionForm(options.questionText)
      && valueIsFraction(correct)
      && !valueIsFraction(submitted)) return false;
  if (decimalMissesRequiredFractionForm(submitted, correct, options.questionText)) return false;
  // The stem named the 24-hour clock, so "5.13 p.m" is the answer in the notation it ruled out.
  // Decided as a whole-answer guard: the clock tiers sit behind several branches below and each
  // reconciles the two forms into the same instant.
  if (questionRequires24HourAnswer(options.questionText)
      && submissionBreaks24HourForm(submitted, correct)) {
    return false;
  }
  // "2 hours 70 minutes" converts to the right 190 minutes, so isUnitEquivalent's compound tier
  // accepts it — but a composite time whose minutes don't fit in an hour is not a valid duration.
  if (submittedOverflowsCompositeTimeUnits(submitted)) return false;
  if (yesNoVerdictConflict(submitted, correct)) return false;
  // Same labels, wrong which are added vs the total ("A + C = B" for "A and B together equal C").
  if (labelledSumEquationConflict(submitted, correct)) return false;
  // Equation form of the same additive claim as the prose mark scheme.
  if (labelledSumEquationsAgree(submitted, correct)) return true;
  // A bare measurement ("169 cm²") must not accept an equation that merely contains that number.
  if (answerIsBareNumericOrMeasurement(correct) && submittedLooksLikeEquation(submitted)) return false;
  if (correctAnswerIsDate(correct)) return dateAnswersEquivalent(submitted, correct);
  // Mirrors FuzzyAnswerMatching: an area/volume mark scheme is a quantity in a fixed dimension, and a
  // "state the units of your answer" stem makes naming that unit half the task. Both are decided here
  // so Check answer can't upgrade what the server (and the Review page) call wrong.
  {
    const unitVerdict = measuredUnitVerdict(submitted, correct, options.questionText);
    if (unitVerdict === true) return true;
    if (unitVerdict === false) return false;
    if (questionAsksToStateUnits(options.questionText)) {
      const cParsed = parseValueWithUnit(plainAnswerText(correct).trim());
      const sParsed = parseValueWithUnit(plainAnswerText(submitted).trim());
      if (cParsed && cParsed[1] && (!sParsed || !sParsed[1])) return false;
    }
  }
  if (unitsConflict(submitted, correct)) return false;
  if (timesGlyphCalculationMatches(submitted, correct)) return true;
  if (submittedMatchesWorkingEquationResult(submitted, correct)) return true;
  if (operationFillMatchesFilledEquation(submitted, correct)
    || operationFillMatchesFilledEquation(correct, submitted)) return true;
  // "4 or 5" on a stem asking for ONE number: naming both options is a hedge, not a choice. Hoisted to
  // the top because the alternatives reach the tiers below in several shapes (the raw "4 or 5", the
  // display list "4, 5" compared as a value set, the per-value list tier) and each accepted it.
  if (questionAsksForOneValue(options.questionText)
      && submissionHedgesAlternatives(submitted, extractCorrectAlternatives(plainAnswerText(correct)))) {
    return false;
  }
  if (algebraicEquationMatches(submitted, correct)) return true;
  // Algebraic expressions ("(2x - 3)²") — authoritative, same as FuzzyAnswerMatching. Must run before
  // the compact-equation tier below, which used to strip letters and unicode powers so "(2x-3)³"
  // collapsed to the same "(2-3)" skeleton as the mark scheme.
  if (isAlgebraicExpression(correct)) {
    return algebraicExpressionMatches(submitted, correct);
  }
  // Compact equation quoted inside prose ("180+180=360" in "…180° + 180° = 360°").
  {
    const stripDeg = (v) => String(v ?? "").replace(/\\circ\b|°/g, "").replace(/[×⋅·]/g, "*").replace(/÷/g, "/")
      .replace(/[−–—\u2010\u2011\u2012]/g, "-").replace(/<[^>]*>/g, " ");
    // Fold powers first and keep letters so algebraic answers aren't collapsed to a digit skeleton.
    const compact = (v) => normalizeNumericPowerExpr(stripDeg(v)).replace(/[^0-9a-z+\-*/^=()]/gi, "");
    const s = compact(submitted);
    const c = compact(correct);
    // The quoted sum has to be the whole run. A neighbouring operator, including "=", means the
    // student stopped early: "1^2+2^2+3^2+4^2" is the opening of "…=30=1/6*4*5*9".
    const occurrenceIsWholeRun = (hay, needle) => {
      if (needle.length < 5) return false;
      let offset = 0;
      while (offset <= hay.length) {
        const at = hay.indexOf(needle, offset);
        if (at < 0) return false;
        const before = at > 0 ? hay[at - 1] : "";
        const after = hay[at + needle.length] || "";
        const extendsNumber = /[0-9./]/.test(before) || /[0-9./]/.test(after);
        const continues = /[+\-*/^=]/.test(before) || /[+\-*/^=]/.test(after);
        if (!extendsNumber && !continues) return true;
        offset = at + 1;
      }
      return false;
    };
    if (/[+\-*/=]/.test(s) && occurrenceIsWholeRun(c, s)) return true;
  }
  // A coordinate is an ORDERED pair: "22,21" is not "(21, 22)". Decided before every tier below —
  // the multiset fallback splits both on the comma and reads them as the same two values. Only when
  // the submission is itself readable as points; anything else falls through to the normal tiers.
  if (correctIsCoordinatePair(correct) && extractCoordinatePairs(submitted).length > 0) {
    return coordinateAnswerMatches(submitted, correct);
  }
  // "(552 + 448) × (552 - 448)" answered without its brackets is a different sum written with the same
  // symbols — decided before the tiers that compare an answer by the digits and operators it contains.
  if (bracketedCalculationMismatch(submitted, correct)) return false;
  // "List ALL the ways to make 60p" — every sum in the mark scheme is required, so decide the whole
  // list by set equality before the tiers below, which credit a subset of it as a full match.
  if (correctIsSumEnumeration(correct)) return sumEnumerationMatches(submitted, correct);
  // A route / order of labelled points ("A E D B C G") — the whole sequence is the answer.
  if (correctIsLabelSequence(correct)) return labelSequenceMatches(submitted, correct);
  if (correctIsEdgeRoute(correct)) return edgeRouteMatches(submitted, correct);
  // A sum ("1+3+5+…", "20p + 20p + 20p") — the operators are part of the answer. Ahead of the coin
  // list below, the order isFuzzyTextMatch decides them in: a sum of money terms is also a money
  // list, and the multiset compare there would accept it with the "+" signs dropped.
  if (isSumExpression(correct)) {
    return isSumExpression(submitted) && sumExpressionCanonical(submitted) === sumExpressionCanonical(correct);
  }

  // A prime factorisation is a multiset of factors: order free, but a missing factor is a different
  // number. Mirrors the backend's product tier.
  if (isProductExpression(correct)) {
    return isProductExpression(submitted) && productExpressionCanonical(submitted) === productExpressionCanonical(correct);
  }
  // A coin list ("£1, 20p, 5p, 1p") — decided here, like isFuzzyTextMatch does, because the
  // multi-value fallback below splits it on the commas and compares "1" with "£1" one token at a
  // time, where a bare number passes for a pounds amount.
  if (isMoneyDenominationList(correct)) return moneyDenominationListMatches(submitted, correct);
  // "Which TWO cities…" — both names are the answer, in either order.
  const namedPair = correctIsNamedPair(correct);
  if (namedPair) return namedPairMatches(submitted, namedPair, options.questionText);
  if (options.ordered || correctIsNumericSequence(correct)) {
    const correctOrdered = splitMultiValueAnswer(String(correct));
    if (correctOrdered.length > 1) {
      let submittedOrdered = splitMultiValueAnswer(String(submitted));
      // When the correct sequence is all numbers, students often separate them with spaces
      // ("798 879 897") or bare commas ("798,879,897"), which splitMultiValueAnswer doesn't break.
      // Re-tokenize on any whitespace/comma run so a correct space-separated list isn't failed.
      const correctAllNumeric = correctOrdered.every((p) => /^-?\d+(?:\.\d+)?$/.test(String(p).trim()));
      if (correctAllNumeric && submittedOrdered.length < correctOrdered.length) {
        const reSplit = String(submitted).trim().split(/[\s,]+/).map((p) => p.trim()).filter(Boolean);
        if (reSplit.length > submittedOrdered.length) submittedOrdered = reSplit;
      }
      if (submittedOrdered.length !== correctOrdered.length) return false;
      return correctOrdered.every((cp, i) => singleValueMatches(submittedOrdered[i], cp));
    }
  }
  if (markSchemeToleranceAccepts(submitted, correct)) return true;
  if (singleValueMatches(submitted, correct, options.questionText)) return true;
  if (estimateNumericAnswerEquivalent(submitted, correct, options.questionText)) return true;
  if (countQuestionNumericAnswerEquivalent(submitted, correct, options.questionText)) return true;
  // A discrete value set is exact: the fallback below only checks that every EXPECTED value was found
  // somewhere, so a list carrying the whole set plus a repeat ("1,2,4,7,28,1,2,14") or an extra wrong
  // value ("1,2,4,7,14,28,3") was upgraded to Correct over the backend's 0 — the client upgrade tier on
  // the take page reads this function. Decided by set equality instead, as the backend does.
  if (correctIsShortValueSet(correct)) return answerSetMatchesExactly(submitted, correct);
  const correctParts = splitMultiValueAnswer(String(correct));
  const submittedParts = splitMultiValueAnswer(String(submitted));
  if (correctParts.length > 1 && submittedParts.length > 1) {
    let matched = 0;
    const usedSubmitted = new Set();
    for (const cPart of correctParts) {
      for (let si = 0; si < submittedParts.length; si++) {
        if (usedSubmitted.has(si)) continue;
        if (singleValueMatches(submittedParts[si], cPart)) {
          matched++;
          usedSubmitted.add(si);
          break;
        }
      }
    }
    if (matched === correctParts.length) return true;
    // Submitting a subset of required values is partial, not correct — fall through.
  }
  return false;
};
